// components/BookingForm.js
import { qs, html }             from '../utils/dom.js';
import { naluTrack }            from "../utils/naluTracking";

import { DatePicker }           from './DatePicker.js';
import { PassengerPicker }      from './PassengerPicker.js';
import { initSliders }          from './Slider.js';
import { initStickySidebar }    from './StickySidebar.js';
import { initScrollAffordances } from './ScrollAffordance.js';
import { fetchCalendarRange, fetchSimpleCalendar, fetchSimpleCalendarMulti, fetchSimpleCalendarPrefetch, fetchSimpleCalendarMultiPrefetch } from '../api/calendar.js';
import { heartbeatViewer, getViewers, generateSessionId } from '../api/viewers.js';
import { postNaluAjax } from '../api/ajax.js';

import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import customParseFormat from 'dayjs/plugin/customParseFormat';

// Load + enable timezone globally
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(minMax);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

// set a single default timezone for the whole file
const HST = 'Pacific/Honolulu';

// Put near top of BookingForm.js (after dayjs.extend(...))
const HST_TZ = 'Pacific/Honolulu';

/**
 * Diagnostic-only console.log. Gated behind window.naluBookingVars.debug,
 * which mirrors PHP's WP_DEBUG (set in BookingFormShortcode.php). Use this
 * for breadcrumbs; use console.warn / console.error for real failures —
 * those should always surface to devtools.
 */
const naluDebug = (...args) => {
    if ( typeof window !== 'undefined' && window.naluBookingVars?.debug ) {
        // eslint-disable-next-line no-console
        console.log(...args);
    }
};

/**
 * Same WP_DEBUG gate as naluDebug, but emits the call stack. Used on the state
 * transitions we can only diagnose by knowing WHO fired them — notably the
 * Step-3 unmount, which is what silently mints a second PaymentIntent and
 * customer when a post-charge failure navigates the visitor away.
 */
const naluTrace = (...args) => {
    if ( typeof window !== 'undefined' && window.naluBookingVars?.debug ) {
        // eslint-disable-next-line no-console
        console.trace(...args);
    }
};

/**
 * Capture an unrecoverable booking-flow error to Sentry. wp-sentry's browser
 * SDK is initialised page-wide (see WpSentry.php); we just route through
 * window.Sentry when present and silently no-op when it isn't (local dev,
 * SDK not yet loaded, blocked by an extension, etc.). Tags + extras let us
 * triage in the Sentry UI without parsing message strings.
 */
function naluSentryCapture(err, ctx = {}) {
    try {
        const S = (typeof window !== 'undefined') ? window.Sentry : null;
        if (!S || typeof S.captureException !== 'function') return;
        const error = (err instanceof Error) ? err : new Error(String(err?.message || err || 'Booking flow error'));
        S.withScope?.((scope) => {
            scope.setTag?.('nalu_flow', 'booking');
            if (ctx.site) scope.setTag?.('nalu_site', String(ctx.site));
            if (ctx.code != null && ctx.code !== '') scope.setTag?.('nalu_error_code', String(ctx.code));
            scope.setExtras?.({ ...ctx });
            S.captureException(error);
        }) || S.captureException(error);
    } catch { /* never let observability break the flow */ }
}

/**
 * One-shot flag: set by naluMarkBookingSessionStarted() on the first calendar
 * load of a visit, consumed by _downloadWindow() so exactly one portal calendar
 * request carries `session_start=1`. The portal counts that as
 * `wp.booking.started` server-side — the per-shopper denominator the per-request
 * `wp.calendar` counter can't be (one shopper triggers many calendar requests:
 * month prefetch, edge sliding, modal re-opens), pairing cleanly with
 * `wp.reservation.complete`.
 *
 * Why server-side and not a browser Sentry metric: wp-sentry ships
 * @sentry/browser 9.x, whose `metrics` API is a no-op stub ("does not include
 * tracing") unless the tracing bundle is loaded — it isn't — and v9 has sunset
 * metrics anyway. So the count has to happen in the portal, where
 * Sentry.metrics.count is the proven path (see app/api/wp/calendar/route.ts).
 */
let naluSessionStartPending = false;

/**
 * Mark a booking session as started — once per browser visit, the first time the
 * full booking calendar loads. sessionStorage-guarded so it's once per visit,
 * not once per page load (the modal auto-injects site-wide, so a page-load flag
 * would count every browsed page); a module flag backs it up where sessionStorage
 * is blocked (private mode). Sets naluSessionStartPending for _downloadWindow().
 */
function naluMarkBookingSessionStarted() {
    try {
        const KEY = 'naluBookingSessionStarted';
        const ss  = (typeof window !== 'undefined') ? window.sessionStorage : null;
        if (ss) {
            if (ss.getItem(KEY)) return;
            ss.setItem(KEY, '1');
        } else {
            if (naluMarkBookingSessionStarted._fired) return;
            naluMarkBookingSessionStarted._fired = true;
        }
        naluSessionStartPending = true;
    } catch { /* observability must never break booking */ }
}

// Operator-customizable copy for the live viewer badges. Set in WP Admin
// (Settings → Nalu Booking → Availability Labels & Cutoff → Live Viewer
// Indicators) and surfaced via naluBookingVars. Defaults here mirror the
// PHP-side defaults so a missing/empty value never strips the indicator.
const DEFAULT_VIEWER_CALENDAR_TEMPLATE = '{count} booking now';
const DEFAULT_VIEWER_STEP3_TEMPLATE    = 'Others booking now: {count} ({seats} {seats:seat|seats})';

/**
 * Substitute `{name}` and `{name:singular|plural}` tokens in an operator-
 * supplied template. Numeric tokens render as the value; pluralization
 * tokens read the same number and emit `singular` when value === 1, else
 * `plural`. Unknown tokens render empty so a typo does not stop the line
 * from displaying. Falls back to the supplied default when the template is
 * missing or not a string.
 */
function formatViewerTemplate(template, vars, fallback) {
    const tpl = (typeof template === 'string' && template.length) ? template : fallback;
    return tpl.replace(/\{(\w+)(?::([^|}]*)\|([^}]*))?\}/g, (_match, name, singular, plural) => {
        const value = vars?.[name];
        if (singular !== undefined) {
            return Number(value) === 1 ? singular : plural;
        }
        return (value === undefined || value === null) ? '' : String(value);
    });
}

// HST "today" as YYYY-MM-DD, used for past/sold-out gating
export function hstTodayISO() {
  try {
    // Precise, DST-proof (HST has no DST, but this is future-proof)
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Pacific/Honolulu',
      year: 'numeric', month: '2-digit', day: '2-digit'
    }).format(new Date()); // "YYYY-MM-DD"
  } catch {
    // Fallback to your previous math if Intl/timeZone ever failed
    const now = new Date();
    const nowUtcMs = now.getTime() + now.getTimezoneOffset() * 60_000;
    const HST_OFFSET_MIN = 10 * 60; // ensure defined here if you removed the global
    const hstMs = nowUtcMs - HST_OFFSET_MIN * 60_000;
    const d = new Date(hstMs);
    const yyyy = d.getUTCFullYear();
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }
}

function addDaysISO(iso, delta) {
    const [y,m,d] = iso.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m-1, d));
    dt.setUTCDate(dt.getUTCDate() + delta);
    const yy = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth()+1).padStart(2,'0');
    const dd = String(dt.getUTCDate()).padStart(2,'0');
    return `${yy}-${mm}-${dd}`;
}


function normalizeEventsByDate(eventsByDate) {
    if (!eventsByDate || typeof eventsByDate !== 'object') return eventsByDate;
    const out = Object.create(null);

    // Re-bucket every event by its *HST* calendar date inferred from its local string
    Object.values(eventsByDate).forEach(list => {
        (list || []).forEach(ev => {
        // Prefer the server's local start string; fallback to any similar field
        const local = ev?.isoLocalStartDate || ev?.localDate || ev?.date || '';
        const key   = local ? String(local).slice(0,10) : null; // "YYYY-MM-DD"
        if (!key) return;
        (out[key] ||= []).push(ev);
        });
    });

    return out;
}


// (Optional) pretty label in HST when you need to *display* a date
export const fmtHstHuman = (iso) => dayjs.tz(iso, HST_TZ).format('dddd, MMMM D');

import { loadStripe } from "@stripe/stripe-js/pure";

/* ------------------------------------------------------------------
 * Small helper that toggles the overlay
 * ------------------------------------------------------------------*/
const $loader = () => document.getElementById('naluLoader');

// capture the original default ("Loading...") once
let _loaderDefault = null;

function showLoader(message) {
    const el = $loader();
    if (!el) return;

    const textEl = el.querySelector('.nalu-loader__text');
    if (textEl) {
        if (_loaderDefault == null) {
            _loaderDefault = textEl.dataset.loading || textEl.textContent.trim() || 'Loading...';
        }

        if (typeof message === 'string' && message.trim()) {
            // temporary override (becomes the current default until hide)
            textEl.dataset.loading = message.trim();
            textEl.textContent = message.trim();
        } else {
            // use whatever the current default is
            textEl.textContent = textEl.dataset.loading || _loaderDefault;
        }
    }

    el.classList.add('is-active');
}

function hideLoader() {
    const el = $loader();
    if (!el) return;

    const textEl = el.querySelector('.nalu-loader__text');
    if (textEl) {
        // reset to original default
        const fallback = _loaderDefault || 'Loading...';
        textEl.dataset.loading = fallback;
        textEl.textContent = fallback;
    }

    el.classList.remove('is-active');
}

/* ──────────────────────────────────────────────────────────────── *
 *  Nalu Booking UX helpers – open / close / timers                *
 * ──────────────────────────────────────────────────────────────── */
export const NaluBookingUx = (() => {

    /* cached elements */
    const $root     = () => document.querySelector('.nalu-booking-modal');
    const $exitDlg  = () => document.getElementById('nalu-exit-dialog');
    const $stillDlg = () => document.getElementById('nalu-still-dialog');
    const $closeBtn = () => document.getElementById('naluBookingClose');

    /* Internal state */
    let holdTimer = null;
    let bookingJs = null; // reference to BookingForm

    /* iOS scroll lock helpers */
    let bodyScrollY = 0;

    const lockBody = () => {
        bodyScrollY = window.scrollY || window.pageYOffset || 0;
        const body = document.body;
        body.style.position = 'fixed';
        body.style.top = `-${bodyScrollY}px`;
        body.style.left = '0';
        body.style.right = '0';
        body.style.width = '100%';
    };

    const unlockBody = () => {
        const body = document.body;
        body.style.position = '';
        body.style.top = '';
        body.style.left = '';
        body.style.right = '';
        body.style.width = '';
        window.scrollTo(0, bodyScrollY || 0);
    };

    /* Helpers */
    const openModal  = (triggerBtn) => {
        // Per-button boat preselect. Operators author `data-tour-id` (and a
        // required `data-tour-name`) on the trigger anchor; honoring it pins
        // that boat on Step-2. Re-read on every open so reopening from a plain
        // "Book Now" button clears a stale pin. `data-tour-name` is required
        // because a fully delisted boat has no card to source a name from —
        // it's the only name available for the "in maintenance" notice.
        if (bookingJs) {
            const rawId   = triggerBtn?.dataset?.tourId;
            const rawName = triggerBtn?.dataset?.tourName;
            const name    = (rawName && rawName.trim() !== '') ? rawName.trim() : null;
            bookingJs.preselectTourId   = (rawId != null && String(rawId).trim() !== '') ? String(rawId).trim() : null;
            // Capitalize the first letter so a lowercase data-tour-name ("cheap")
            // still reads as a proper boat name ("Cheap") in the Step-1 heading
            // and the maintenance notice; the rest is left as the operator typed it.
            bookingJs.preselectTourName = name ? name.charAt(0).toUpperCase() + name.slice(1) : null;
            // Intent slug (?intent= / data-intent). Re-read on every open so a
            // plain "Book Now" clears a stale intent. Forwarded on the calendar
            // request; the portal returns the intent-ordered tour list.
            const rawIntent = triggerBtn?.dataset?.intent;
            bookingJs.preselectIntent   = (rawIntent != null && String(rawIntent).trim() !== '') ? String(rawIntent).trim() : null;
        }

        const proceed = async () => {
            // close stray dialogs
            $exitDlg()?.close?.();
            $stillDlg()?.close?.();

            // Lock background scroll
            lockBody();

            $root()?.classList.add('open');
            document.body.classList.add('nalu-modal-open');

            // Wire the "more below" scroll cues now that the modal is laid out.
            // Idempotent + self-gating on overflow, so a single call covers
            // every step/breakpoint; ResizeObserver handles later re-renders.
            requestAnimationFrame(() => initScrollAffordances());

            // Reset tracking flags for this session (e.g., step views)
            // Optional but keeps things tidy
            if (bookingJs) bookingJs._tracked = {};

            // TRACK: modal open (enter Nalu)
            naluTrack('nalu_modal_open', {});

            try {
                // Always show Step 1 on open
                if (bookingJs?.showStep1) bookingJs.showStep1();

                // reset per-session view flags
                if (bookingJs) bookingJs._tracked = {};

                // Open the network gate now that modal is visible
                if (bookingJs) bookingJs._allowNetwork = true;

                // Do the very first blocked-days fetch once per page session
                if (bookingJs && !bookingJs._didInitialLoad) {
                    bookingJs._didInitialLoad = true;

                    // NEW: pick first available date (today if bookable; otherwise skip any fully blocked days)
                    try { await bookingJs.initialAutoSelectDate?.(); } catch {}

                    bookingJs.refreshBlockedDays?.(); // includes your setLoading true/false

                    // Deep-link only: warm the full window so Step 1 can show the
                    // boat's specific child-age floor (and Step 2 is instant for
                    // this month). Fire-and-forget — never blocks Step 1.
                    bookingJs._enrichDeepLinkChildAge?.();
                }
            } catch {}
            
        };

        // Find *all* open Bootstrap modals
        const openBootstrapModals = document.querySelectorAll('.modal.show');
        if (openBootstrapModals.length) {

            // Bootstrap 4 path (jQuery plugin)
            if (window.jQuery && typeof jQuery.fn.modal === 'function') {
                const $modals = jQuery(openBootstrapModals);
                // When the first one is fully hidden, assume all are done
                $modals.one('hidden.bs.modal', proceed);
                $modals.modal('hide');
                return; // proceed() will run from the event
            }

            // Bootstrap 5 path
            if (window.bootstrap && window.bootstrap.Modal) {
                let remaining = openBootstrapModals.length;

                openBootstrapModals.forEach((el) => {
                    let inst = null;

                    if (typeof window.bootstrap.Modal.getInstance === 'function') {
                        inst = window.bootstrap.Modal.getInstance(el);
                    }
                    if (!inst) {
                        inst = new window.bootstrap.Modal(el);
                    }

                    el.addEventListener(
                        'hidden.bs.modal',
                        () => {
                            remaining -= 1;
                            if (remaining === 0) {
                                proceed();
                            }
                        },
                        { once: true }
                    );

                    inst.hide();
                });

                return; // proceed() will be called when all are hidden
            }

            // Last resort fallback (no Bootstrap JS detected)
            openBootstrapModals.forEach((el) => {
                el.classList.remove('show');
                el.style.display = 'none';
            });
            document.body.classList.remove('modal-open');
            document.querySelectorAll('.modal-backdrop').forEach((el) => el.remove());

            proceed();
            return;
        }

        // No Bootstrap modal open → proceed immediately (once)
        proceed();
    };

    // CLOSE the modal and clean up according to the 4 use–cases.
    // - Case 1: Pay clicked → Stripe declined → EXIT modal  → delete PENDING reservation
    // - Case 2: (handled in Step-3 back button handler, not here)
    // - Case 3: Closing tab/window → no JS event here (cron will clean)
    // - Case 4: (handled via router/back hook, not here)
    const hardModalClose = async () => {
        if (bookingJs?._closing) return;
        bookingJs._closing = true;

        clearTimeout(holdTimer);
        bookingJs?.stopHoldCountdown?.();
        bookingJs._allowStep3 = false;

        try {
            if (bookingJs?.currentBookingId && bookingJs?._deletePendingOnLeave) {
                try {
                    if (typeof showLoader === 'function') showLoader();
                    await bookingJs.deletePendingReservation(bookingJs.currentBookingId);
                } catch (err) {
                    console.warn('Nalu pending reservation delete failed on hard close', err);
                } finally {
                    bookingJs.currentBookingId = null;
                    bookingJs.bookingHoldId    = null;
                    if (typeof hideLoader === 'function') hideLoader();
                }
            } else if (bookingJs && bookingJs.bookingHoldId && !bookingJs.confirmationShown) {
                try {
                    if (typeof showLoader === 'function') showLoader();
                    await bookingJs.deleteReservationHold(bookingJs.bookingHoldId);
                } catch (err) {
                    console.warn('Nalu hold release failed on hard close', err);
                } finally {
                    bookingJs.bookingHoldId = null;
                    if (typeof hideLoader === 'function') hideLoader();
                }
            }
        } catch (e) {
            console.warn('Nalu hardModalClose cleanup issue', e);
        }

        // Get current step for tracking
        //const stepName = bookingJs?._currentStep || 'unknown';

        // Infer a more specific step name if possible 
        // (e.g., "confirm" instead of just "step4") for better analytics;
        // fallback to "unknown" if any error
        const stepName = (() => {
            try {
                if (bookingJs?.confirmationShown) return 'confirm';
                const els = bookingJs?.els;
                if (els?.step3?.classList?.contains('active')) return 'info';
                if (els?.step2?.classList?.contains('active')) return 'tours';
                return 'search';
            } catch {
                return 'unknown';
            }
        })();

        // TRACK: modal close (exit Nalu). step_name + the hold/reservation/confirmed
        // flags are what separate a harmless close-after-success from a hard
        // abandon: "modal_close where step_name=info && confirmed=false" is the
        // payment-abandon metric, "step_name=tours" is a browse-and-leave.
        naluTrack('nalu_modal_close', {
            step_name       : stepName,
            confirmed       : !!bookingJs?.confirmationShown,
            had_hold        : !!bookingJs?.bookingHoldId,
            had_reservation : !!bookingJs?.currentBookingId,
            ...(bookingJs?._trackCtx?.() || {}),
        });

        // IMPORTANT: clear flags BEFORE touching URL
        bookingJs.confirmationShown = false;
        bookingJs._currentStep      = 'search';
        bookingJs._pressedPayNow    = false;

        // Reset UI/state (date reset should be silent; see step 3)
        bookingJs?.reset?.({ silent: true });
        
        // Make initial auto-pick + first blocked-days fetch run again next open
        bookingJs._didInitialLoad     = false;   // re-run refreshBlockedDays() once per open
        bookingJs._didInitialAutoPick = false;   // allow initialAutoSelectDate() to run again
        bookingJs._lastBlockedKey     = null;    // force new simple-calendar fetch next time

        // Clear the operator-preselected boat. These are only ever *written* (in
        // openModal, from the trigger's data-tour-id/name) — nothing else resets
        // them. Without this, a closed session leaks its boat into the next open:
        // e.g. open Boat A (in maintenance → case-C notice), exit, open Boat B and
        // the stale A title/pin shows because the reopen didn't overwrite them.
        bookingJs.preselectTourId   = null;
        bookingJs.preselectTourName = null;
        bookingJs.preselectIntent   = null;

        // Close the network gate and stop background loops. _allowNetwork is only
        // ever set true (on open), so without this it stays open after close and
        // refreshBlockedDays / month-refresh keep firing with the plugin shut —
        // and on a portal outage that means the "call us" dialog keeps popping
        // over a closed plugin. openModal() re-opens the gate next time.
        bookingJs._allowNetwork = false;
        bookingJs._stopMonthRefresh?.();

        // Also reset the DatePicker's one-time bump (if present)
        try { bookingJs.date?.resetAutoBump?.(); } catch {}

        // Close modal UI
        $root()?.classList.remove('open');
        document.body.style.overflow = '';
        document.body.classList.remove('nalu-modal-open');

        // Unlock background scroll
        unlockBody();

        // Ensure Step-1 visible next time
        if (bookingJs?.els) {
            const { step1, step2, step3 } = bookingJs.els;
            step1 && step1.classList.add('active');
            step2 && step2.classList.remove('active');
            step3 && step3.classList.remove('active');
        }

        // Clear any busy state
        try { document.querySelector('.nalu-booking-modal')?.classList.remove('is-busy'); } catch {}

        // Normalize URL without re-triggering sync
        try {
            bookingJs?.router?.replace?.({}, { silent: true });                  // clear state
            history.replaceState(null, '', location.pathname + location.search); // strip "#!..."
        } catch {}

        // Purge caches (optional)
        try {
            if (typeof fetchedWindows !== 'undefined') fetchedWindows.length = 0;
            bookingJs?.invalidateCache?.();
        } catch {}

        // reset tracking flags for next open
        if (bookingJs) bookingJs._tracked = {};

        bookingJs._closing = false;
    };

    /* Open buttons (btn-nalu-modal OR [data-nalu-modal=open]) */
    function bindOpenButtons () {
        document.querySelectorAll('[data-nalu-modal=open], .btn-nalu-modal')
        .forEach(btn => {
            if (btn._wiredNaluOpen) return; // <-- guard
            btn._wiredNaluOpen = true; // <-- mark as wired
            btn.addEventListener('click', e => {
                e.preventDefault();
                openModal(btn);
            });
        });
        
    }

    /* X / close button at top-right */
    const bindCloseButton = () => {
        const btn = $closeBtn();
        //if (!btn) return;
        if (!btn || btn._wired) return;
        btn._wired = true;

        btn.addEventListener('click', async e => {
            e.preventDefault();

            // Manage-reservation mode owns its own close UX (dirty check
            // against the manage form, navigate-home on Done, etc.).
            // Defer to it before any of the booking-flow cleanup runs so
            // we don't try to release nonexistent holds.
            if (bookingJs?._manageActive && window.NaluManageReservation) {
                window.NaluManageReservation.requestClose();
                return;
            }

            // On Step-4 (thank-you page): close immediately (no "are you sure?")
            if (bookingJs?.confirmationShown) {
                await hardModalClose();
                return;
            }

            // Otherwise show the "exit booking?" dialog
            const dlg = $exitDlg();
            if (!dlg) { 
                await hardModalClose();
                return;
            }
            dlg.classList.add('open');
            try { dlg.showModal?.(); } catch { /* non-dialog fallback ok */ }
            
            // Cancel → just hide the dialog
            dlg.querySelectorAll('[data-exit-cancel]').forEach(btn => {
                if (btn._wired) return;
                btn._wired = true;
                btn.addEventListener('click', () => {
                    dlg.classList.remove('open');
                    dlg.close?.();
                });
            });

            // Confirm → call hardModalClose (which also releases the hold if needed)
            dlg.querySelectorAll('[data-exit-confirm]').forEach(btn => {
                if (btn._wired) return;
                btn._wired = true;
                btn.addEventListener('click', async () => {
                    dlg.classList.remove('open');
                    dlg.close?.();
                    if (typeof showLoader === 'function') showLoader();
                    try {
                        await hardModalClose(); // your existing cleanup+close
                    } finally {
                        if (typeof hideLoader === 'function') hideLoader();
                    }
                });
            });
            
        });
    };

    /* 5-minute “still there?” timer */
    const startHoldTimer = () => {
        clearTimeout(holdTimer);
        holdTimer = setTimeout(() => {
            const dlg = $stillDlg();
            dlg?.showModal?.();
            if (!dlg) return; // optional: bail if the dialog isn't present
            
            dlg.querySelector('[data-still-continue]').onclick = () => {
                dlg.close();
                startHoldTimer();
            };
            dlg.querySelector('[data-still-exit]').onclick     = () => {
                dlg.close();
                hardModalClose();
            };
        }, 5*60*1000);
    };

    /* Public API */
    function bindAll (bookingFormInstance) {
        bookingJs = bookingFormInstance;
        bindOpenButtons();
        bindCloseButton();
    }

    function notifyEnteredStep3 () {
        //startHoldTimer();
    }

    // openModal is exposed so the lazy-load boot (index.js) can open the modal
    // for the click that triggered the markup fetch, before bindOpenButtons()
    // has wired the per-button handlers.
    return { bindAll, notifyEnteredStep3, hardModalClose, openModal };
})();

// Put this near the top of BookingForm.js, replacing the existing function
function groupEventsByDate(events) {
    // Format to YYYY-MM-DD in Pacific/Honolulu
    const toHstYmd = (iso) => {
        // 'en-CA' yields  YYYY-MM-DD  with Intl
        return new Intl.DateTimeFormat('en-CA', {
            timeZone: 'Pacific/Honolulu',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).format(new Date(iso));
    };

    return events.reduce((acc, ev) => {
        // 1) Strongest signal: backend's intended service day (if present)
        const key =
        ev.yyyymmdd ||
        // 2) Otherwise compute HST day from whichever ISO we’ve got
        toHstYmd(ev.isoLocalStartDate || ev.isoUTCStartDate);

        (acc[key] ||= []).push(ev);
        return acc;
    }, {});
}

/** Cache of already-downloaded windows so we don’t re-hit PHP */
const fetchedWindows = []; // [{start:'2025-07-04', end:'2025-07-28', eventsByDate:{…}, tours:{…}}, …]

function windowContains(w, iso){
  const d = dayjs(iso);
  return (d.isSame(w.start) || d.isAfter(w.start)) &&
         (d.isSame(w.end)   || d.isBefore(w.end));
}

/* --------------------------------------------------------------
   Countdown helpers (module-scope, NOT inside the class)
-------------------------------------------------------------- */
let holdTimerId  = null;

/** Fetch & apply blocked days; auto-retry later if tourId not ready */
function debounce(fn, wait = 250) {
    let t;
    return function(...args) {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
    };
}

// Nalu localStorage key for pending cleanup flag
const LS_CLEANUP_KEY = 'nalu.pending.cleanup';
const HOLD_TTL_MS    = 6 * 60 * 1000;        // 6 minutes
const PENDING_TTL_MS = 1 * 60 * 60 * 1000;   // 1 hour

export class BookingForm {
    _allowStep3 = false;
    bookingHoldId = null;     // portal hold UUID for the in-flight Step-3 session; null = no live hold
    currentBookingId = null;  // portal reservation id once the hold is claimed; null = not yet created

    constructor(root, router) { 
        this.root = root;
        this.router = router;
        this.centerDate = null;
        // Per-date "Best Available" picks from the portal, keyed yyyy-MM-dd.
        // Populated when a calendar window is adopted; renderEvents reads it.
        this.recommendations = {};
        // Portal-authored agreement text, keyed by organization_agreements id →
        // { title, message, isRequired }. The canonical wording for the tour's
        // mandatory Step-3 warnings; buildStep3 renders from this, falling back
        // to the built-in AGR_META when an older portal sends nothing.
        this.agreementsMeta = {};
        // Active intent slug (?intent=); biases the calendar request + Step-2
        // ordering. null = no intent. Set in openModal, cleared on close.
        this.preselectIntent = null;
        this.els  = {
            dateBtn : qs('#nalu-date-picker', root),
            //dateInput : qs('#nalu-date-picker', root),
            checkBtn  : qs('#nalu-booking-check', root),
            results   : qs('#nalu-results', root),
            step1     : qs('#nalu-step-1', root),
            step2     : qs('#nalu-step-2', root),
            step3     : qs('#nalu-step-3', root),
            step4     : qs('#nalu-step-4', root),
            selected  : qs('#nalu-selected-date', root),
        };
        this._currentStep = 'search'; // track where we are - for back-navigation cleanup
        this.activeTourIds = Array.isArray(window.naluBookingVars?.default_tour_ids)
            ? window.naluBookingVars.default_tour_ids.map(Number).filter(Boolean)
            : [];

        // Stop any API on page load; only allow after modal opens.
        this._allowNetwork = false;     // default: block network
        this._didInitialLoad = false;   // prevent double initial fetch
    }

    // Shared analytics context. Spread into every naluTrack call so GA4 can
    // segment the whole funnel (which tour, party size, date) without each
    // call site re-deriving it. Reads live state, so it's accurate wherever
    // it's called — including showConfirmation, where `ev` is out of scope.
    // Returns only defined keys; undefined fields are dropped by naluTrack's
    // JSON push anyway, but keep this lean so dataLayer payloads stay readable.
    _trackCtx() {
        const ev   = this.selectedEvent || {};
        const tour = (this.tours || {})[ev.tourId] || {};
        return {
            tour_id      : ev.tourId,
            tour_name    : tour.name || ev.tourName || ev.boatName,
            booking_type : this.selectedType,            // "Group" | "Private"
            adults       : this.people?.adults,
            children     : this.people?.children,
            tour_date    : ev.isoLocalStartDate,
        };
    }

    // Toggle the modal's busy state (e.g., during fetches)
    // Also shows/hides the global loader with an optional message.
    _setModalBusy(on, message = '') {
        const el = document.querySelector('.nalu-booking-modal');
        if (el) el.classList.toggle('is-busy', !!on);

        // Use the existing global loader you already built
        if (typeof this._showLoader === 'function') {
            if (on) {
            this._showLoader(true, message || 'Processing your booking…');
            } else {
            this._showLoader(false);
            }
        }
    }

    // Toggle the modal's busy state (e.g., during fetches)
    // _setModalBusy(on) {
    //     const el = document.querySelector('.nalu-booking-modal');
    //     if (el) el.classList.toggle('is-busy', !!on);
    // }

    /* ------------------------------------------------------------------
    * Bridging helper – lets internal code call the overlay in one line
    * ------------------------------------------------------------------*/
    _showLoader(on = true, message) {               // true  ⇒ show,  false ⇒ hide
        on ? showLoader(message) : hideLoader();  // you already defined these globals
    }

    // ── countdown state (timestamp-driven; no setInterval) ──
    _holdEndsAt = null;          // ms epoch when the hold expires
    _countdownRaf = null;        // rAF handle
    _visHandlerBound = null;     // visibilitychange listener (bound fn)
    _holdExpiredFired = false;   // guard so we fire expiry once
    
    // interval id for the visible ticker
    _countdownId    = null;

    /** Current fetch Promise (null = idle) */
    _fetching  = null;

    /** Next ISO date the user asked for while we were still fetching */
    _queuedISO = null;

    _lastBlockedKey = null;
    _blockedReqSeq = 0;
    _blockedInFlight = null;

    // Lazy per-month blocked-days cache. Each entry is authoritative for its
    // month: 'YYYY-MM' -> string[] of blocked HST days in it. The union across
    // loaded months is what the DatePicker greys out. Cleared when party size /
    // tour filter changes, since those drive the capacity-based availability.
    _blockedByMonth = new Map();
    _blockedParamsKey = null;
    _blockedNavTimer = null;
    // Number of foreground (non-background) blocked-days loads in flight. The
    // date spinner follows THIS, not the request seq: seq is bumped by
    // background prefetches too, so gating the spinner-clear on `seq === current`
    // let a prefetch orphan the spinner (the foreground finally sees a bumped seq
    // and skips the clear; the prefetch never clears it). A ref count can't be
    // orphaned — the spinner clears exactly when the last foreground load ends.
    _blockedLoadingCount = 0;

    // Background "keep the active month fresh" loop (Step-2 only). Self-
    // rescheduling timeout, not a fixed interval, so we can jitter each
    // cycle — that de-syncs concurrent modals so a `resv:` bump on the
    // portal doesn't herd them all onto the cold cache key at once.
    _monthRefreshTimer = null;
    _monthRefreshOn    = false;
    // Month keys (YYYY-MM) with an in-flight opportunistic prefetch, so
    // rapid navigation doesn't fire the same prefetch twice.
    _prefetchInFlight  = new Set();

    // Pick the first *unblocked* day (HST) in a short horizon on initial open
    // Pick first available date (HST) once on initial open
    async initialAutoSelectDate() {
        if (this._didInitialAutoPick) return;
        this._didInitialAutoPick = true;

        const startISO = (typeof hstTodayISO === 'function')
            ? hstTodayISO()
            : dayjs().format('YYYY-MM-DD');
        // This method only scans 120 days forward (see the pick loop below) to
        // choose the first open date, so a wider window is pure waste: the
        // blocked-days RPC materializes every (rule × day) across the range and
        // joins reservations/holds/day-flags over it. Anchor the end to startISO
        // so the fetched window exactly matches the scan horizon (and stays in
        // HST rather than mixing in a local-now dayjs()).
        const endISO   = dayjs(startISO).add(120, 'day').format('YYYY-MM-DD');

        const adults   = Number(this.people?.adults ?? 1);
        const children = Number(this.people?.children ?? 0);
        const tourIds  = Array.isArray(this.activeTourIds) ? this.activeTourIds : [];

        let blocked = [];
        try {
            this.date?.setLoading?.(true);

            if (tourIds.length === 1) {
                blocked = await fetchSimpleCalendar(startISO, endISO, tourIds[0], adults, children);
            } else {
                blocked = await fetchSimpleCalendarMulti(startISO, endISO, tourIds, adults, children);
            }
        } catch (e) {
            const msg = String(e || '');
            // AbortError/superseded are expected when a newer request supersedes this one
            if (!(e?.name === 'AbortError' || msg.includes('superseded'))) {
                console.warn('initialAutoSelectDate: simple calendar failed, fallback to today', e);
            }
        } finally {
            this.date?.setLoading?.(false);
        }

        // If the user picked a day while this fetch was in flight, don't stomp
        // their choice. The Step-1 calendar is live during the await, so a click
        // on a different day must win over the auto-selected first-available one
        // (otherwise the selection visibly snaps back when the fetch resolves).
        if (this.date?.userPicked) return;

        const blockedSet = new Set(blocked || []);
        let pick = startISO;
        for (let i = 0; i < 120; i++) {
            const ymd = dayjs(startISO).add(i, 'day').format('YYYY-MM-DD');
            if (!blockedSet.has(ymd)) { pick = ymd; break; }
        }

        // Set silently; Step 1 label updates and we’ll still fetch blocked-days after
        this.date?.setISO?.(pick, { silent: true });
    }


    // Call this when pax change or modal opens. Loads blocked days only for the
    // months the picker is (about to be) showing; navigation lazy-loads the rest
    // via _onPickerMonthView. A pax/tour-filter change clears the per-month cache
    // (capacity-based availability shifts) and forces a fresh load.
    async refreshBlockedDays() {
        if (!this._allowNetwork) return;
        const paramsChanged = this._resetBlockedCacheIfParamsChanged();
        const monthKeys = this._visibleOrAnchorMonthKeys();
        await this._loadBlockedMonths(monthKeys, { force: paramsChanged });
        this._prefetchMonthAfter(monthKeys);
    }

    // Snapshot of the inputs that decide which days are blocked. Party size and
    // the active tour filter both feed the capacity/child checks in
    // get_block_dates_v4, so a change to either invalidates every loaded month.
    _currentBlockedParamsKey() {
        const adults   = Number(this.people?.adults ?? 1);
        const children = Number(this.people?.children ?? 0);
        const tourIds  = Array.isArray(this.activeTourIds) ? [...this.activeTourIds].sort((a, b) => a - b) : [];
        return JSON.stringify({ adults, children, tourIds });
    }

    // Returns true (and clears the per-month cache) when the blocked-days inputs
    // changed since the last load, so callers know to force a refetch.
    _resetBlockedCacheIfParamsChanged() {
        const key = this._currentBlockedParamsKey();
        if (key !== this._blockedParamsKey) {
            this._blockedParamsKey = key;
            this._blockedByMonth.clear();
            // Nothing is known anymore — re-blur every visible month until the
            // fresh load lands.
            this.date?.setLoadedMonths?.([]);
            return true;
        }
        return false;
    }

    // Flattened union of every loaded month's blocked days — the full set the
    // DatePicker greys out.
    _blockedUnionArray() {
        const out = [];
        for (const days of this._blockedByMonth.values()) out.push(...days);
        return out;
    }

    // Months to cover right now: the picker's visible panels if it has drawn,
    // otherwise the selected date's month plus the next one (desktop shows two),
    // so the data is ready by the time the user opens the picker.
    _visibleOrAnchorMonthKeys() {
        const fromPicker = this.date?.visibleMonthKeys?.();
        if (fromPicker && fromPicker.length) return fromPicker;
        const anchorISO = this.date?.iso || hstTodayISO();
        return [
            dayjs(anchorISO).format('YYYY-MM'),
            dayjs(anchorISO).add(1, 'month').format('YYYY-MM'),
        ];
    }

    // Fired by the DatePicker after each draw with the months now on screen.
    // Skips entirely when everything visible is already cached, so a redraw on
    // the same month costs nothing; otherwise debounces so scrolling across
    // months only fires the (per-month-join) RPC for the one the user lands on.
    _onPickerMonthView(months) {
        if (!this._allowNetwork) return;
        if (!Array.isArray(months) || !months.length) return;
        const monthKeys = months.map(m => `${m.year}-${String(m.month).padStart(2, '0')}`);
        // Bail only when the visible months AND the one we'd prefetch are all
        // cached — otherwise we still have work to do (load and/or prefetch).
        const nextKey = this._monthAfter(monthKeys);
        if (monthKeys.every(k => this._blockedByMonth.has(k)) && this._blockedByMonth.has(nextKey)) return;
        clearTimeout(this._blockedNavTimer);
        this._blockedNavTimer = window.setTimeout(async () => {
            if (!monthKeys.every(k => this._blockedByMonth.has(k))) {
                await this._loadBlockedMonths(monthKeys);
            }
            this._prefetchMonthAfter(monthKeys);
        }, 250);
    }

    // 'YYYY-MM' of the month immediately after the last of `monthKeys`.
    _monthAfter(monthKeys) {
        const last = monthKeys[monthKeys.length - 1];
        return dayjs(last + '-01').add(1, 'month').format('YYYY-MM');
    }

    // Opportunistically warm the month just past the visible window so the next
    // forward scroll is instant (no blur). Fire-and-forget background load: no
    // spinner, no blur, errors swallowed. Skipped if already cached.
    _prefetchMonthAfter(monthKeys) {
        if (!this._allowNetwork) return;
        if (!Array.isArray(monthKeys) || !monthKeys.length) return;
        const nextKey = this._monthAfter(monthKeys);
        if (this._blockedByMonth.has(nextKey)) return;
        this._loadBlockedMonths([nextKey], { background: true });
    }

    // Fetch blocked days for `monthKeys` in one contiguous span and merge the
    // result per month. The simple-calendar response is authoritative for the
    // whole requested range (any day not returned is bookable), so each month's
    // slice can be stored and trusted — including dropping days that became
    // free. Visible panels are always adjacent months, so the span is tight.
    // force=true refetches even already-cached months (pax/tour-filter change).
    async _loadBlockedMonths(monthKeys, { force = false, background = false } = {}) {
        if (!this._allowNetwork) return;
        if (!Array.isArray(monthKeys) || !monthKeys.length) return;

        const sorted = [...new Set(monthKeys)].sort();
        const wanted = force ? sorted : sorted.filter(k => !this._blockedByMonth.has(k));
        if (!wanted.length) return;

        // One request spanning earliest→latest wanted month. Month-aligned
        // bounds keep the portal cache key shared across visitors on that month.
        const spanStart = dayjs(wanted[0] + '-01').startOf('month');
        const spanEnd   = dayjs(wanted[wanted.length - 1] + '-01').endOf('month');
        const startISO  = spanStart.format('YYYY-MM-DD');
        const endISO    = spanEnd.format('YYYY-MM-DD');

        const adults   = Number(this.people?.adults ?? 1);
        const children = Number(this.people?.children ?? 0);
        // Empty array means "all eligible tours" (server treats empty as all).
        const tourIds  = Array.isArray(this.activeTourIds) ? this.activeTourIds : [];

        // Bump sequence so a newer load (rapid nav / pax change) wins. The
        // coalescer resolves all waiters with the latest result, so only the
        // current seq may trust that the data matches the span it requested.
        const seq = (this._blockedReqSeq = (this._blockedReqSeq || 0) + 1);
        if (!background) {
            this._blockedLoadingCount += 1;
            this.date?.setLoading?.(true);
        }

        try {
            // Prefer multi when 0 or >1 tours; single is the legacy path.
            // Background loads use the isolated prefetch lane so they never
            // abort a foreground visible-month load.
            let blocked;
            if (tourIds.length === 1) {
                blocked = background
                    ? await fetchSimpleCalendarPrefetch(startISO, endISO, tourIds[0], adults, children)
                    : await fetchSimpleCalendar(startISO, endISO, tourIds[0], adults, children);
            } else {
                blocked = background
                    ? await fetchSimpleCalendarMultiPrefetch(startISO, endISO, tourIds, adults, children)
                    : await fetchSimpleCalendarMulti(startISO, endISO, tourIds, adults, children);
            }
            if (seq !== this._blockedReqSeq) return; // superseded — its data isn't ours

            // Split the authoritative span result back into per-month slices.
            // Every month in the span is marked loaded (even with zero blocked
            // days) so we don't refetch it on revisit.
            const blockedSet = new Set(blocked || []);
            for (let m = spanStart.clone(); !m.isAfter(spanEnd); m = m.add(1, 'month')) {
                const monthKey = m.format('YYYY-MM');
                const monthEnd = m.endOf('month');
                const days = [];
                for (let d = m.clone(); !d.isAfter(monthEnd); d = d.add(1, 'day')) {
                    const ymd = d.format('YYYY-MM-DD');
                    if (blockedSet.has(ymd)) days.push(ymd);
                }
                this._blockedByMonth.set(monthKey, days);
            }
            this.date?.setLoadedMonths?.([...this._blockedByMonth.keys()]);
            this.date?.setBlockedDays?.(this._blockedUnionArray());
        } catch (e) {
            // Ignore expected aborts from the coalescer/AbortController.
            const msg = String(e || '');
            if (e?.name === 'AbortError' || msg.includes('superseded')) {
                // no-op
            } else if (!background && seq === this._blockedReqSeq) {
                // A real failure means we can't tell which dates are available,
                // so the datepicker would silently look like "no availability."
                // Don't swallow it: capture for signal and route to call.
                console.warn('Blocked-days fetch failed:', e);
                naluSentryCapture(e instanceof Error ? e : new Error('Blocked-days fetch failed: ' + msg), {
                    stage: 'simple_calendar_blocked_days',
                });
                this._showAvailabilityErrorDialog();
            }
        } finally {
            // Clear the spinner only when the LAST foreground load finishes —
            // independent of seq, so a background prefetch bumping the seq can't
            // orphan it.
            if (!background) {
                this._blockedLoadingCount = Math.max(0, this._blockedLoadingCount - 1);
                if (this._blockedLoadingCount === 0) this.date?.setLoading?.(false);
            }
        }
    }

    init() {
        this.date      = new DatePicker(this.els.dateBtn);
        // Lazy-load blocked days for whatever months the picker shows, instead
        // of pre-fetching a year-plus on open. Wire before init() so the very
        // first draw is covered (network gate keeps it inert until open).
        this.date.onMonthView = (months) => this._onPickerMonthView(months);
        this.date.init();
        
        this.people  = new PassengerPicker(this.root);
        this.people.init();

        this.people.set(1, 0, { emit: true });

        // Listen for DatePicker auto “today → tomorrow” bump
        // (dispatched from DatePicker.setBlockedDays)
        this._onDateBumped = () => {
            // Invalidate the last blocked-days key so the next pax change triggers a fetch
            this._lastBlockedKey = null;
            // Optional: if the modal is open and network allowed, refresh right away
            if (this._allowNetwork) this.refreshBlockedDays();
        };
        
        window.addEventListener('nalu:date-bumped', this._onDateBumped, { passive: true });

        this._debouncedRefreshBlocked = debounce(() => this.refreshBlockedDays(), 250);
        this.people.onChange = (adults, children) => {
            this._lastBlockedKey = null;      // ensure new key is computed
            this.date?.setLoading?.(true);    // show spinner immediately
            this._debouncedRefreshBlocked();  // debounce actual fetch
        };

        // Try to clean any abandoned pending/holds left from a previous refresh
        this._recoverAbandonedFromStorage();

        // Install a tiny hook that stashes ids if the user refreshes/leaves
        this._installUnloadStash();

        this._bindCheckBtn();
        //this.els.checkBtn.addEventListener('click', () => this.onSubmit());

        // On first load, sync UI with whatever is in the hash already
        this.syncWithState(this.router.get());
        
        // Try to fetch; will defer if tourId not resolved yet
        this.refreshBlockedDays();

    }

    dateFmt(d) {
        return new Intl.DateTimeFormat('en-US', {
            weekday : 'short',
            month   : 'short',
            day     : 'numeric',
            year    : 'numeric',
            hour    : 'numeric',
            minute  : '2-digit',
            timeZone: 'Pacific/Honolulu',
            hour12  : true
        }).format(d instanceof Date ? d : new Date(d));
    }

    /**
     * THE single source of truth for the booked day (HST YYYY-MM-DD): the local
     * date of the slot the customer selected (this.selectedEvent). Every booking
     * value — hold, reservation, payment-intent metadata, dialogs — and the
     * Step-3 "When" / confirmation presentation derive from this same field, so
     * what we show can never disagree with what we book.
     *
     * Deliberately NOT `this.date.iso ?? slot`: this.date.iso is the Step-1
     * picker, and Step-2 is carousel-driven — the carousel is meant to keep the
     * picker in sync but a missed sync can leave it stale. The picker is never a
     * source for the booked day; the slot is the only truth. Returns null only
     * when no slot is selected (callers run after selection, so it's set).
     */
    get bookingDay() {
        return this.selectedEvent?.isoLocalStartDate?.slice(0, 10) || null;
    }

    /* ------------------------------------------------------------ */
    /*  Called by HashRouter each time the URL #! hash changes      */
    /* ------------------------------------------------------------ */
    async syncWithState(state) {

        // ── Back-button guard while on Confirmation:
        // If the modal is showing the confirmation page and hash changes to anything
        // else (user pressed Back), just close the modal instead of navigating.
        try {
            const nextStep = state?.step || 'search';
            if (this.confirmationShown && nextStep !== 'confirm') {
                // Back pressed from the confirmation page. Already paid, so this
                // is a benign "close after success" — tag it so it doesn't get
                // lumped in with real abandons in the close numbers.
                naluTrack('nalu_confirm_back', { ...this._trackCtx() });
                // Close modal and normalize URL (your hardModalClose already does both)
                window.NaluBookingUx?.hardModalClose?.();
                return; // stop any further step rendering
            }
        } catch {}
    
        // —— Delete pending reservation when leaving Step-3 after a decline (Case 4)
        const prevStep = this._currentStep || 'search';
        const nextStep = state?.step || 'search';

        if (prevStep === 'info' && nextStep !== 'info') {
            this._step3Mounted = false;   // allow a new mount next time
            naluTrace('[nalu:step3] UNMOUNT via syncWithState', {
                from: prevStep, to: nextStep, bookingId: this.currentBookingId,
            });
            
            // stop the 5-minute timers/countdowns started on Step-3
            try {
                // if your hold timer is in a module/global
                clearTimeout(window.holdTimer); // or just clearTimeout(holdTimer) if in scope
            } catch {}

            this.stopHoldCountdown?.();
            if (this._countdownId) {
                clearInterval(this._countdownId);
                this._countdownId = null;
            }
            
            if (this._cleanupInProgress) return;  // prevent double execution
            this._cleanupInProgress = true;
            
            try {
                if (this.currentBookingId) {
                    // A reservation was created in Step-3
                    if (typeof showLoader === 'function') showLoader('Releasing your booking…');
                    try {
                        if (!this._paymentSucceeded && this._deletePendingOnLeave) {
                            // Payment attempted but failed/declined → delete pending reservation
                            await this.deletePendingReservation(this.currentBookingId);
                        }
                    } catch (e) {
                        console.warn('Delete pending reservation failed:', e);
                    } finally {
                        if (typeof hideLoader === 'function') hideLoader();
                    }

                    // Clear both ids (reservation had claimed the hold)
                    this.currentBookingId = null;
                    this.bookingHoldId    = null;
                    this._deletePendingOnLeave = false;

                } else if (this.bookingHoldId) {
                    // No reservation yet, but a seat hold exists → delete hold
                    if (typeof showLoader === 'function') showLoader('Releasing your held seats…');
                    try {
                        await this.deleteReservationHold(this.bookingHoldId);
                    } catch (e) {
                        console.warn('Delete hold failed:', e);
                    } finally {
                        if (typeof hideLoader === 'function') hideLoader();
                    }
                    this.bookingHoldId = null;
                }
            } catch (e) {
                console.warn('Back from Step-3 cleanup failed:', e);
            } finally {
                this._cleanupInProgress = false;
            }

            this._step3Invalid = true;
            this._allowStep3   = false;
        }

        // ── Backward navigation = "I need to change something" signal.
        // search(0) → tours(1) → info(2) → confirm(3). A move to a lower index
        // is the user stepping back to edit dates/pax/tour. Fired here, the one
        // chokepoint all step transitions pass through, so forward and back are
        // captured consistently. Treat in GA4 as friction (where re-editing
        // happens), NOT as a drop-off — many back-then-forward paths still convert.
        const ORDER = { search: 0, tours: 1, info: 2, confirm: 3 };
        if (prevStep !== nextStep && ORDER[nextStep] < ORDER[prevStep]) {
            naluTrack('nalu_step_back', {
                from_step       : prevStep,
                to_step         : nextStep,
                had_hold        : !!this.bookingHoldId,
                had_reservation : !!this.currentBookingId,  // true = reached payment, then backed out
                ...this._trackCtx(),
            });
        }

        this._currentStep = nextStep;

        const { step1, step2, step3} = this.els;

        /* 0. Helper — always update date & pax fields if present */
        if (state.start_time) this.date.setISO?.(state.start_time);

        if (state.step && state.step !== 'search' && (state.adult || state.child)) {
            // Only apply pax from state when we're navigating to a non-search step
            //this.people.set?.(Number(state.adult) || 2, Number(state.child) || 0, { emit: true });
        }
        //if (state.adult || state.child) this.people.set?.(state.adult, state.child);

        /* 1. Step-1  (default when step is "search" or missing) */
        if (!state.step || state.step === 'search') {
            step1.classList.add("active");
            step2.classList.remove("active");
            step3.classList.remove("active");

            return; // nothing else to prep
        }

        /* 2. Step-2  (Tours list) */
        if (state.step === 'tours') {
            // Make the correct screen visible first
            step1.classList.remove('active');
            step2.classList.add('active');
            step3.classList.remove('active');

            // If we just came back from Step-3 (back arrow), keep the current render
            // and skip the fetch this one time.
            if (this._cameFromStep2) {
                this._cameFromStep2 = false; // reset the flag for next time
                return;
            }

            // If we're already centered on this date and Step-2 is visible, do nothing.
            if (state.start_time && state.start_time === this.centerDate) {
                return;
            }

            const iso = state.start_time || this.date?.iso || hstTodayISO();

            // Try to reuse any cached window that already covers the date
            let win = fetchedWindows.find(w => windowContains(w, iso));

            try {
                // Ensure a window exists; this will only fetch if missing/out-of-range
                if (!win) win = await this.ensureWindow(iso, { force: false });
            } catch (e) {
                console.warn('Nalu syncWithState → ensureWindow failed', e);
                win = null;
            }

            if (win) {
                // Adopt caches from the window (no extra fetch)
                this.eventsByDate = normalizeEventsByDate(win.eventsByDate);
                this.tours        = win.tours;
                this.holdConfig   = win.holdConfig || this.holdConfig || null;
                this.regionPolicy = win.regionPolicy || this.regionPolicy || null;
                this.eventsRange  = { start: win.start, end: win.end };
                this.recommendations = win.recommendations || {};

                // Redraw UI for the requested date
                this.centerDate = iso;
                this.date.setISO?.(iso);
                this.rebuildCarousel(iso);
                this.renderEvents(this.eventsByDate[iso] ?? [], this.tours);
                this.centerDateItem(iso);

                this.refreshBlockedDays();
            } else {
                // Fallback (should be rare): run regular submit without forcing a fetch
                try {
                    await this.onSubmit({ forceFetch: false, silent: true });
                } catch (e) {
                    console.warn('Nalu syncWithState → fallback submit failed', e);
                }
            }

            return;
        }

        /* 3. Step-3  (Booking form) */
        if (state.step === 'info' && state.tour_id) {
            // Block forward/invalid entries
            if (!this._allowStep3 || this._step3Invalid) {
                this._step3Invalid = false;
                (this.router.replace?.bind(this.router) || this.router.push.bind(this.router))({
                    step: 'tours',
                    start_time: state.start_time,
                    adult: state.adult,
                    child: state.child
                });
                return;
            }

            // Make sure we have a window for that day (no UI side effects)
            const win = await this.ensureWindow(state.start_time).catch(() => null);
            if (!win) {
                (this.router.replace?.bind(this.router) || this.router.push.bind(this.router))({
                    step: 'tours',
                    start_time: state.start_time,
                    adult: state.adult,
                    child: state.child
                });
                return;
            }

            // Adopt caches from the window
            this.eventsByDate = normalizeEventsByDate(win.eventsByDate);
            this.tours        = win.tours;
            this.holdConfig   = win.holdConfig || this.holdConfig || null;
            this.regionPolicy = win.regionPolicy || this.regionPolicy || null;
            this.eventsRange  = { start: win.start, end: win.end };
            this.recommendations = win.recommendations || {};

            // Look up the event on that day
            const dayEvents = this.eventsByDate?.[state.start_time] || [];
            const ev = dayEvents.find(e => String(e.eventId) === String(state.tour_id));

            if (!ev) {
                // Timeslot not present anymore – return to Step-2
                (this.router.replace?.bind(this.router) || this.router.push.bind(this.router))({
                    step: 'tours',
                    start_time: state.start_time,
                    adult: state.adult,
                    child: state.child
                });
                return;
            }

            // Show Step-3
            const { step1, step2, step3 } = this.els;
            step1.classList.remove('active');
            step2.classList.remove('active');
            step3.classList.add('active');

            this.selectedEvent = ev;
            //this.populateStep3();
            await this.populateStep3();

            // initStickySidebar();
            // requestAnimationFrame(() => initStickySidebar()); // optional settle pass

            this._allowStep3 = false;  // optional but keeps the gate strict
        }

    }

    /* ────────────────────────────────────────────────────────────── *
    *  1.  Download the calendar month containing centreISO and add  *
    *      it to the in-memory cache. Month-aligned ranges keep the  *
    *      portal-side cache hot across users on the same month.     *
    * ────────────────────────────────────────────────────────────── */
    async _downloadWindow (centreISO) {

        const centre = dayjs(centreISO);
        const start  = centre.clone().startOf('month');
        const end    = centre.clone().endOf('month');

        // Consume the once-per-visit session-start flag so exactly one calendar
        // request tells the portal a booking session began (→ wp.booking.started).
        const sessionStart = naluSessionStartPending;
        naluSessionStartPending = false;

        const res = await fetchCalendarRange({
            start_date    : start.format('YYYY-MM-DD'),
            end_date      : end  .format('YYYY-MM-DD'),
            adults        : this.people.adults,
            children      : this.people.children,
            session_start : sessionStart,
            intent        : this.preselectIntent || ''
        });

        if (!res.success) throw new Error(res.data?.message || (typeof res.data === 'string' ? res.data : null) || 'Calendar API error');

        const windowData = {
            start        : start.format('YYYY-MM-DD'),
            end          : end  .format('YYYY-MM-DD'),
            eventsByDate : groupEventsByDate(res.data.events),
            tours        : res.data.tours,
            holdConfig   : res.data.holdConfig || null,
            // SMS region policy from the portal ({ smsAllowedCountries: [...] }).
            // Used to gate the SMS opt-in by selected country; null on older
            // portals, in which case the phone field keeps its legacy allow-list.
            regionPolicy : res.data.regionPolicy || null,
            // Per-date "Best Available" picks computed by the portal
            // (lib/server/recommendation). Keyed by yyyy-MM-dd → { featuredTourId,
            // reason }. renderEvents reads the entry for this.centerDate instead of
            // re-deriving the highlight client-side. Older portals omit this; the
            // {} fallback keeps the no-highlight path working.
            recommendations : res.data.recommendations || {},
            // Portal-authored agreement text (id → { title, message, isRequired }).
            // Global (same across dates), but carried on the window so a cached
            // window restores it too. Older portals omit it → AGR_META fallback.
            agreements : res.data.agreements || {}
        };

        // Cache the agreement text now so Step-3 has it regardless of which
        // window/day is active. Keep a prior non-empty map if a later fetch
        // returns nothing, so we never regress to the AGR_META fallback once
        // the portal has supplied real text.
        if (res.data.agreements && Object.keys(res.data.agreements).length) {
            this.agreementsMeta = res.data.agreements;
        }

        /* make sure every date key exists, even when there are 0 trips */
        for (let d = start.clone(); !d.isAfter(end); d = d.add(1,'day')) {
            const k = d.format('YYYY-MM-DD');
            if (!windowData.eventsByDate[k]) windowData.eventsByDate[k] = [];
        }
        
        fetchedWindows.push(windowData); // Add to cache
        return windowData;
    }

    // Track queued request while a download is in-flight
    _queuedISO   = null;
    _queuedForce = false;

    /* ────────────────────────────────────────────────────────────── *
    *  Ensure that `iso` is covered by a cached window.
    *  - If {force:true}, re-download the window that contains `iso`
    *    (i.e., bypass cache for that date only).
    * ────────────────────────────────────────────────────────────── */
    async ensureWindow(iso, { force = false, silent = false } = {}) {
        // First full-calendar load of the visit = a booking session started.
        // Once-per-visit guarded inside; safe to call on every ensureWindow.
        naluMarkBookingSessionStarted();

        // (a) Use cache unless forced
        const contains = w => windowContains(w, iso);
        if (!force) {
            const cached = fetchedWindows.find(contains);
            if (cached) return cached;
        } else {
            // If forcing, drop the window that contains `iso` so we fetch fresh
            const idx = fetchedWindows.findIndex(contains);
            if (idx > -1) fetchedWindows.splice(idx, 1);
        }

        // (b) Another download already running → queue & wait, then re-check
        if (this._fetching) {
            this._queuedISO   = iso;
            this._queuedForce = force;
            await this._fetching;
            const qIso   = this._queuedISO;   // snapshot & clear
            const qForce = this._queuedForce;
            this._queuedISO   = null;
            this._queuedForce = false;
            // Re-run with whatever was queued last
            return this.ensureWindow(qIso || iso, { force: qForce || force });
        }

        // (c) First miss (or forced) → download new 24-day window
        if (!silent) this._showLoader(true);
        this._fetching = this._downloadWindow(iso)
            .catch(console.error)
            .finally(() => {
                this._fetching = null;
                if (!silent) this._showLoader(false);
            });

        const win = await this._fetching;

        // (d) If something new was queued while we fetched, handle it
        if (this._queuedISO && !windowContains(win, this._queuedISO)) {
            const qIso   = this._queuedISO;
            const qForce = this._queuedForce;
            this._queuedISO   = null;
            this._queuedForce = false;
            return this.ensureWindow(qIso, { force: qForce });
        }

        return win;
    }

    /* ────────────────────────────────────────────────────────────── *
    *  Fire-and-forget: pull the month *after* `centreISO` into the   *
    *  window cache so crossing the month boundary in Step-2 is       *
    *  instant. No loader, errors swallowed — purely opportunistic.   *
    *  Month-aligned by _downloadWindow, so it shares the portal      *
    *  cache key with every other visitor on that month.             *
    * ────────────────────────────────────────────────────────────── */
    async _prefetchNextMonth(centreISO) {
        if (!this._allowNetwork) return;
        if (!centreISO) return;
        const nextAnchor = dayjs(centreISO).add(1, 'month').startOf('month').format('YYYY-MM-DD');
        const monthKey = dayjs(nextAnchor).format('YYYY-MM');
        if (this._prefetchInFlight.has(monthKey)) return;                    // already fetching
        if (fetchedWindows.some(w => windowContains(w, nextAnchor))) return; // already have it
        this._prefetchInFlight.add(monthKey);
        try {
            await this._downloadWindow(nextAnchor);
        } catch (e) {
            console.warn('Nalu next-month prefetch failed', e);
        } finally {
            this._prefetchInFlight.delete(monthKey);
        }
    }

    /* Deep-link only: when the visitor arrived via a boat-specific trigger
     * (preselectTourId set in openModal), warm the full month window in the
     * background so we can fill the Step-1 child age hint with that boat's actual
     * floor (e.g. "Ages 4-12"). The hint is blank until we know the value — the
     * all-boats case has no single floor, so it stays blank at Step 1 (the value
     * shows per-tour on the Step 2 card).
     *
     * `tours` is month-invariant, so one window is enough — scrolling months
     * later never invalidates the band. Silent (no loader) + fire-and-forget:
     * Step 1 stays interactive, and the fetch doubles as a Step-2 warm-up for
     * this month (we also opportunistically pull the next month). On any error
     * we simply leave the hint blank — the band is a nicety, never a
     * blocker. Leading with the static upper bound means the floor is *appended*
     * when it lands, not a jarring correction of a wrong number. */
    async _enrichDeepLinkChildAge() {
        const note   = this.root?.querySelector('[data-child-age-note]');
        const tourId = this.preselectTourId;

        // All-boats (no deep-link): the floor varies per tour, so we don't know a
        // single value at Step 1 — show nothing (the per-tour value appears on the
        // Step 2 card). This also RESETS a boat-specific range left on this
        // persistent (server-rendered, once) DOM node by a prior deep-link
        // session — otherwise "(Ages 4-12)" leaks into the next all-boats open.
        if (tourId == null || String(tourId).trim() === '') {
            if (note) note.textContent = '';
            return;
        }
        if (!this._allowNetwork) return;

        const iso = this.date?.iso || hstTodayISO();
        let win = fetchedWindows.find(w => windowContains(w, iso));
        try {
            if (!win) win = await this.ensureWindow(iso, { silent: true });
        } catch {
            return;
        }
        // Bonus: warm the next month too so crossing the boundary in Step 2 is instant.
        this._prefetchNextMonth(iso);

        // The fetch is async; bail if the preselect changed underneath us (e.g.
        // the visitor closed and reopened as all-boats) so a stale in-flight
        // enrichment never clobbers the now-current label.
        if (String(this.preselectTourId ?? '') !== String(tourId)) return;

        const tours  = win?.tours || {};
        const tour   = tours[tourId] || Object.values(tours).find(t => String(t?.id) === String(tourId));
        const minAge = Number(tour?.minAge || 0);
        // Show the band only when the boat has a configured floor; otherwise blank.
        if (note) note.textContent = minAge > 0 ? `(Ages ${minAge}-12)` : '';
    }

    /* Re-download the month anchored by `monthAnchorISO` (any day in it)
       and swap the fresh window into `fetchedWindows`, dropping the stale
       one for that month. Returns the fresh window, or null on failure
       (the stale window is left in place). */
    async _refetchWindow(monthAnchorISO) {
        try {
            const monthKey = dayjs(monthAnchorISO).format('YYYY-MM');
            // _downloadWindow always month-aligns and pushes the result.
            const fresh = await this._downloadWindow(monthAnchorISO);
            // Drop any *older* window covering the same month so
            // windowContains() / ensureWindow() see only the fresh one.
            for (let i = fetchedWindows.length - 1; i >= 0; i--) {
                const w = fetchedWindows[i];
                if (w !== fresh && dayjs(w.start).format('YYYY-MM') === monthKey) {
                    fetchedWindows.splice(i, 1);
                }
            }
            return fresh;
        } catch (e) {
            console.warn('Nalu month refresh: re-fetch failed for', monthAnchorISO, e);
            return null;
        }
    }

    /* Re-apply the sold-out class/badge to the carousel tiles currently in
       the DOM from `this.eventsByDate` (which may have just been refreshed).
       Cheap — a class toggle and at most one span add/remove per tile, no
       network, no carousel rebuild, no scroll change. */
    _repaintCarouselTiles() {
        const track = this._getTrack?.();
        if (!track) return;
        track.querySelectorAll('.nalu-date-item').forEach(el => {
            const iso = el.dataset.date;
            if (!iso) return;
            const sold = this._isSoldOutISO(iso);
            el.classList.toggle('sold', sold);
            const content = el.querySelector('.nalu-date-item__content');
            if (!content) return;
            let badge = content.querySelector('.status');
            if (sold && !badge) {
                badge = document.createElement('span');
                badge.className = 'status';
                badge.innerHTML = 'Sold <span>Out</span>';
                content.appendChild(badge);
            } else if (!sold && badge) {
                badge.remove();
            }
        });
    }

    /* Start the Step-2 background refresh loop. Idempotent — a no-op if
       it's already running, so repeated Step-2 renders don't reset the
       cadence. Stopped by _stopMonthRefresh() (Step-3 entry, reset). */
    _startMonthRefresh() {
        if (this._monthRefreshOn) return;
        this._monthRefreshOn = true;
        const tick = async () => {
            if (!this._monthRefreshOn) return;
            try { await this._refreshActiveMonths(); }
            catch (e) { console.warn('Nalu month refresh tick failed', e); }
            if (!this._monthRefreshOn) return;
            this._monthRefreshTimer = window.setTimeout(tick, this._monthRefreshDelay());
        };
        this._monthRefreshTimer = window.setTimeout(tick, this._monthRefreshDelay());
    }

    _stopMonthRefresh() {
        this._monthRefreshOn = false;
        if (this._monthRefreshTimer) {
            clearTimeout(this._monthRefreshTimer);
            this._monthRefreshTimer = null;
        }
    }

    // 30s base + up to 30s jitter. 30s is the portal cache TTL, so faster
    // buys nothing on a quiet org and only amplifies the cold-key herd on
    // a busy one; the jitter is what actually spreads concurrent modals.
    _monthRefreshDelay() {
        return 30_000 + Math.floor(Math.random() * 30_000);
    }

    /* One refresh cycle: re-pull the month the visitor is looking at (and
       the one we eagerly prefetched) so the in-memory window cache stays
       fresh AND the portal-side calendar cache stays warm for other
       visitors. We deliberately do NOT rebuild the Step-2 tour-card list
       here — re-rendering it every cycle would flash the panel and risk a
       mid-click rebuild; a now-full slot is still caught at click time by
       the unavailable dialog. We do refresh the cheap at-a-glance bits:
       the carousel sold-out tiles. */
    async _refreshActiveMonths() {
        if (this._currentStep !== 'tours') return;          // only while on the calendar
        if (!this._allowNetwork) return;
        if (typeof document !== 'undefined' && document.hidden) return;
        if (this._fetching || this._fetchingSubmit) return; // don't fight an in-flight nav
        const centre = this.centerDate;
        if (!centre) return;

        const months = [
            dayjs(centre).startOf('month').format('YYYY-MM-DD'),
            dayjs(centre).add(1, 'month').startOf('month').format('YYYY-MM-DD'),
        ];

        for (const monthISO of months) {
            if (this._fetching || this._fetchingSubmit) break; // a nav started — let it win
            const fresh = await this._refetchWindow(monthISO);
            if (!fresh) continue;
            // If a navigation started while we were fetching, don't clobber the
            // data it just adopted — it already read our fresh window anyway.
            if (this._fetching || this._fetchingSubmit) break;
            // If this is the window currently on screen, quietly adopt it.
            if (this._currentStep === 'tours' && windowContains(fresh, this.centerDate)) {
                this.eventsByDate = normalizeEventsByDate(fresh.eventsByDate);
                this.tours        = fresh.tours;
                this.holdConfig   = fresh.holdConfig || this.holdConfig || null;
                this.regionPolicy = fresh.regionPolicy || this.regionPolicy || null;
                this.eventsRange  = { start: fresh.start, end: fresh.end };
                this.recommendations = fresh.recommendations || {};
                this._repaintCarouselTiles();
            }
        }
    }

    async onSubmit(opt = {}) {
        if (this._fetchingSubmit) return; // already running
        this._fetchingSubmit = true; // lock

        const { step1, step2, results } = this.els;

        step1.classList.remove("active");
        step2.classList.add("active");

        results.innerHTML = ``;

        // Show Loader
        showLoader('Loading Tour Availability...');

        /* ------------------------------------------------------------------ */
        /* A) pick the 12-day window we want in the carousel                  */
        /* ------------------------------------------------------------------ */
        
        // Use the HST-safe ISO from DatePicker directly (no local TZ conversions)
        const centreISO = String(this.date.iso);             // "YYYY-MM-DD" in HST from DatePicker

        this.centerDate  = centreISO;                        // keep instance-wide
        this.date.setISO?.( centreISO );                     // sync the date-picker

        // opt.forceFetch → skip cache & hit API
        const win = await this.ensureWindow(centreISO, { force: !!opt.forceFetch });

        if (!win) {
            hideLoader();
            this._fetchingSubmit = false;
            naluSentryCapture(new Error('Calendar window load failed (ensureWindow returned null)'), {
                stage: 'step1_calendar_load',
                centerDate: centreISO,
                forceFetch: !!opt.forceFetch,
            });
            // Honest failure: the window genuinely failed to load (transport,
            // auth, bad/empty portal response — all now arrive as WP_Error → 503
            // → null window). Don't fake "no longer available / try again"; send
            // the customer to call. Sentry capture above gives us the signal.
            this._showAvailabilityErrorDialog();
            return;
        }

        this.eventsByDate = normalizeEventsByDate(win.eventsByDate);  // adopt the data
        this.tours        = win.tours;
        this.holdConfig   = win.holdConfig || this.holdConfig || null;
        this.regionPolicy = win.regionPolicy || this.regionPolicy || null;
        this.eventsRange  = { start: win.start, end: win.end };
        this.recommendations = win.recommendations || {};

        /* ------------------------------------------------------------------ */
        /* C) build UI (carousel + cards)                                     */
        /* ------------------------------------------------------------------ */
        this.rebuildCarousel(centreISO, { force: true }); // Step-1 submit: hard re-center + always 13 tiles (-6 … +6)
        this.bindCarouselNavButtons();      // wire once, safe to call again

        const eventsToday = this.eventsByDate?.[centreISO] ?? [];
        this.renderEvents(eventsToday, this.tours);

        // explicit refresh with now-known data
        this.refreshBlockedDays();

        // Eagerly pull the next month in the background so crossing the month
        // boundary in the carousel is instant. Fire-and-forget — skips the
        // fetch if that month is already cached, and swallows errors.
        this._prefetchNextMonth(centreISO);

        /* Back to Step 1 Search Block */
        this._bindBackToStep1?.();

        /* Push hash only when we’re not in a silent sync */
        if (!opt.silent) {
            this.router.push({
                step       : 'tours',
                start_time : this.date.iso,
                adult      : this.people.adults,
                child      : this.people.children,
            });
        }

        // Hide Loader
        hideLoader();

        this._fetchingSubmit = false; // unlock
    }

    _bindBackToStep1() {
        const back = this.root.querySelector('.nalu-back-btn[data-step="nalu-step-1"]');
        if (!back || back._wired) return;
        back._wired = true;

        back.addEventListener('click', (e) => {
            e.preventDefault();
            showLoader();
            this.els.step1.classList.add('active');
            this.els.step2.classList.remove('active');
            this.els.step3.classList.remove('active');
            this.router.replace({
                step: 'search',
                start_time: this.date.iso,
                adult: this.people.adults,
                child: this.people.children
            });
            hideLoader();
        });
    }

    async _cleanupToStep2KeepingSearch() {
        try { showLoader(); } catch {}
        // The unavailable dialog kicks a silent, forced Step-2 refresh on open.
        // Wait for it to settle, and note whether it actually re-rendered Step-2
        // for the date we're still on — if so we can skip the re-fetch below and
        // the "Loading Tour Availability…" flash that comes with it.
        let preRefreshed = false;
        try {
            if (this._unavailRefresh) {
                await this._unavailRefresh;
                preRefreshed = (this._unavailRefreshDate === (this.date?.iso ?? null))
                            && this._inRange(this.date?.iso);
            }
        } catch {} finally { this._unavailRefresh = null; this._unavailRefreshDate = null; }
        this.stopHoldCountdown?.();
        try {
            if (this.currentBookingId && !this._paymentSucceeded) {
                await this.deletePendingReservation(this.currentBookingId);
            } else if (this.bookingHoldId) {
                await this.deleteReservationHold(this.bookingHoldId);
            }
        } catch {}
        this.currentBookingId = null;
        this.bookingHoldId    = null;
        this._allowStep3      = false;
        this._step3Invalid    = false;

        if (preRefreshed) {
            // Step-2 is already on-screen with fresh data from the dialog's
            // silent refresh — just sync the URL it skipped (it ran silent) and
            // we're done; no second fetch, no loader flash. (A hold released in
            // the teardown above isn't reflected until the next calendar action,
            // which is fine — and arguably better than re-surfacing seats we
            // just told the visitor were gone.)
            this.router.push({
                step       : 'tours',
                start_time : this.date.iso,
                adult      : this.people.adults,
                child      : this.people.children,
            });
        } else {
            // No proactive refresh (or its data is stale/out of range) — land on
            // Step-2 with a fresh fetch.
            await this.onSubmit({ forceFetch: true, silent: false });
        }
        try { hideLoader(); } catch {}
    }

    /* ========== VIRTUALIZED DATE STRIP ========== */
    /** Build one tile’s HTML once (used for add/remove on the edges) */
    /** Track node we animate (the parent “items” div) */
    _getTrack() {
        return this.root.querySelector('.nalu-date-carousel__items');
    }

    /** Remove any lingering animation classes */
    _clearTrackAnimClasses(track) {
        if (!track) return;
        track.classList.remove('animate');
        for (let i = 1; i <= 4; i++) {
            track.classList.remove(`next--${i}`);
            track.classList.remove(`prev--${i}`);
        }
    }

    /** Set/unset the single visual centre */
    _setCenterClass(iso) {
        const track = this._getTrack();
        if (!track) return;
        track.querySelectorAll('.nalu-date-item').forEach(el => {
            el.classList.toggle('nalu-date-center', el.dataset.date === iso);
        });
    }

    /** Build a single tile (uses your existing state) */
    _renderTileHTML(iso) {
        const d      = dayjs(iso);
        const todayHst = hstTodayISO();     // "YYYY-MM-DD" in HST
        const isPast   = iso < todayHst;    // string compare is safe on ISO dates
        const showSold = this._isSoldOutISO(iso);

        const classes = [
            'nalu-date-item',
            iso === this.centerDate ? 'nalu-date-center' : '',
            showSold ? 'sold' : '',
            isPast ? 'past' : '',
        ].filter(Boolean).join(' ');

        return `
            <a href="#"
            class="${classes}"
            data-date="${iso}"
            ${isPast ? 'aria-disabled="true" tabindex="-1"' : ''}>
            <span class="nalu-date-item__content">
                <span class="day">${d.format('ddd')}</span>
                <span class="date">${d.date()}</span>
                <span class="month">${d.format('MMM')}</span>
                ${showSold ? '<span class="status">Sold <span>Out</span></span>' : ''}
            </span>
            </a>`;
    }

    /** Is this ISO inside the window we fetched from the API? */
    _inRange(iso) {
        if (!this.eventsRange?.start || !this.eventsRange?.end) return false;
        // string comparisons are safe on YYYY-MM-DD
        return !(iso < this.eventsRange.start || iso > this.eventsRange.end);
    }

    /** True if the day is "sold out" — i.e., nothing on it can be booked:
     *  - future day
     *  - we have a cached window covering it
     *  - either no events, or every event is non-bookable for any reason
     *    (capacity, portal-flagged `available:false` with a reason —
     *    cutoff, private_taken, inactive, too_many_children, …).
     *
     * Read directly from `fetchedWindows` rather than `this.eventsByDate`
     * / `this.eventsRange`, so a tile for last month's date that's still
     * in the carousel strip reflects *that* month's data after we've
     * adopted next month's window into `this`. Otherwise crossing the
     * month boundary briefly clears sold-out badges on the leftover
     * tiles, then re-renders them when the new month's data lands.
     */
    _isSoldOutISO(iso) {
        const todayHst = hstTodayISO();       // "YYYY-MM-DD" in HST
        if (iso < todayHst) return false;     // string compare is safe for ISO dates

        const win = fetchedWindows.find(w => windowContains(w, iso));
        if (!win) return false;

        const evs = win.eventsByDate?.[iso] || [];
        if (evs.length === 0) return true;

        // Match _slotIsBookable (the per-card predicate) — any portal reason
        // counts as "can't book this slot", so a day with only cutoff /
        // private_taken / inactive / capacity_exceeded slots is sold-out.
        return evs.every(e => !this._slotIsBookable(e));
    }

    /** Wire click for a single tile so it animates via goToDate with exact N */
    _wireOneTile(el) {
        if (!el || el._wired) return;
        el._wired = true;
        el.addEventListener('click', async e => {
            e.preventDefault();
            if (el.classList.contains('past')) return;

            const iso   = el.dataset.date;
            if (!iso || iso === this.centerDate) return;

            // Hand off to goToDate (it will compute exact N and apply next--N/prev--N)
            await this.goToDate(iso);
        });
    }

    /** Keep 13 tiles: append right, drop first */
    _appendRightOne() {
        const track = this._getTrack();
        if (!track) return;
        const lastISO = track.querySelector('.nalu-date-item:last-child')?.dataset.date;
        if (!lastISO) return;

        const nextISO = addDaysISO(lastISO, +1);
        track.insertAdjacentHTML('beforeend', this._renderTileHTML(nextISO));
        track.firstElementChild?.remove();
        this._wireOneTile(track.lastElementChild);
    }

    /** Keep 13 tiles: append left, drop first */
    _prependLeftOne() {
        const track = this._getTrack();
        if (!track) return;
        const firstISO = track.querySelector('.nalu-date-item:first-child')?.dataset.date;
        if (!firstISO) return;

        const prevISO = addDaysISO(firstISO, -1);
        track.insertAdjacentHTML('afterbegin', this._renderTileHTML(prevISO));
        track.lastElementChild?.remove();
        this._wireOneTile(track.firstElementChild);
    }
    
    /**
     * Animate by |steps| (1..4) via classes, then hard-normalize to 13 tiles.
     * Center class flips FIRST so active styling is immediate.
     */
    async _animateStep(steps, direction /* 'next' | 'prev' */) {
        const track = this.root.querySelector('.nalu-date-carousel__items');
        if (!track) return;

        const N     = Math.min(Math.max(steps, 1), 4);
        const delta = direction === 'next' ? +N : -N;

        // 1) Preselect future centre to show active styling during the slide
        const futureCentre = dayjs(this.centerDate).add(delta, 'day').format('YYYY-MM-DD');
        this._setCenterClass(futureCentre);

        // 2) Start the visual slide
        this._clearTrackAnimClasses(track);
        track.classList.add('animate', direction === 'next' ? `next--${N}` : `prev--${N}`);

        // 3) Wait for transition end (fallback timer included)
        await new Promise((resolve) => {
            let done = false;
            const finish = () => { if (!done) { done = true; resolve(); } };
            const onEnd = (e) => { if (e.target === track) { track.removeEventListener('transitionend', onEnd); finish(); } };
            track.addEventListener('transitionend', onEnd);
            setTimeout(finish, 550);
        });

        // 4) Reset transform classes
        this._clearTrackAnimClasses(track);

        // 5) Commit new centre and NORMALIZE to 13 tiles centered on it
        this.centerDate = futureCentre;
        this._normalizeStripAroundCenter();  // <-- guarantees 6|center|6

        // 6) Ensure center class on the actual node
        this._setCenterClass(this.centerDate);

        // 7) Sync any external picker UI. Silent: this is a programmatic
        //    carousel→picker sync, not a user pick — must not flip userPicked.
        this.date.setISO?.(this.centerDate, { silent: true });
    }


    /** Build the shell once or refresh center class if already built */
    rebuildCarousel(centreISO, opts = {}) {
        const { force = false } = opts;
        const wrap = this.root.querySelector('.nalu-date-carousel');
        if (!wrap) return;

        this.centerDate = centreISO;

        let view = wrap.querySelector('.nalu-date-carousel-view');
        if (!view) {
            const tiles = [];
            for (let i = -6; i <= 6; i++) {
                tiles.push(this._renderTileHTML(addDaysISO(centreISO, i)));
            }

            wrap.innerHTML = `
                <a href="#" class="nalu-date-carousel__button nalu-date-carousel__button--prev nalu-icon--prev">
                    <span class="nalu-sr">Prev</span>
                </a>
                <div class="nalu-date-carousel-view">
                    <div class="nalu-date-carousel__items">
                    ${tiles.join('')}
                    </div>
                </div>
                <a href="#" class="nalu-date-carousel__button nalu-date-carousel__button--next nalu-icon--next">
                    <span class="nalu-sr">Next</span>
                </a>
            `;
            
            // IMPORTANT: delegate once on the track
            this.bindCarouselClicks();

            // Your existing arrow binder (works & clamps past)
            this.bindCarouselNavButtons?.();

            // Only for first paint
            this.centerDateItem?.(centreISO);
            return;
        }

        
        // Already built → maybe force replace the items to re-center hard
        const track = view.querySelector('.nalu-date-carousel__items');
        if (!track) return;

        // Helper: is new centre within current 13-tile window?
        const firstISO = track.querySelector('.nalu-date-item:first-child')?.dataset.date;
        const lastISO  = track.querySelector('.nalu-date-item:last-child')?.dataset.date;
        const inWindow = firstISO && lastISO
            ? (dayjs(centreISO).isSame(dayjs(firstISO)) || dayjs(centreISO).isAfter(dayjs(firstISO)))
            && (dayjs(centreISO).isSame(dayjs(lastISO))  || dayjs(centreISO).isBefore(dayjs(lastISO)))
            : false;

        if (force || !inWindow) {
            // Hard reset the 13 tiles centered on centreISO (keeps the same track node)
            this._clearTrackAnimClasses?.(track); // if you added this earlier
            const list = [];
            for (let i = -6; i <= 6; i++) {
                list.push(addDaysISO(centreISO, i));
                //list.push(dayjs(centreISO).add(i, 'day').format('YYYY-MM-DD'));
            }
            track.innerHTML = list.map(iso => this._renderTileHTML(iso)).join('');
            this._setCenterClass?.(centreISO); // ensure visual centre on actual node

            // If you have a helper that disables the prev arrow at boundaries, call it:
            this._updatePrevNextDisabled?.();
            return;
        }

        // If we get here, date is already within the 13-tile window. The
        // strip *must* still be re-normalized around the new centre: visual
        // centring is driven by `justify-content: center` plus a symmetric
        // strip with the centred date at slot 6/13, so leaving the existing
        // tile order in place and only flipping the highlight class would
        // anchor the new centre at whichever slot it happened to occupy in
        // the prior strip (e.g. slot 11 when the auto-pick advanced from
        // today=14 to 19), making it visually slide off to one side. The
        // container is `overflow: hidden` and not actually scrollable, so
        // scrollLeft isn't a usable lever here — re-laying-out the tiles is.
        this._normalizeStripAroundCenter();
        this._setCenterClass?.(centreISO);
    }


    /** Smoothly slide to an ISO. Adds/removes edge tiles while animating to center. */
    async _slideToISO (iso) {
        // Serialized via the shared nav chain so it can't interleave with
        // goToDate() or another _slideToISO() (see _serializeNav).
        return this._serializeNav(() => this._slideToISOImpl(iso));
    }

    async _slideToISOImpl (iso) {
        // Ensure data is present for the target day (may prefetch next window)
        const win = await this.ensureWindow(iso);
        this.eventsByDate = normalizeEventsByDate(win.eventsByDate);
        this.tours        = win.tours;
        this.eventsRange  = { start: win.start, end: win.end };
        this.recommendations = win.recommendations || {};

        const track = this.root.querySelector('.nalu-date-carousel__items');
        if (!track) return;

        const current = dayjs(this.centerDate);
        const target  = dayjs(iso);
        let deltaDays = target.diff(current, 'day'); // can be >1 or <-1

        if (deltaDays === 0) {
            // just render events for the same day
            this.renderEvents(this.eventsByDate[iso] ?? [], this.tours);
            this.centerDateItem(iso);
            return;
        }

        // When target is outside the currently rendered 13 items, walk step-by-step
        // so we can animate and maintain 13 items.
        const step = deltaDays > 0 ? +1 : -1;

        // Animate step-by-step so the strip feels like slick
        while (deltaDays !== 0) {
            const nextCentre = dayjs(this.centerDate).add(step, 'day').format('YYYY-MM-DD');

            // 1) Grow the strip on the side we’re moving toward and trim the opposite side.
            if (step > 0) this._appendRightOne(); else this._prependLeftOne();

            // 2) Update the center class to the new centre
            //    (don't rebuild the whole track; just flip classes)
            const items = track.querySelectorAll('.nalu-date-item');
            items.forEach(el => el.classList.toggle('nalu-date-center', el.dataset.date === nextCentre));

            // 3) Scroll so the newly-centered tile is visually centered (smooth)
            this.centerDateItem(nextCentre);

            // 4) Advance
            this.centerDate = nextCentre;
            deltaDays -= step;

            // tiny await so the browser can paint the smooth scroll frame-by-frame
            await new Promise(r => setTimeout(r, 150));
        }

        // Finally, sync the date picker, render the events for the new center, and update hash.
        // Silent: programmatic carousel→picker sync, not a user pick.
        this.date.setISO?.(this.centerDate, { silent: true });
        this.renderEvents(this.eventsByDate[this.centerDate] ?? [], this.tours);

        this.router.push({
            step       : 'tours',
            start_time : this.centerDate,
            adult      : this.people.adults,
            child      : this.people.children,
        });
    }

    /** Wire click for a single freshly-created tile */
    _wireOneTile (el) {
        if (!el || el._wired) return;
        el._wired = true;
        el.addEventListener('click', async e => {
            e.preventDefault();
            const iso = el.dataset.date;
            if (!iso || iso === this.centerDate) return;
            await this._slideToISO(iso);
        });
    }

    /** Re-wire (or initially wire) all tiles currently in the strip */
    bindCarouselClicks() {
        const track = this.root.querySelector('.nalu-date-carousel__items');
        if (!track || track._wired) return;
        track._wired = true;

        track.addEventListener('click', async (e) => {
            const a = e.target.closest('.nalu-date-item');
            if (!a || !track.contains(a)) return;

            e.preventDefault();
            e.stopPropagation();
            if (a.classList.contains('past')) return;

            const iso = a.dataset.date;
            if (!iso || iso === this.centerDate) return;

            await this.goToDate(iso);

            // Optional: keep your router in step-2
            this.router.push({
                step       : 'tours',
                start_time : iso,
                adult      : this.people.adults,
                child      : this.people.children
            });
        });
    }

    centerDateItem(dateStr) {
        const container = this.root.querySelector('.nalu-date-carousel-view');
        if (!container) return;
        const items = container.querySelectorAll('.nalu-date-item');

        items.forEach(el => el.classList.remove('nalu-date-center'));

        const target = Array.from(items).find(i => i.dataset.date === dateStr);
        if (!target) return;

        target.classList.add('nalu-date-center');

        // NB: visual centring of the strip is driven by `justify-content: center`
        // on `.nalu-date-carousel__items` plus a symmetric 13-tile strip around
        // `this.centerDate` (see `_normalizeStripAroundCenter`). The container
        // is `overflow: hidden` and not actually scrollable, so we don't touch
        // `scrollLeft` here — the centering job belongs to whoever rebuilt the
        // strip. This method only flips the highlight class.
    }


    /* --------------------------- NAV BUTTONS -------------------------------- */
    bindCarouselNavButtons(){
        const prev = this.root.querySelector('.nalu-date-carousel__button--prev');
        const next = this.root.querySelector('.nalu-date-carousel__button--next');

        // Accumulate rapid arrow taps and resolve them as ONE navigation after a
        // short debounce, instead of queuing an animate-and-wait per click (the
        // old "click → wait → scroll" stutter). The pending delta is anchored to
        // centerDate only at flush time, so N taps move N days in a single hop —
        // animated when ≤4 away, instant re-center when farther (see _goToDateImpl).
        const NAV_DEBOUNCE_MS = 160;
        const flushNav = async () => {
            const delta = this._pendingNavDelta || 0;
            this._pendingNavDelta = 0;
            this._navDebounceTimer = null;
            if (!delta) return;

            const todayISO = hstTodayISO();
            let targetISO  = addDaysISO(this.centerDate, delta);
            if (targetISO < todayISO) targetISO = todayISO;
            if (targetISO === this.centerDate) return;

            // Await the slide BEFORE pushing the route. router.push fires
            // syncWithState synchronously; if centerDate hasn't reached target
            // yet it hard-rebuilds the strip (instant, no animation) and the
            // glide is lost. Letting goToDate finish first means syncWithState
            // sees start_time === centerDate and no-ops.
            await this.goToDate(targetISO);   // animates ≤4, settle-glides farther
            this.router.push({
                step       : 'tours',
                start_time : targetISO,
                adult      : this.people.adults,
                child      : this.people.children
            });
        };

        const wire = (el, deltaDays) => {
            if (!el || el._wired) return;
            el._wired = true;

            el.addEventListener('click', (e) => {
                e.preventDefault(); e.stopPropagation();
                if (el.classList.contains('is-disabled')) return;

                this._pendingNavDelta = (this._pendingNavDelta || 0) + deltaDays;
                clearTimeout(this._navDebounceTimer);
                this._navDebounceTimer = setTimeout(flushNav, NAV_DEBOUNCE_MS);
            });
        };

        wire(prev, -1);
        wire(next, +1);
    }

    /** Kept for API compatibility with your other code; now uses _slideToISO */
    async moveCentreBy(days){
        const newCentreISO = dayjs(this.centerDate).add(days,'day').format('YYYY-MM-DD');
        await this._slideToISO(newCentreISO);
    }

    /**
     * Serialize carousel navigation so overlapping calls queue instead of
     * interleaving. goToDate()/_slideToISO() each await ~150–550ms per animation
     * step; without serialization a second click mid-animation starts a second
     * run that races on this.centerDate / this.date.iso, and the picker (Step-1
     * value) can be left out of sync with the rendered slot — which is how a
     * stale day reached a booking. One shared chain covers both entry points so
     * they can't interleave with each other either. Each link isolates the
     * previous one's failure so a single bad nav can't poison the queue.
     */
    _serializeNav(fn) {
        const run = (this._navChain || Promise.resolve()).catch(() => {}).then(fn);
        this._navChain = run;
        return run;
    }

    /**
     * Ensure data window, then animate in chunks of up to 4.
     * For in-strip clicks (≤4 days away), you’ll see next--2/3/4 or prev--2/3/4.
     * Public entry point — serialized via _serializeNav (see _goToDateImpl).
     */
    async goToDate(iso) {
        return this._serializeNav(() => this._goToDateImpl(iso));
    }

    async _goToDateImpl(iso) {
        const todayHst = dayjs(hstTodayISO());
        if (dayjs(iso).isBefore(todayHst, 'day')) return;

        // Ensure your data window (unchanged)
        const win = await this.ensureWindow(iso);
        this.eventsByDate = normalizeEventsByDate(win.eventsByDate);
        this.tours        = win.tours;
        this.eventsRange  = { start: win.start, end: win.end };
        this.recommendations = win.recommendations || {};

        // Optional prefetch near edges (keep if you already do)
        const d = dayjs(iso);
        if (d.diff(win.start,'day') < 3) this.ensureWindow(d.subtract(12,'day').format('YYYY-MM-DD'));
        if (dayjs(win.end).diff(d,'day') < 3) this.ensureWindow(d.add(12,'day').format('YYYY-MM-DD'));

        // Always keep "current + next month" warm as the visitor moves.
        this._prefetchNextMonth(iso);

        // A single ≤4-day hop animates cleanly. Chaining _animateStep for a
        // far jump (e.g. a "next available" chip that lands weeks out) is what
        // flickered: each step re-applied the same transform on the persistent
        // track, so the painted transform didn't change across the paint
        // boundary, the CSS transition stopped re-firing, and the interleaved
        // innerHTML rebuilds snapped the strip back and forth. Animate one
        // in-strip hop; hard re-center anything farther (instant + correct).
        // SLIDE_CAP is the most tiles we physically animate in one glide — it's
        // also the farthest single-strip slide the CSS defines (next--4/prev--4)
        // and leaves trailing tiles visible during the slide.
        const SLIDE_CAP = 4;
        const delta = d.diff(dayjs(this.centerDate), 'day');
        if (delta !== 0) {
            const dir  = delta > 0 ? 'next' : 'prev';
            const dist = Math.abs(delta);
            if (dist > SLIDE_CAP) {
                // Far jump: teleport (no animation) to within SLIDE_CAP of the
                // target, then glide the last hop in ONE _animateStep. A single
                // slide can't reproduce the chained-step flicker, so this stays
                // smooth at any distance — a quick "fast-forward then settle".
                const anchor = addDaysISO(iso, dir === 'next' ? -SLIDE_CAP : SLIDE_CAP);
                this.rebuildCarousel(anchor);
                // Commit the transform:0 anchor state so the slide actually animates.
                void this.root.querySelector('.nalu-date-carousel__items')?.offsetWidth;
                await this._animateStep(SLIDE_CAP, dir);
            } else {
                await this._animateStep(dist, dir);
            }
        }

        // Render events for the new day
        const eventsForDay = this.eventsByDate[this.centerDate] ?? [];
        this.renderEvents(eventsForDay, this.tours);
    }

    /* ------------------------------------------------------------------ */
    /*  AVAILABILITY PREDICATES + "nearby dates" suggestions              */
    /*  Shared by renderEvents (tour cards) and the unavailable dialog.   */
    /* ------------------------------------------------------------------ */

    /** Event-level availabilityMode wins; tour-level is the fallback. The portal
     *  narrows the event's mode after a partial booking on a mixed tour (e.g.
     *  group_and_private → group_only); tour-first would mask that downgrade. */
    _eventMode(event, tour) {
        return event?.availabilityMode || tour?.availabilityMode || '';
    }

    /** Mirrors renderEvents' `isBookable`: not portal-flagged unavailable and has seat capacity. */
    _slotIsBookable(event) {
        return !!event && event.available !== false && Number(event.remainingCapacity ?? 0) > 0;
    }

    /**
     * Does this slot belong to the Group or Private section?
     * `type` is 'Group' | 'Private'. Mirrors renderEvents' slotMatchesType,
     * including the legacy fallback for portals that don't send availabilityMode.
     */
    _slotMatchesBookingType(event, tour, type) {
        const wantPrivate = String(type || '').toLowerCase() === 'private';
        const mode = this._eventMode(event, tour);
        if (mode) {
            return wantPrivate
                ? (mode === 'group_and_private' || mode === 'private_only')
                : (mode === 'group_and_private' || mode === 'group_only');
        }
        const isTrue = v => v === 1 || v === '1' || v === true;
        return wantPrivate
            ? isTrue(event?.privateAvailable)
            : Number(event?.remainingCapacity ?? 0) > 0;
    }

    /** Minimum party size before a Private slot is worth offering as a suggestion. Operator-tunable (Settings → Availability); defaults to 8. */
    _privateMinPax() {
        const n = Number(naluBookingVars?.privateMinPax);
        return Number.isFinite(n) && n >= 1 ? n : 8;
    }

    /**
     * Nearby dates the visitor can rebook onto, mined from the already-cached
     * calendar window (`this.eventsByDate`). Returns up to `limit` entries
     * closest to `aroundISO`:
     *   { iso, dayOffset, times: ['09:00', ...], hasExactTime }
     * Ordered by |dayOffset|, then dates that still carry the originally
     * selected time first, then earlier date. `aroundISO` itself and past
     * dates are excluded. Like the tour-card "Next: …" footer this only sees
     * what's already cached — near a month boundary it returns a shorter list
     * rather than triggering a fetch.
     *
     * Only slots that fit the current party (`this.people`) count. If the
     * visitor explicitly picked **Private**, we suggest Private dates for that
     * tour regardless of party size — they've shown the intent. Otherwise (a
     * Group attempt, or no fixed type) we always suggest Group dates and add
     * Private dates only once the party reaches `privateMinPax`; smaller
     * parties aren't steered toward a charter. This only governs which dates
     * we proactively suggest — the Private option in Step-2 is never hidden.
     *
     * @param {object} opt
     * @param {number|string} opt.tourId   - restrict to this tour
     * @param {string} [opt.bookingType]   - 'Group' | 'Private' the visitor's attempt was for
     * @param {string} opt.aroundISO       - YYYY-MM-DD the attempt was for
     * @param {string} [opt.aroundHHMM]    - 'HH:MM' the visitor had picked
     * @param {number} [opt.limit=6]
     * @param {number} [opt.radiusDays=45]
     */
    _nearbyBookableDates({ tourId, bookingType = '', aroundISO, aroundHHMM = '', limit = 6, radiusDays = 45 } = {}) {
        if (tourId == null || !aroundISO) return [];
        const byDate   = this.eventsByDate || {};
        const tour     = this.tours?.[tourId];
        const todayHst = hstTodayISO();
        const center   = dayjs(aroundISO);
        const hhmm     = e => (e?.isoLocalStartDate || '').slice(11, 16);

        const totalPax        = (this.people?.adults || 0) + (this.people?.children || 0);
        const minSeats        = Math.max(1, totalPax);
        const privateMinPax   = this._privateMinPax();
        const wantPrivateOnly = String(bookingType).toLowerCase() === 'private';

        // Is this slot a valid suggestion for the current party?
        const slotQualifies = e => {
            if (String(e?.tourId) !== String(tourId)) return false;
            if (!this._slotIsBookable(e)) return false;
            if (Number(e?.remainingCapacity ?? 0) < minSeats) return false;   // must fit the party

            // Visitor explicitly chose Private → suggest Private dates, no pax gate.
            if (wantPrivateOnly) return this._slotMatchesBookingType(e, tour, 'Private');

            // Group attempt / no fixed type: always offer Group dates; offer
            // Private dates only when the party is large enough for a charter.
            if (this._slotMatchesBookingType(e, tour, 'Group')) return true;
            return totalPax >= privateMinPax && this._slotMatchesBookingType(e, tour, 'Private');
        };

        const out = [];
        for (const iso of Object.keys(byDate)) {
            if (iso === aroundISO || iso < todayHst) continue;
            const dayOffset = dayjs(iso).diff(center, 'day');
            if (Math.abs(dayOffset) > radiusDays) continue;

            const slots = (byDate[iso] || []).filter(slotQualifies);
            if (!slots.length) continue;

            const times = [...new Set(slots.map(hhmm).filter(Boolean))].sort();
            out.push({ iso, dayOffset, times, hasExactTime: !!aroundHHMM && times.includes(aroundHHMM) });
        }

        out.sort((a, b) =>
            Math.abs(a.dayOffset) - Math.abs(b.dayOffset)
            || (Number(b.hasExactTime) - Number(a.hasExactTime))
            || (a.iso < b.iso ? -1 : 1)
        );
        return out.slice(0, limit);
    }

    /** Render a row of date "jump" chips from a `_nearbyBookableDates` list. Empty string when none. */
    _dateChipsHTML(list, chipClass = 'nalu-date-chip') {
        if (!list || !list.length) return '';
        const label = iso => {
            const [y, m, d] = iso.split('-').map(Number);
            return new Intl.DateTimeFormat('en-US', {
                weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC',
            }).format(new Date(Date.UTC(y, m - 1, d)));
        };
        return list.map(({ iso }) =>
            `<button type="button" class="${chipClass}" data-jump-iso="${iso}">${label(iso)}</button>`
        ).join('');
    }

    /* ------------------------------------------------------------------ */
    /*  AGE-BAND STEERING (Step-2 slot selection)                         */
    /* ------------------------------------------------------------------ */

    /**
     * Age-band steering for a Step-2 slot the visitor just picked. Only fires
     * when the party includes children AND the chosen boat enforces a higher
     * minimum child age than another bookable option, so a younger child in
     * the party could be turned away. Returns the gentler boats to offer
     * instead — same-day first, and only when none run that day, nearby days
     * that do.
     *
     * Silent (`warn:false`) when there are no children, when the chosen boat is
     * already the least restrictive (minAge 0 / unset), or when nothing less
     * restrictive exists anywhere in the cached window (no actionable option →
     * no nag). `maxAge` is intentionally ignored: operators keep it fixed, so
     * `minAge` is the only axis of restrictiveness. Ages themselves aren't
     * known yet (collected later), so this is a heads-up, never a hard block.
     *
     * @param {object} event    - the selected slot (carries tourId)
     * @param {string} bookType - 'Group' | 'Private'
     * @returns {{warn:boolean, selMin:number, alternatives:Array, otherDays:Array}}
     */
    _ageBandGuidance(event, bookType) {
        const empty = { warn: false, selMin: 0, selMax: 0, alternatives: [], otherDays: [] };
        const children = Number(this.people?.children || 0);
        if (children <= 0 || !event) return empty;

        const selTour = this.tours?.[event.tourId];
        const selMin  = Number(selTour?.minAge || 0);
        const selMax  = Number(selTour?.maxAge || 0);
        if (selMin <= 0) return empty;   // nothing is more welcoming than "no minimum"

        const alternatives = this._lessRestrictiveSameDay(event, bookType, selMin);
        if (alternatives.length) return { warn: true, selMin, selMax, alternatives, otherDays: [] };

        const otherDays = this._lessRestrictiveOtherDays(selMin, bookType);
        if (otherDays.length) return { warn: true, selMin, selMax, alternatives: [], otherDays };

        return empty;   // restrictive, but no gentler option exists → stay quiet
    }

    /**
     * Bookable same-day boats with a lower child-age minimum than the picked
     * boat, that also fit the current party and booking type. One entry per
     * tour carrying ALL its fitting departure `slots` (times ascending), so the
     * dialog can render the times as pills grouped by boat. Boats ordered
     * least-restrictive first. Drawn entirely from the cached window
     * (`this.eventsByDate`) — no fetch.
     */
    _lessRestrictiveSameDay(event, bookType, selMin) {
        const day = this.bookingDay;                 // the slot's own day is the truth
        if (!day) return [];
        const dayEvents = this.eventsByDate?.[day] || [];
        const totalPax  = Number(this.people?.adults || 0) + Number(this.people?.children || 0);
        const minSeats  = Math.max(1, totalPax);
        const wantType  = String(bookType).toLowerCase() === 'private' ? 'Private' : 'Group';
        const privateMinPax = this._privateMinPax();

        const byTour = new Map();
        for (const e of dayEvents) {
            if (String(e.tourId) === String(event.tourId)) continue;
            const tour = this.tours?.[e.tourId];
            if (!tour) continue;
            const altMin = Number(tour.minAge || 0);
            if (!(altMin < selMin)) continue;                        // must be gentler
            if (!this._slotIsBookable(e)) continue;
            if (Number(e.remainingCapacity ?? 0) < minSeats) continue;
            if (wantType === 'Private' && totalPax < privateMinPax) continue;
            if (!this._slotMatchesBookingType(e, tour, wantType)) continue;

            let rec = byTour.get(String(e.tourId));
            if (!rec) {
                rec = {
                    tourId : e.tourId,
                    name   : tour.name || 'Another boat',
                    minAge : altMin,
                    maxAge : Number(tour.maxAge || 0),
                    type   : wantType,
                    slots  : [],
                };
                byTour.set(String(e.tourId), rec);
            }
            rec.slots.push(e);
        }
        // Times ascending within each boat; gentlest boats first.
        const out = [...byTour.values()];
        out.forEach(rec => rec.slots.sort(
            (a, b) => (a.isoLocalStartDate || '') < (b.isoLocalStartDate || '') ? -1 : 1
        ));
        return out.sort((a, b) => a.minAge - b.minAge);
    }

    /**
     * Nearby days (within radius, from the cached window) that run at least one
     * bookable boat with a lower child-age minimum than the picked boat and
     * that fits the party. Used only when no gentler boat runs on the chosen
     * day. Returns `[{ iso, dayOffset }]` closest-first, for `_dateChipsHTML`.
     */
    _lessRestrictiveOtherDays(selMin, bookType, { limit = 6, radiusDays = 45 } = {}) {
        const aroundISO = this.bookingDay;
        if (!aroundISO) return [];
        const byDate   = this.eventsByDate || {};
        const todayHst = hstTodayISO();
        const center   = dayjs(aroundISO);
        const totalPax = Number(this.people?.adults || 0) + Number(this.people?.children || 0);
        const minSeats = Math.max(1, totalPax);
        const wantType = String(bookType).toLowerCase() === 'private' ? 'Private' : 'Group';
        const privateMinPax = this._privateMinPax();

        const qualifies = e => {
            const tour = this.tours?.[e.tourId];
            if (!tour) return false;
            if (!(Number(tour.minAge || 0) < selMin)) return false;
            if (!this._slotIsBookable(e)) return false;
            if (Number(e.remainingCapacity ?? 0) < minSeats) return false;
            if (wantType === 'Private' && totalPax < privateMinPax) return false;
            return this._slotMatchesBookingType(e, tour, wantType);
        };

        const out = [];
        for (const iso of Object.keys(byDate)) {
            if (iso === aroundISO || iso < todayHst) continue;
            const dayOffset = dayjs(iso).diff(center, 'day');
            if (Math.abs(dayOffset) > radiusDays) continue;
            if (!(byDate[iso] || []).some(qualifies)) continue;
            out.push({ iso, dayOffset });
        }
        out.sort((a, b) => Math.abs(a.dayOffset) - Math.abs(b.dayOffset) || (a.iso < b.iso ? -1 : 1));
        return out.slice(0, limit);
    }

    /**
     * Take the reservation hold for `this.selectedEvent` and advance to Step-3.
     * Extracted verbatim from the Step-2 BOOK-button handler so the age-band
     * dialog's "switch to this boat" action re-enters the *identical* flow.
     * Hold behaviour is whatever the configured strategy does — a real hold, a
     * sentinel for the "none" strategy, or an inline-at-confirm hold; this
     * method makes no assumption that a durable hold is acquired. Reads
     * `this.selectedEvent` fresh so a swapped-in boat books correctly.
     *
     * @param {string} bookType - 'Group' | 'Private'
     */
    async _beginHoldFromStep2(bookType) {
        // Set selected Type
        this.selectedType = bookType; // "Group" | "Private"

        // TRACK: tour chosen. Fired here, before the hold fetch, so we
        // capture intent even when the hold fails (sold out / race) —
        // that's the difference between "browsed tours" and "tried to book
        // this specific one". _trackCtx now resolves the selected tour.
        naluTrack('nalu_tour_selected', { ...this._trackCtx() });

        // The booked day is the slot, always (this.bookingDay). The
        // Step-1 picker only seeds the carousel's starting point; it never
        // sources a booked day. See the bookingDay getter for the why.
        const slotDay = this.bookingDay;

        if (!slotDay) {
            // Should be impossible (data-ev always carries isoLocalStartDate),
            // but never let a hold go out without a slot-derived day.
            naluSentryCapture(new Error('Booking aborted: selected slot has no isoLocalStartDate'), {
                stage      : 'create_hold_day_guard',
                code       : 'NO_SLOT_DAY',
                pickerISO  : this.date?.iso ?? null,
                centerDate : this.centerDate ?? null,
                eventId    : this.selectedEvent?.eventId ?? null,
            });
            hideLoader();
            this._allowStep3 = false;
            this._showUnavailableDialog({
                message : 'Sorry, we could not confirm your selected date. Please choose your date again.',
                tourId  : this.selectedEvent?.tourId,
            });
            return;
        }

        // Invariant: the Step-1 date-picker value (this.date.iso) should
        // match the slot the customer is looking at in Step 2. Step 2 is
        // carousel-driven; the carousel is supposed to keep this.date.iso
        // in sync as it moves, but a missed sync (e.g. overlapping
        // animations) can leave a stale Step-1 value behind. That stale
        // value is what previously rode into the booking.
        //
        // Self-heal: the slot is authoritative, so when they drift we keep
        // going with the slot day (the correct value) and only report the
        // drift to Sentry — the customer is never interrupted, and we still
        // get the signal to fix the underlying sync.
        if (this.date?.iso && this.date.iso !== slotDay) {
            naluSentryCapture(new Error('Booking date self-healed: Step-1 picker value drifted from selected slot'), {
                stage      : 'create_hold_day_guard',
                code       : 'DATE_MISALIGNED',
                pickerISO  : this.date.iso,
                centerDate : this.centerDate ?? null,
                slotDay,
                eventId    : this.selectedEvent?.eventId ?? null,
                tourId     : this.selectedEvent?.tourId ?? null,
            });

            // Cosmetic: bring the stale picker value back in line with the
            // slot so any later read of this.date.iso is consistent. Silent
            // so it doesn't trigger picker side effects mid-booking.
            this.date?.setISO?.(slotDay, { silent: true });
        }

        const dayToSend = slotDay;

        // Is Private Booking?
        const isPrivate  = (bookType === 'Private');

        // Check availability mode for this event/tour and whether the selected booking type is allowed
        const selectedTour = this.tours?.[this.selectedEvent?.tourId] || {};
        const selectedMode = this._eventMode(this.selectedEvent, selectedTour);

        // If availabilityMode is set to 'private_only' but user selected 'Group', show unavailable
        if (selectedMode === 'private_only' && !isPrivate) {
            hideLoader();
            this._allowStep3 = false;
            this._showUnavailableDialog({ tourId: this.selectedEvent?.tourId, day: dayToSend });
            return;
        }

        // If availabilityMode is set to 'group_only' but user selected 'Private', show unavailable
        if (selectedMode === 'group_only' && isPrivate) {
            hideLoader();
            this._allowStep3 = false;
            this._showUnavailableDialog({ tourId: this.selectedEvent?.tourId, day: dayToSend });
            return;
        }

        // Get the total pax
        const totalPax = (this.people?.adults || 0) + (this.people?.children || 0);

        // Compute hold minutes based on total pax
        const dynamicHoldMinutes = this._computeHoldMinutes(totalPax);

        const createHoldBody = new URLSearchParams({
            action              : 'nalu_create_hold',
            security            : naluBookingVars.security,
            event_id            : this.selectedEvent.eventId,
            day                 : dayToSend,
            adult               : this.people.adults,
            child               : this.people.children,
            is_private          : isPrivate ? '1' : '0',
            availability_mode   : selectedMode,
            manual_booking      : (selectedTour?.manualBooking === true) ? '1' : '0',
            holdInMinutes       : String(dynamicHoldMinutes),
        });

        const creteHoldResponse = await postNaluAjax(createHoldBody);

        const holdId = creteHoldResponse?.data?.holdId ?? creteHoldResponse?.holdId ?? null;
        if (!creteHoldResponse?.success || !holdId) {
            hideLoader();
            this._allowStep3 = false;
            const msg = creteHoldResponse?.data?.message ||
                        creteHoldResponse?.message ||
                        'Could not create a reservation hold.';
            // TRACK: silent drop-off today. They picked a tour but the
            // hold couldn't be created (sold out, race, expired window).
            naluTrack('nalu_hold_failed', {
                reason: msg,
                code  : creteHoldResponse?.data?.code,
                ...this._trackCtx(),
            });
            this._showUnavailableDialog({
                message     : msg,
                tourId      : this.selectedEvent?.tourId,
                bookingType : isPrivate ? 'Private' : 'Group',
                day         : dayToSend,
                time        : this.selectedEvent?.isoLocalStartDate?.slice(11, 16),
            });
            return;
        }

        this.bookingHoldId = holdId;

        this._allowStep3 = true;
        this._step3Invalid = false;
        this.els.step2.classList.remove("active");
        this.els.step3.classList.add("active");

        // Update current step
        this._currentStep = 'info';

        await this.populateStep3();

        // Track Step-2 to Step-3 Transition
        naluTrack('nalu_tours_complete', { step: 3, step_name: 'info', ...this._trackCtx() });

        hideLoader();
        this.startHoldCountdown();
        window.NaluBookingUx?.notifyEnteredStep3?.();

        const newHash = {
            step       : "info",
            // Step 3: the slot is the date, not the Step-1 picker.
            start_time : this.bookingDay,
            adult      : this.people.adults,
            child      : this.people.children,
            tour_id    : this.selectedEvent.eventId,
            tour_date  : this.selectedEvent.isoLocalStartDate
        };
        const cur = this.router.get();
        if (cur.step !== "info" || cur.tour_id !== String(newHash.tour_id)) {
            this.router.push(newHash);
        } else {
            this.router.replace(newHash);
        }

        // Sticky Sidebar Init
        initStickySidebar();

        // optional: run again after layout settles (modal transitions)
        requestAnimationFrame(() => initStickySidebar());
    }


    /** Return the 13 ISO dates centered on `centerISO` (-6..+6). */
    _expectedISOList(centerISO) {
        const out = [];
        for (let i = -6; i <= 6; i++) {
            out.push(dayjs(centerISO).add(i,'day').format('YYYY-MM-DD'));
        }
        return out;
    }

    /**
     * Normalize the DOM so it exactly contains 13 consecutive dates
     * from center-6 to center+6, with `.nalu-date-center` on the center.
     * (Lightweight: only 13 nodes.)
     */
    _normalizeStripAroundCenter() {
        const track = this.root.querySelector('.nalu-date-carousel__items');
        if (!track) return;

        const list = this._expectedISOList(this.centerDate);
        track.innerHTML = list.map(iso => this._renderTileHTML(iso)).join('');
        
    }

    /* ------------------------------------------------------------------ */
    /*  RENDER STEP-2 CARDS                                               */
    /* ------------------------------------------------------------------ */
    renderEvents(events, tours = {}) {
        const { results } = this.els;
        const PLUGIN_URL = window.naluBookingVars.plugin_url;

        /* 1) Quick lookup: tourId → tour object */
        const tourById = Object.fromEntries(Object.values(tours).map(t => [t.id, t]));

        /* 2) Availability checks
         * "Bookable" excludes events the portal flagged unavailable
         * (available === false carries a `reason` for the disabled-card UI). */
        const isBookable = e => e?.available !== false && (e?.remainingCapacity ?? 0) > 0;

        /* 3) Group events by tourId */
        const grouped = (events || []).reduce((acc, ev) => {
            (acc[ev.tourId] ||= []).push(ev);
            return acc;
        }, {});

        /* helper: truthy → boolean */
        const isTrue = v => v === 1 || v === '1' || v === true;

        /* Contact info surfaced on group_only / private_only tours when the
         * active mode hasn't been booked yet. Hardcoded for now; promote to a
         * plugin setting if more operators reuse this codebase. */
        const CONTACT_PHONE_TEL     = '+18083389980';
        const CONTACT_PHONE_DISPLAY = '(808) 338-9980';
        const CONTACT_EMAIL         = 'info@makanacharters.com';

        /* Reason copy for unavailable events (portal returns one of these codes
         * when includeUnavailable=1; see docs/wp-calendar-include-unavailable.md
         * in the portal repo). Priority for picking a tour-level reason:
         * too_many_children > capacity_exceeded > private_taken > cutoff > inactive
         *
         * Self-explanatory inline labels — no hover tooltip needed; the user
         * can read the constraint and act (smaller party, earlier date, etc.)
         * without a second interaction.
         *
         * `cutoff` is special-cased at render time: when the event start is
         * still > CUTOFF_CALL_THRESHOLD_MIN away, the button becomes a tel:
         * link with "Call to inquire" copy, since the operator can sometimes
         * accommodate calls within that window. Closer than that and the
         * label stays "Past cutoff" with no callable affordance.
         *
         * Both the labels and the call-window threshold are operator-tunable
         * via the WP plugin settings (Availability Labels & Cutoff). The
         * defaults below are the shipped wording — used as a fallback when a
         * naluBookingVars entry is missing or empty so rendering never breaks. */
        const REASON_SHORT_DEFAULTS = {
            capacity_exceeded : 'Sold Out',
            too_many_children : 'Child Limit Reached',
            private_taken     : 'Privately Booked',
            // Soft-restriction label: rendered on Private slots whose mode
            // has been downgraded by a group booking on the same date. The
            // event is still bookable as Group; only the Private side shows
            // this disabled button. Paired with the portal's `restriction`
            // field (see docs/wp-calendar-include-unavailable.md).
            group_taken       : 'Group Booked',
            cutoff            : 'Past Cutoff',
            inactive          : 'Unavailable',
            // Temporary pause on an otherwise-active day. Customer-facing copy
            // stays neutral ("Unavailable") by default; with the Paused Call CTA
            // option on (below) the slot instead becomes a callable "Call to book".
            paused            : 'Unavailable',
        };
        const REASON_SHORT = { ...REASON_SHORT_DEFAULTS, ...(naluBookingVars?.reasonLabels || {}) };

        // Badge text for the highlight tour (first card). Operator-tunable via
        // Settings → Availability Labels & Cutoff. Default deliberately avoids
        // "today"-style wording: the customer may be viewing a future date.
        const FEATURED_BADGE_LABEL =
            (naluBookingVars?.featuredBadgeLabel || '').trim() || 'Best Available';

        // Badge on a card pinned because the visitor preselected this boat on
        // the WP page (overrides the FEATURED badge on that card).
        const SELECTED_BADGE_LABEL =
            (naluBookingVars?.selectedBadgeLabel || '').trim() || 'Your Selection';

        // Past-cutoff slots become callable when departure is still this many
        // minutes away. Operator-tunable via Settings → Availability Labels &
        // Cutoff. 0 disables the call CTA entirely (every cutoff stays inert).
        const cutoffCallRaw = Number(naluBookingVars?.cutoffCallMinutes);
        const CUTOFF_CALL_THRESHOLD_MIN = Number.isFinite(cutoffCallRaw) && cutoffCallRaw >= 0
            ? cutoffCallRaw
            : 120;

        // Sold-out call CTA: when ON (operator-tunable, default on), a slot that
        // is sold out by capacity (`capacity_exceeded`) becomes a callable tel:
        // link ("Call to book") instead of an inert disabled button, and a
        // preselected boat with every slot sold out collapses to the same
        // call/email CTA the request-only (manual_booking) card uses. Scoped to
        // true sold-out only — other unavailable reasons (cutoff, private/group
        // taken, child limit, inactive) keep their existing rendering.
        const SOLD_OUT_CALL_CTA = !!naluBookingVars?.soldOutCallCta;

        // How a fully-sold-out card's collapsed pricing region reads (when every
        // slot on the card is sold out by capacity):
        //   'cta'            → call/email contact block (default). Gated on
        //                      SOLD_OUT_CALL_CTA — it's part of the call feature.
        //   'next_available' → just the "Sold Out" heading; the next-available
        //                      date chips below become the only affordance.
        //                      Independent of SOLD_OUT_CALL_CTA (it invites no
        //                      call), so it collapses even when per-slot
        //                      "Call to book" is off.
        // Unknown/missing values fall back to 'cta' so rendering never breaks.
        const SOLD_OUT_DISPLAY = naluBookingVars?.soldOutDisplay === 'next_available'
            ? 'next_available'
            : 'cta';

        // Hide sold-out slots: when ON (operator opt-in, default OFF), a slot
        // that's sold out by capacity or booked out as a private/group charter
        // (the same reasons that count toward `allSoldOut`) is dropped from
        // per-slot rendering entirely rather than shown as a disabled
        // "Sold Out"/"Privately Booked"/"Group Booked" button. Applies only to
        // a MIXED day: on an all-sold-out day `allSoldOut` owns the card (the
        // collapsed CTA / "next available" / inert card), so hiding is skipped
        // there and the boat still reads sold out instead of vanishing. Takes
        // precedence over the per-slot Sold-Out Call CTA — a hidden slot has no
        // button left to make callable.
        const HIDE_SOLD_OUT_SLOTS = !!naluBookingVars?.hideSoldOutSlots;

        // Hide past-cutoff slots: when ON (operator opt-in, default OFF), a slot
        // the portal reports as past its booking cutoff is dropped from per-slot
        // rendering instead of shown as an inert "Past Cutoff" button — but only
        // AFTER the late-booking call window has closed for that departure. While
        // the slot is still callable ("Call to inquire", departure > CUTOFF_CALL_
        // THRESHOLD_MIN away) it keeps rendering so the operator can still take a
        // phone booking; it disappears only once that window elapses. With the
        // window at 0 (call CTA off) there's no window, so cutoff slots hide
        // immediately. Scoped to `cutoff` only — other reasons are untouched.
        const HIDE_CUTOFF_SLOTS = !!naluBookingVars?.hideCutoffSlots;

        // Paused call CTA: when ON (operator-tunable, default OFF), a slot the
        // portal reports as `paused` (a temporary, still-active pause — distinct
        // from a hard `inactive` deactivation) becomes a callable "Call to book"
        // tel: link instead of an inert disabled button. Default off so a paused
        // day keeps the neutral disabled rendering unless the operator opts in.
        const PAUSED_CALL_CTA = !!naluBookingVars?.pausedCallCta;

        /* availabilityMode helpers */
        // Event-level wins; tour-level is the fallback. The portal downgrades the
        // event's mode (e.g. group_and_private → group_only) once a group is booked
        // on a mixed-mode tour, while the tour row keeps the original mode — so
        // tour-first would mask the downgrade and let Private render as bookable
        // even after a group is locked in. The portal only ever narrows the mode
        // toward the tour's, never widens it, so event-first cannot accidentally
        // surface a direction the operator hasn't authorized.
        const getAvailabilityMode = (event, tour) => {
            return event?.availabilityMode || tour?.availabilityMode || '';
        };

        // Does this event slot belong in the group/private section?
        // (type compatibility only — bookability is checked separately by canBookXxx)
        //
        // The `restriction === 'group_taken'` clause keeps a downgraded slot
        // visible in the Private section: a group_and_private tour that's had
        // a group booking flips event-level to group_only, but we still want
        // the time to render on the Private side as a disabled "Group Booked"
        // button (parallel to how private_taken slots stay visible in both
        // sections as "Privately Booked"). canBookPrivate still returns
        // false for these, so they only render as disabled.
        const slotMatchesType = (event, tour, type) => {
            const mode = getAvailabilityMode(event, tour);
            if (mode) {
                if (type === 'group-tour')   return mode === 'group_and_private' || mode === 'group_only';
                if (type === 'private-tour') {
                    if (mode === 'group_and_private' || mode === 'private_only') return true;
                    return mode === 'group_only' && event?.restriction === 'group_taken';
                }
                return false;
            }
            // Legacy fallback if old portal does not send availabilityMode.
            if (type === 'group-tour')   return Number(event?.remainingCapacity ?? 0) > 0;
            if (type === 'private-tour') return isTrue(event?.privateAvailable);
            return false;
        };

        // Determine if group booking is allowed for this event/tour.
        // available === false → portal flagged this slot unbookable (capacity, cutoff, etc.)
        const canBookGroup = (event, tour) => {
            if (event?.available === false) return false;
            const mode = getAvailabilityMode(event, tour);

            if (mode) {
                return mode === 'group_and_private' || mode === 'group_only';
            }

            // Legacy fallback if old API does not send availabilityMode.
            return Number(event?.remainingCapacity ?? 0) > 0;
        };

        // Determine if private booking is allowed, with legacy privateAvailable fallback.
        const canBookPrivate = (event, tour) => {
            if (event?.available === false) return false;
            const mode = getAvailabilityMode(event, tour);

            if (mode) {
                return mode === 'group_and_private' || mode === 'private_only';
            }

            // Legacy fallback.
            return isTrue(event?.privateAvailable);
        };

        /* passengers from step-1 */
        const A = this.people.adults;
        const C = this.people.children;
        const paxLabel =
            `${A} Adult${A > 1 ? 's' : ''}` + (C ? `, ${C} Child${C > 1 ? 'ren' : ''}` : '');

        /* ---------- per-tour card builder (kept your original structure) ---------- */
        // `isFeatured` pins the "highlight" tour to the first card slot and adds
        // a badge (FEATURED_BADGE_LABEL, default "Best Available") + accent
        // border. The reason-stating label (vs a generic "Featured") keeps the
        // reordering from reading as arbitrary. Selection is computed below;
        // this builder only renders the flag it's handed.
        const renderCard = (tourId, tripList, isFeatured = false, isSelected = false, intentBadge = null) => {
            const tour = tourById[tourId];
            if (!tour) return '';

            tripList = tripList || [];
            const hasAnyEvents = tripList.length > 0;
            const hasBookableToday = tripList.some(isBookable);

            // "Sold out" — every event today is unbookable *because the boat/slot
            // is full or taken*, as opposed to closed for a time/operational reason
            // (past-cutoff / paused / inactive) or a party-composition reason
            // (child-limit). Three reasons count as sold out:
            //   - capacity_exceeded — no seats left (or seats hit zero).
            //   - private_taken     — the departure is booked out as a private
            //                         charter, so the whole boat is gone.
            //   - group_taken       — a group has taken the slot on the private
            //                         side. Note these are usually still bookable
            //                         *as group* (available:true), in which case
            //                         isBookable() is true and the slot never
            //                         reaches here; it only counts when a
            //                         group_taken slot is itself unbookable.
            // Only an all-sold-out day earns the collapsed pricing region (the
            // "call to book" CTA or the "Sold Out + next available" heading); a
            // mixed day (e.g. some slots merely past cutoff) falls through to
            // per-slot rendering so each slot keeps its own correct label and
            // callable affordance.
            const SOLD_OUT_REASONS = new Set(['capacity_exceeded', 'private_taken', 'group_taken']);
            const isSoldOutSlot = t => !isBookable(t) && (
                SOLD_OUT_REASONS.has(t?.reason || t?.restriction)
                || (t?.available !== false && (t?.remainingCapacity ?? 0) <= 0)
            );
            const allSoldOut = hasAnyEvents && !hasBookableToday && tripList.every(isSoldOutSlot);

            // Heading for an eventless day. Default is the generic "Not Running
            // Today", but the operator can override it with availability-window
            // copy: a pre-launch message for dates before the boat's configured
            // window, or a post-window message for dates after it. Both the
            // bound AND its message must be set. The bounds (availableFrom /
            // availableTo) are server-provided ISO YYYY-MM-DD strings, so this
            // is a plain string compare against this.centerDate — not a
            // hardcoded date. Mid-window eventless days keep the generic
            // heading.
            const centreISO = this.centerDate;
            let notRunningHeading = 'Not Running Today';
            if (tour.preAvailabilityMessage && tour.availableFrom && centreISO && centreISO < tour.availableFrom) {
                notRunningHeading = tour.preAvailabilityMessage;
            } else if (tour.postAvailabilityMessage && tour.availableTo && centreISO && centreISO > tour.availableTo) {
                notRunningHeading = tour.postAvailabilityMessage;
            }
            const ev = tripList[0]; // first event drives common meta (undefined when no events that day)
            const gTotal = A * tour.adultPrice + C * tour.childPrice;
            const pTotal = tour.privatePrice;

            // Only events carry a duration in minutes; the tours map doesn't.
            // "Not Running Today" cards therefore omit the duration line.
            const durationMinutes = Number(ev?.duration ?? 0);
            const durationHours   = durationMinutes ? Math.round(durationMinutes / 60) : 0;

            const priceBlock = (type) => {
            // Section heading is just the type — availability is conveyed by
            // the body (pricing + buttons, "Not Available", or contact CTA)
            // and by the muted is-unavailable styling, not by repeating "Tour".
            const sectionLabel = type === 'group-tour' ? 'Group' : 'Private';

            // sort trips by local time
            const mins = t => {
                const [h, m] = t.isoLocalStartDate.slice(11,16).split(':').map(Number);
                return h * 60 + m;
            };

            // Off-mode flags are needed both for the empty-section branch
            // (CTA / placeholder) AND for the slots-as-inactive override
            // below, so hoist them out of the !sorted.length block.
            const tourMode = getAvailabilityMode(undefined, tour);
            const offMode  = (type === 'private-tour' && tourMode === 'group_only')
                          || (type === 'group-tour'   && tourMode === 'private_only');

            const activeModeTouched = tripList.some(t =>
                (t?.available !== false && Number(t?.remainingCapacity ?? 0) < Number(tour?.capacity ?? Infinity))
                || (t?.available === false && (t?.reason === 'capacity_exceeded' || t?.reason === 'private_taken'))
            );

            // Operator-controlled toggles. Default ON for the CTA, OFF for
            // the slots-as-inactive override (opt-in). Slots-as-inactive
            // only takes effect when the CTA is OFF for that direction.
            //
            // wp_localize_script coerces values to strings: PHP true → "1",
            // PHP false → "". Use truthy/falsy checks so both string and
            // native boolean payloads behave the same way.
            const ctaAllowed = (
                (type === 'private-tour' && tourMode === 'group_only'
                    && !!naluBookingVars?.offerPrivateOnGroupOnly)
                ||
                (type === 'group-tour' && tourMode === 'private_only'
                    && !!naluBookingVars?.offerGroupOnPrivateOnly)
            );

            const showSlotsAsInactive = (
                (type === 'private-tour' && tourMode === 'group_only'
                    && !!naluBookingVars?.showPrivateSlotsOnGroupOnly)
                ||
                (type === 'group-tour' && tourMode === 'private_only'
                    && !!naluBookingVars?.showGroupSlotsOnPrivateOnly)
            );

            // Keep ALL slots that match the section type (group vs private),
            // including unavailable ones — they render as disabled buttons with a reason.
            let sorted = [...tripList]
                .sort((a, b) => mins(a) - mins(b))
                .filter(t => slotMatchesType(t, tour, type));

            // Override: when off-mode AND the CTA toggle is off AND the
            // operator opts in to surface the slots, replace the (empty) sorted
            // list with the full tripList marked inactive. Each event flows
            // through the regular disabled-button render path below, picking
            // up the operator-configured `inactive` reason label.
            if (offMode && !ctaAllowed && showSlotsAsInactive && tripList.length > 0) {
                sorted = [...tripList]
                    .sort((a, b) => mins(a) - mins(b))
                    .map(t => ({ ...t, available: false, reason: 'inactive' }));
            }

            // Bookability for THIS section (group vs private). Hoisted above the
            // hide filters because "sold out" is section-specific: the same
            // group_and_private event can be group-bookable (seats left) yet
            // unbookable as private (the boat is partly consumed by the group),
            // so it renders a live "Only N seats" button on the Group side and a
            // disabled "Sold Out"/"Group Booked" button on the Private side. Used
            // again below for bookableCount and the per-slot render.
            const isEnabled = type === 'group-tour' ? canBookGroup : canBookPrivate;

            // Hide sold-out slots (operator opt-in). Drop the sold-out times so
            // only bookable + other-reason slots remain. Section-aware: a slot is
            // "sold out" HERE when it can't be booked in THIS section (`!isEnabled`)
            // AND its reason/restriction is a sold-out reason — this is what the
            // per-slot render below decides too, so the event-level `isSoldOutSlot`
            // (which treats a group-bookable slot as not-sold-out and so left the
            // Private-side "Sold Out" button on screen) is deliberately not used
            // here. Guarded on !allSoldOut: on an all-sold-out day the card-level
            // collapse takes priority (see HIDE_SOLD_OUT_SLOTS note), so filtering
            // here would just empty a card that should still read "Sold Out". A
            // section left with zero slots after this falls through to the stable
            // "Not Available" placeholder below, same as any other empty section.
            if (HIDE_SOLD_OUT_SLOTS && !allSoldOut) {
                sorted = sorted.filter(t =>
                    isEnabled(t, tour) || !SOLD_OUT_REASONS.has(t?.reason || t?.restriction)
                );
            }

            // Hide past-cutoff slots (operator opt-in), but only once the
            // late-booking call window has closed for that departure. A cutoff
            // slot still inside the window renders as a callable "Call to
            // inquire" (the operator can still take a phone booking), so it's
            // kept; only the inert, past-window cutoff slots are dropped. With
            // CUTOFF_CALL_THRESHOLD_MIN at 0 the call CTA is off (no window), so
            // every cutoff slot is hideable immediately. Unlike sold-out, cutoff
            // is NOT part of the `allSoldOut` card collapse, so a day whose slots
            // are all past-window cutoff empties its sections to the stable
            // "Not Available" placeholder below rather than collapsing the card.
            if (HIDE_CUTOFF_SLOTS) {
                sorted = sorted.filter(t => {
                    if (isBookable(t)) return true;
                    const reason = t?.reason || t?.restriction || '';
                    if (reason !== 'cutoff') return true;
                    const minsToDeparture = (new Date(t.isoUTCStartDate).getTime() - Date.now()) / 60000;
                    const stillCallable = CUTOFF_CALL_THRESHOLD_MIN > 0
                        && minsToDeparture > CUTOFF_CALL_THRESHOLD_MIN;
                    return stillCallable; // keep while callable; drop after the window closes
                });
            }

            if (!sorted.length) {
                // Section has zero matching slots. Don't drop it — keep the
                // layout slot so the card's visual order is stable across
                // group_only / private_only / group_and_private tours.
                //
                // For group_only (private side) and private_only (group side),
                // surface a contact CTA while the active mode is still
                // untouched (and the operator hasn't disabled it). Otherwise
                // fall back to the inactive-reason label so the off-mode
                // language matches the disabled-button copy.
                if (offMode && !activeModeTouched && ctaAllowed) {
                    return `
                        <div class="nalu-tours__card-pricing__item ${type} is-unavailable">
                            <div class="nalu-tours__card-pricing__heading">${sectionLabel}</div>
                            <div class="nalu-tours__card-pricing__contact">
                                <div class="nalu-tours__card-pricing__contact-title">On Request</div>
                                <a class="nalu-tours__card-pricing__contact-row" href="tel:${CONTACT_PHONE_TEL}" aria-label="Call ${CONTACT_PHONE_DISPLAY}">
                                    <img src="${PLUGIN_URL}img/icon-phone.svg" width="14" height="14" alt="">
                                    <span>${CONTACT_PHONE_DISPLAY}</span>
                                </a>
                                <a class="nalu-tours__card-pricing__contact-row" href="mailto:${CONTACT_EMAIL}" aria-label="Email ${CONTACT_EMAIL}">
                                    <img src="${PLUGIN_URL}img/icon-email.svg" width="14" height="14" alt="">
                                    <span>${CONTACT_EMAIL}</span>
                                </a>
                            </div>
                        </div>`;
                }

                return `
                    <div class="nalu-tours__card-pricing__item ${type} is-unavailable">
                        <div class="nalu-tours__card-pricing__heading">${sectionLabel}</div>
                        <div class="nalu-tours__card-pricing__status">${REASON_SHORT.inactive || 'Not Available'}</div>
                    </div>`;
            }

            const bookableCount = sorted.filter(t => isEnabled(t, tour)).length;
            const heading = sectionLabel;

            const cardTotal = type === 'group-tour' ? gTotal : pTotal;
            const bookLabel = type === 'group-tour' ? 'Group' : 'Private';

            // Prefer the server-rendered price strings (set by AjaxController
            // per the operator's "Pricing Display" setting). Fall back to
            // client-side currency formatting of the legacy base total when
            // the response didn't carry tour.pricing — usually because
            // nalu_fees was missing during the request.
            const pricingForType = tour?.pricing?.[type === 'group-tour' ? 'group' : 'private'];
            const amountDisplay = pricingForType?.amountDisplay
                || (cardTotal / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
            const feesNote = pricingForType?.feesNote || '';

            // Spacer is only needed when the SIBLING section is rendering a
            // real price bar — otherwise both sides have no bar and the
            // button rows naturally line up. Compute the sibling's natural
            // bookable count (ignoring any disabled-slots override) so the
            // placeholder fires only when there's actually height to match.
            const naturalBookableCount = (theType) => {
                const isEnabledFn = theType === 'group-tour' ? canBookGroup : canBookPrivate;
                return tripList
                    .filter(t => slotMatchesType(t, tour, theType))
                    .filter(t => isEnabledFn(t, tour))
                    .length;
            };
            const siblingType = type === 'group-tour' ? 'private-tour' : 'group-tour';
            const siblingHasPriceLine = naturalBookableCount(siblingType) > 0;

            // Per-person base prices, surfaced beneath the pax label at the same
            // scale/treatment as the fees-note. Group pricing is genuinely
            // per-passenger; the private (flat-rate) side emits an invisible
            // spacer line instead so both halves' price bars keep the same
            // height and the blue band reads as one continuous strip.
            const money = cents =>
                ((cents || 0) / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
            // Secondary line beneath the pax label. Group shows the per-person
            // base rate(s); private (flat whole-boat rate) uses the same slot to
            // point parties past the boat's capacity to a phone/email instead of
            // leaving an empty spacer.
            const perPersonNote = type === 'group-tour'
                ? `${money(tour.adultPrice)}/adult`
                    + (Number(tour.childPrice) > 0 ? ` · ${money(tour.childPrice)}/child` : '')
                : 'Contact us for larger groups';

            // The private side is a flat whole-boat rate, so the per-passenger
            // breakdown ("2 Adults, 1 Child") sitting next to a $3-6k charter
            // price reads as a per-head cost. Show the boat's capacity as an
            // "Up to N Pax" ceiling instead; the group side keeps the actual
            // party count since its price genuinely scales per passenger.
            const paxCountLabel = type === 'private-tour'
                ? `Up to ${tour.capacity} Pax`
                : paxLabel;

            const priceLine = bookableCount > 0
                ? `<div class="nalu-tours__card-pricing__total">
                    <span class="nalu-tours__card-pricing__passengers">
                        <span class="nalu-tours__card-pricing__pax-count">${paxCountLabel}</span>
                        <span class="nalu-tours__card-pricing__per-person${perPersonNote ? '' : ' is-empty'}">${perPersonNote || '&nbsp;'}</span>
                    </span>
                    <span class="nalu-tours__card-pricing__amount">
                        <span class="nalu-tours__card-pricing__amount-main">${amountDisplay}</span>
                        ${feesNote ? `<span class="nalu-tours__card-pricing__fees-note">${feesNote}</span>` : ''}
                    </span>
                </div>`
                : (siblingHasPriceLine
                    ? `<div class="nalu-tours__card-pricing__total nalu-tours__card-pricing__total--placeholder" aria-hidden="true">
                        <span class="nalu-tours__card-pricing__passengers">
                            <span class="nalu-tours__card-pricing__pax-count">&nbsp;</span>
                            <span class="nalu-tours__card-pricing__per-person">&nbsp;</span>
                        </span>
                        <span class="nalu-tours__card-pricing__amount">
                            <span class="nalu-tours__card-pricing__amount-main">&nbsp;</span>
                            ${feesNote ? `<span class="nalu-tours__card-pricing__fees-note">&nbsp;</span>` : ''}
                        </span>
                    </div>`
                    : '');

            return `
                <div class="nalu-tours__card-pricing__item ${type}${bookableCount === 0 ? ' is-unavailable' : ''}">
                <div class="nalu-tours__card-pricing__heading">${heading}</div>

                ${priceLine}

                <div class="nalu-tours__card-pricing__btns">
                    ${sorted.map(t => {
                    const timeStr = new Intl.DateTimeFormat(
                        'en-US',
                        { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Pacific/Honolulu' }
                    ).format(new Date(t.isoLocalStartDate));

                    const isMorning = mins(t) < 12 * 60;
                    const btnClass  = isMorning ? 'btn-nalu-morning' : 'btn-nalu-afternoon';

                    // Disabled slot: render a non-interactive span carrying the
                    // reason code + short label. The booking-button click handler
                    // selects ".btn-nalu" only, so this span is inert by design.
                    //
                    // Exception: a `cutoff` event whose departure is still
                    // CUTOFF_CALL_THRESHOLD_MIN away becomes a callable tel:
                    // link — same disabled visual but tappable, with a phone
                    // icon and "Call to inquire" copy.
                    if (!isEnabled(t, tour)) {
                        // `reason` is the hard-stop code (available === false);
                        // `restriction` is the soft-stop code on an otherwise
                        // bookable slot — e.g. a group_and_private slot with a
                        // group already booked is `available: true` but carries
                        // `restriction: "group_taken"` to drive the Private-side
                        // disabled label. Same render path either way.
                        const reason     = t?.reason || t?.restriction || '';
                        const shortLabel = REASON_SHORT[reason] || 'Unavailable';

                        const minutesToDeparture = (new Date(t.isoUTCStartDate).getTime() - Date.now()) / 60000;
                        const isCallableCutoff  = reason === 'cutoff' && minutesToDeparture > CUTOFF_CALL_THRESHOLD_MIN;
                        // Sold-out-by-capacity slot → callable when the operator
                        // option is on AND the tour still has another slot bookable
                        // online today (`hasBookableToday`). If this sold-out time is
                        // the only path that day (e.g. the rest are past-cutoff), a
                        // "Call to book" invite is a dead end — there's nothing to move
                        // the caller onto — so it stays an inert "Sold Out" span. (A day
                        // that's *all* sold-out-by-capacity never reaches here: the card
                        // collapses to the single call/email CTA above.) Same disabled
                        // visual as the cutoff path, tappable, with "Call to book" copy.
                        const isCallableSoldOut = SOLD_OUT_CALL_CTA && reason === 'capacity_exceeded' && hasBookableToday;
                        // Paused slot → callable when the operator option is on (default
                        // off). Same disabled visual as the cutoff/sold-out paths,
                        // tappable, with "Call to book" copy.
                        const isCallablePaused  = PAUSED_CALL_CTA && reason === 'paused';

                        if (isCallableCutoff || isCallableSoldOut || isCallablePaused) {
                            const callCopy = (isCallableSoldOut || isCallablePaused) ? 'Call to book' : 'Call to inquire';
                            const ariaCtx  = isCallableSoldOut ? 'sold out' : isCallablePaused ? 'paused' : 'past cutoff';
                            return `
                                <a href="tel:${CONTACT_PHONE_TEL}"
                                class="btn-nalu--callable ${btnClass}"
                                aria-label="${timeStr} ${ariaCtx} — call ${CONTACT_PHONE_DISPLAY}"
                                data-reason="${reason}">
                                <span class="btn-text">${timeStr} <span class="btn-text-sm"><img src="${PLUGIN_URL}img/icon-phone.svg" width="12" height="12" alt="">${callCopy}</span></span>
                                </a>`;
                        }

                        return `
                            <span class="btn-nalu--disabled ${btnClass}"
                            role="button"
                            aria-disabled="true"
                            aria-label="${timeStr} — ${shortLabel}"
                            data-reason="${reason}">
                            <span class="btn-text">${timeStr} <span class="btn-text-sm">${shortLabel}</span></span>
                            </span>`;
                    }

                    const remaining = Number(
                        t.remainingCapacity ?? t.remaining ?? t.seatsRemaining ?? t.spotsLeft ?? 0
                    );

                    // "Only N seats left" — hidden by _applyViewerBadge when a live
                    // "N booking now" badge is present (the live count wins).
                    const lowBadge = (type === 'group-tour' && remaining > 0 && remaining <= 5)
                        ? `<span class="btn-text-sm nalu-low-seats">Only ${remaining} Seat${remaining > 1 ? 's' : ''} Left</span>`
                        : '';

                    // Slot identity for the post-render viewer-count decoration.
                    // The event already carries its YYYY-MM-DD as the leading
                    // chunk of isoLocalStartDate; reuse that rather than
                    // re-deriving from the centerDate (which would mis-tag a
                    // slot if it ever rendered cross-day).
                    const slotDay = (t.isoLocalStartDate || '').slice(0, 10);

                    return `
                        <a href="javascript:void(0)"
                        title="Book ${timeStr}"
                        aria-label="Book ${timeStr}"
                        class="btn-nalu ${btnClass}"
                        data-booktype="${bookLabel}"
                        data-tour="${tour.name}"
                        data-price="${tour.adultPrice}"
                        data-child="${tour.childPrice}"
                        data-event-id="${t.eventId}"
                        data-day="${slotDay}"
                        data-ev='${encodeURIComponent(JSON.stringify({
                            ...t,
                            adultPrice  : tour.adultPrice,
                            childPrice  : tour.childPrice,
                            privatePrice: tour.privatePrice,
                            tourName    : tour.name,
                            boatName    : tour.name,
                            meals       : tour.meals || []
                            }))}'>
                        <span class="btn-text">${timeStr} ${lowBadge}</span>
                        </a>`;
                    }).join('')}
                </div>
                </div>`;
            };

            // Compact treatment for a preselected boat ("Your Selection") whose
            // full card — gallery, description, amenities, pricing grid — is
            // wasted height because none of it is actionable today. On mobile
            // the full card swallows the whole viewport before the visitor
            // reaches the boats they CAN book. Two cases collapse to the compact
            // stack, both keeping the "Your Selection" badge + boat name:
            //   - request-only (manual_booking): never bookable online, so the
            //     call/email CTA is the only action — surface it directly.
            //   - not running today (zero events): show a short status +
            //     next-available chips so the visitor's intent is preserved.
            // A SOLD-OUT day (events present, none bookable) deliberately does
            // NOT collapse — it keeps the full card so the visitor still sees
            // what they're looking at, and only the group/private pricing region
            // is swapped for the call CTA further down.
            if (isSelected && (isTrue(tour.manualBooking) || !hasAnyEvents)) {
                let body;
                if (isTrue(tour.manualBooking)) {
                    // Request-only: the same call/email CTA the full card shows,
                    // reduced to the compact stack.
                    body = `
                        <div class="nalu-tours__card-compact-status">By Request Only</div>
                        <div class="nalu-tours__card-pricing__contact">
                            <div class="nalu-tours__card-pricing__contact-title">Contact us for availability</div>
                            <a class="nalu-tours__card-pricing__contact-row" href="tel:${CONTACT_PHONE_TEL}" aria-label="Call ${CONTACT_PHONE_DISPLAY}">
                                <img src="${PLUGIN_URL}img/icon-phone.svg" width="14" height="14" alt="">
                                <span>${CONTACT_PHONE_DISPLAY}</span>
                            </a>
                            <a class="nalu-tours__card-pricing__contact-row" href="mailto:${CONTACT_EMAIL}" aria-label="Email ${CONTACT_EMAIL}">
                                <img src="${PLUGIN_URL}img/icon-email.svg" width="14" height="14" alt="">
                                <span>${CONTACT_EMAIL}</span>
                            </a>
                        </div>`;
                } else {
                    // Not running today (zero events) → operator's
                    // not-running/availability-window heading + next-available
                    // chips. (A sold-out day keeps its full card and never
                    // reaches here.)
                    const near  = this._nearbyBookableDates({ tourId: tour.id, aroundISO: this.centerDate, limit: 4 });
                    const chips = this._dateChipsHTML(near);
                    body = `
                        <div class="nalu-tours__card-compact-status">${notRunningHeading}</div>
                        ${chips
                            ? `<div class="nalu-tours__card-next">
                                <span class="nalu-tours__card-next-label">Next available:</span>
                                <div class="nalu-tours__card-next-dates">${chips}</div>
                              </div>`
                            : ''}`;
                }
                return `
                <article class="nalu-tours__card nalu-tours__card--selected nalu-tours__card--compact" data-tour-id="${tourId}">
                    <div class="nalu-tours__card-compact">
                        <span class="nalu-tours__card-badge nalu-tours__card-badge--selected">${SELECTED_BADGE_LABEL}</span>
                        <h3 class="nalu-h3 nalu-tours__card-title">${tour.name}</h3>
                        ${body}
                    </div>
                </article>`;
            }

            // Gallery – first 12 media items
            const media = (tour.media || []).slice(0, 12);
            // Only the first slide is visible up front, so load it eagerly and
            // lazy-load the rest — a card with 12 images otherwise fires 12
            // requests/decodes on Step-2 render for slides nobody has scrolled to.
            const slides = media.length <= 1
                ? (media[0]
                    ? `<div class="nalu-tours__card-slider__item">
                        <img src="${media[0].url}" alt="${tour.name}" decoding="async">
                        </div>`
                    : ''
                    )
                : media.map((m, idx) => `
                    <div class="nalu-slide">
                        <div class="nalu-tours__card-slider__item">
                        <img src="${m.url}" alt="${tour.name}" loading="${idx === 0 ? 'eager' : 'lazy'}" decoding="async">
                        </div>
                    </div>
                    `).join('');

            // Card modifier + badge precedence: intent (deep-link) outranks the
            // operator-preselected "Your Selection", which outranks the
            // portal-picked "Best Available". Only one modifier class is applied;
            // the intent badge carries the portal-supplied label (e.g. "Sunset").
            const modifier = intentBadge ? ' nalu-tours__card--intent'
                : isSelected ? ' nalu-tours__card--selected'
                : isFeatured ? ' nalu-tours__card--featured' : '';
            return `
            <article class="nalu-tours__card${modifier}" data-tour-id="${tourId}">
                <div class="nalu-tours__card-img">
                    ${(isSelected || isFeatured || intentBadge) ? `<div class="nalu-tours__card-badges">${
                        intentBadge ? `<span class="nalu-tours__card-badge nalu-tours__card-badge--intent">${intentBadge}</span>` : ''
                    }${
                        isSelected ? `<span class="nalu-tours__card-badge nalu-tours__card-badge--selected">${SELECTED_BADGE_LABEL}</span>` : ''
                    }${
                        isFeatured ? `<span class="nalu-tours__card-badge">${FEATURED_BADGE_LABEL}</span>` : ''
                    }</div>` : ''}
                    <div class="nalu-tours__card-slider">${slides}</div>
                </div>
                <div class="nalu-tours__card-info">
                    <h3 class="nalu-h3 nalu-tours__card-title">${tour.name}</h3>
                    <p class="hidden">Max Child: ${tour.maxChildren}</p>

                    <div class="nalu-tours__card-amenities">
                        <div class="nalu-tours__card-amenities__item">
                            <span class="nalu-tours__card-amenities__icon">
                                <img src="${PLUGIN_URL}img/icon-passengers.svg" alt="Capacity">
                            </span> Max: ${tour.capacity}
                        </div>
                        ${durationHours ? `
                        <div class="nalu-tours__card-amenities__item">
                            <span class="nalu-tours__card-amenities__icon">
                                <img src="${PLUGIN_URL}img/icon-clock.svg" alt="Duration">
                            </span> ${durationHours} hrs
                        </div>` : ''}
                        ${Number(tour.minAge) > 0 ? `
                        <div class="nalu-tours__card-amenities__item">
                            <span class="nalu-tours__card-amenities__icon nalu-tours__card-amenities__icon--age">
                                <img src="${PLUGIN_URL}img/icon-lifebuoy.svg" alt="Minimum age">
                            </span> Min Age: ${Number(tour.minAge)}
                        </div>` : ''}
                    </div>

                    <div class="nalu-tours__card-text"><p>${tour.description || ''}</p></div>

                    ${
                        isTrue(tour.manualBooking)
                        ? `
                            <!-- Manual-booking tour: the portal flags this tour as
                                 request-only. Render the tour but replace ALL slots
                                 (available or not) with a call/email CTA. The portal's
                                 WP reservation routes also hard-reject any hold/confirm
                                 against it, so this is presentation, not enforcement. -->
                            <div class="nalu-tours__card-pricing">
                                <div class="nalu-tours__card-pricing__item is-unavailable">
                                    <div class="nalu-tours__card-pricing__heading">By Request Only</div>
                                    <div class="nalu-tours__card-pricing__contact">
                                        <div class="nalu-tours__card-pricing__contact-title">Contact us for availability</div>
                                        <a class="nalu-tours__card-pricing__contact-row" href="tel:${CONTACT_PHONE_TEL}" aria-label="Call ${CONTACT_PHONE_DISPLAY}">
                                            <img src="${PLUGIN_URL}img/icon-phone.svg" width="14" height="14" alt="">
                                            <span>${CONTACT_PHONE_DISPLAY}</span>
                                        </a>
                                        <a class="nalu-tours__card-pricing__contact-row" href="mailto:${CONTACT_EMAIL}" aria-label="Email ${CONTACT_EMAIL}">
                                            <img src="${PLUGIN_URL}img/icon-email.svg" width="14" height="14" alt="">
                                            <span>${CONTACT_EMAIL}</span>
                                        </a>
                                    </div>
                                </div>
                            </div>`
                        : !hasAnyEvents
                        ? `
                            <!-- Tour has zero events on this date — not running
                                 today, or an operator availability-window message
                                 (pre-launch / post-window) when configured. -->
                            <div class="nalu-tours__card-pricing">
                                <div class="nalu-tours__card-pricing__item is-unavailable">
                                    <div class="nalu-tours__card-pricing__heading">${notRunningHeading}</div>
                                </div>
                            </div>`
                        : (allSoldOut && SOLD_OUT_DISPLAY === 'next_available')
                        ? `
                            <!-- Sold out today (events present, none bookable) with the
                                 card display set to "next available": keep the full card
                                 but collapse the pricing region to just the "Sold Out"
                                 heading — no contact CTA. The "Next available" date chips
                                 below (via the !hasBookableToday block) are the only
                                 affordance, steering the visitor to a bookable date
                                 instead of the phone. Deliberately independent of the
                                 Sold-Out Call CTA toggle: this collapse invites no call,
                                 so it applies even when per-slot "Call to book" is off
                                 (the call toggle only gates the call/email cta variant
                                 and the per-slot links). -->
                            <div class="nalu-tours__card-pricing">
                                <div class="nalu-tours__card-pricing__item is-unavailable">
                                    <div class="nalu-tours__card-pricing__heading">${REASON_SHORT.capacity_exceeded || 'Sold Out'}</div>
                                </div>
                            </div>`
                        : (SOLD_OUT_CALL_CTA && allSoldOut)
                        ? `
                            <!-- Sold out today (events present, none bookable) with
                                 the Sold-Out Call CTA option on: keep the full card
                                 (gallery, description, amenities) so the visitor
                                 still sees what they're looking at, and replace just
                                 the group/private pricing region with one call/email
                                 CTA — instead of repeating a "Call to book" button on
                                 every sold-out slot. The "Next available" chips below
                                 still render via the !hasBookableToday block. -->
                            <div class="nalu-tours__card-pricing">
                                <div class="nalu-tours__card-pricing__item is-unavailable">
                                    <div class="nalu-tours__card-pricing__heading">${REASON_SHORT.capacity_exceeded || 'Sold Out'}</div>
                                    <div class="nalu-tours__card-pricing__contact">
                                        <div class="nalu-tours__card-pricing__contact-title">Sold out online — call to book</div>
                                        <a class="nalu-tours__card-pricing__contact-row" href="tel:${CONTACT_PHONE_TEL}" aria-label="Call ${CONTACT_PHONE_DISPLAY}">
                                            <img src="${PLUGIN_URL}img/icon-phone.svg" width="14" height="14" alt="">
                                            <span>${CONTACT_PHONE_DISPLAY}</span>
                                        </a>
                                        <a class="nalu-tours__card-pricing__contact-row" href="mailto:${CONTACT_EMAIL}" aria-label="Email ${CONTACT_EMAIL}">
                                            <img src="${PLUGIN_URL}img/icon-email.svg" width="14" height="14" alt="">
                                            <span>${CONTACT_EMAIL}</span>
                                        </a>
                                    </div>
                                </div>
                            </div>`
                        : `
                            <!-- Normal (Nalu-managed) pricing blocks -->
                            <div class="nalu-tours__card-pricing">
                                ${priceBlock('group-tour')}
                                ${priceBlock('private-tour')}
                            </div>`
                        }
                    ${(!isTrue(tour.manualBooking) && !hasBookableToday) ? (() => {
                        // Fully unavailable on this date — offer a small window of
                        // upcoming dates this tour is bookable on, mined from the
                        // cached month. Each chip hands off to goToDate. (Cross-month
                        // gaps just yield a shorter list — no prefetch.)
                        const near = this._nearbyBookableDates({ tourId: tour.id, aroundISO: this.centerDate, limit: 4 });
                        const chips = this._dateChipsHTML(near);   // .nalu-date-chip; compact size via .nalu-tours__card-next-dates scope
                        return chips
                            ? `<div class="nalu-tours__card-next">
                                <span class="nalu-tours__card-next-label">Next available:</span>
                                <div class="nalu-tours__card-next-dates">${chips}</div>
                              </div>`
                            : '';
                    })() : ''}
                </div>
            </article>`;
        };

        /* 4) Iterate every Nalu-managed tour in the response, not just those
         *    with events. Tours with zero events on the selected date render
         *    as a "Not Running Today" card. */
        const allTours = Object.values(tours || {});
        const allTourIds = allTours.map(t => t.id).filter(id => id != null);

        // Operator-configured order — used within each availability tier and as
        // the final tie-break for the highlight pick.
        const bySortOrder = (a, b) => {
            const sa = Number((tourById[a] && tourById[a].sortOrder) ?? Number.POSITIVE_INFINITY);
            const sb = Number((tourById[b] && tourById[b].sortOrder) ?? Number.POSITIVE_INFINITY);
            if (sa !== sb) return sa - sb;                         // primary: sortOrder asc
            const na = (tourById[a]?.name || '').localeCompare(tourById[b]?.name || '');
            if (na !== 0) return na;                               // tie-break: name asc
            return Number(a) - Number(b);                          // final: id asc
        };

        // A boat is "bookable today" if any slot renders a live booking
        // affordance — the same `isBookable` predicate the card itself uses, so
        // the tier matches what the visitor sees on the card. Request-only
        // (manual_booking) tours never expose a live affordance (their slots are
        // replaced by a call/email CTA), so they are never "bookable" here — they
        // get their own top tier instead.
        const bookableToday = (tourId) =>
            !isTrue(tourById[tourId]?.manualBooking)
            && (grouped[tourId] || []).some(isBookable);

        // Tiered order (top → bottom):
        //   0. bookable today (live availability)
        //   1. request-only (manual_booking) — below bookable, above dead cards
        //   2. sold-out / not-running
        // A dead card can never sit above a request-only or bookable one, and
        // request-only sits above the dead tier. Within each tier the operator's
        // sortOrder is preserved, so a card only moves day-to-day when its
        // availability state actually changes (sells out / frees up), never
        // arbitrarily. The highlight is pinned within the bookable tier below.
        const tierRank = (tourId) =>
            bookableToday(tourId) ? 0
            : isTrue(tourById[tourId]?.manualBooking) ? 1
            : 2;
        const sortedTourIds = allTourIds.slice().sort((a, b) => {
            const ra = tierRank(a);
            const rb = tierRank(b);
            if (ra !== rb) return ra - rb;                         // tier first
            return bySortOrder(a, b);                              // within tier: sortOrder
        });

        /* 4b) Pick the "highlight" tour for this day. Among tours running at
         * least one still-bookable GROUP slot today, rank by:
         *   1. number of open group slots   (a boat running AM+PM beats one slot)
         *   2. open seats summed across those slots
         *   3. operator sortOrder           (inherited from the iteration order)
         * The winner is pulled to the first card position and badged "Featured".
         * When no tour has an open group slot (sold-out / private-only day),
         * there is no highlight and the list keeps its normal sortOrder. Private
         * seats are whole-boat, not per-seat, so only group slots feed the rank. */
        const groupOpenStats = (tourId) => {
            const tour = tourById[tourId];
            const open = (grouped[tourId] || []).filter(
                e => canBookGroup(e, tour) && Number(e.remainingCapacity ?? 0) > 0,
            );
            return {
                slots: open.length,
                seats: open.reduce((sum, e) => sum + Number(e.remainingCapacity ?? 0), 0),
            };
        };

        /* Operator-preselected boat (from the trigger button's data-tour-id).
         * When it matches a tour in today's payload it is pinned first and
         * badged "Your Selection", taking the top slot. Sold-out / not-running
         * is fine — the card still pins and shows its next-available chips, so
         * the visitor's intent is preserved. A preselect id with no match here
         * falls through to the maintenance notice below. (Match is limited to
         * Nalu-managed tours.) The featured pick is independent: when it lands
         * on the selected boat, that one card stacks both badges. */
        const preselectId    = (this.preselectTourId != null) ? String(this.preselectTourId) : null;
        const selectedTourId = preselectId !== null
            ? (sortedTourIds.find(id => String(id) === preselectId) ?? null)
            : null;

        let featuredTourId = null;
        let featuredStats = null;
        // The "Best Available" pick is computed by the portal
        // (lib/server/recommendation) and delivered per-date in
        // this.recommendations. When the portal supplied an entry for the date
        // on screen it is authoritative — resolve it back to the matching id in
        // sortedTourIds so the type/identity matches the rest of this builder
        // (and a pick not in today's payload simply yields no highlight).
        const serverRec = this.recommendations?.[this.centerDate];
        if (serverRec !== undefined) {
            const recId = serverRec.featuredTourId;
            featuredTourId = recId != null
                ? (sortedTourIds.find(id => String(id) === String(recId)) ?? null)
                : null;
        } else {
            // Legacy fallback for an older portal that doesn't send
            // recommendations (the map has no entry for this date). Mirrors the
            // server rule: within the bookable tier sortedTourIds is
            // sortOrder-ordered, so iterating it and only replacing on a
            // STRICTLY better rank lets sortOrder win every tie.
            for (const id of sortedTourIds) {
                if (isTrue(tourById[id]?.manualBooking)) continue;     // request-only is never "Best Available"
                const stats = groupOpenStats(id);
                if (stats.slots === 0) continue;                       // not highlight-eligible
                if (
                    featuredTourId === null
                    || stats.slots > featuredStats.slots
                    || (stats.slots === featuredStats.slots && stats.seats > featuredStats.seats)
                ) {
                    featuredTourId = id;
                    featuredStats = stats;
                }
            }
        }

        /* Intent ordering (deep-link ?intent=). When the portal sends an `order`
         * with at least one intent match for this date, it is authoritative:
         * render in that order and badge by reason, and — per the agreed
         * precedence — ignore the tour_id preselect (intent outranks it). With no
         * intent (or an older portal that omits `order`) this stays inert and the
         * existing selected/featured pinning below runs unchanged. */
        const serverOrder = Array.isArray(serverRec?.order) ? serverRec.order : null;
        const intentBadgeById = new Map();
        if (serverOrder) {
            for (const o of serverOrder) {
                if (o && o.reason === 'intent') intentBadgeById.set(String(o.tourId), o.badge || FEATURED_BADGE_LABEL);
            }
        }
        const intentActive = intentBadgeById.size > 0;

        /* 5) Build cards in sortOrder; tag the featured one. Track ids in
         * parallel so the featured card can be pulled to the front without
         * re-matching on HTML. Under an active intent the selected/featured
         * badges are suppressed in favor of the intent badge. */
        const cardIds = [];
        const cardsArr = [];
        for (const tourId of sortedTourIds) {
            const intentBadge = intentActive ? (intentBadgeById.get(String(tourId)) || null) : null;
            const cardHtml = renderCard(
                tourId,
                grouped[tourId] || [],
                !intentBadge && tourId === featuredTourId,
                !intentActive && selectedTourId !== null && tourId === selectedTourId,
                intentBadge,
            );
            if (!cardHtml) continue;
            cardIds.push(tourId);
            cardsArr.push(cardHtml);
        }

        /* Card ordering. Three shapes:
         *
         *   A/B — preselected boat matched (selectedTourId): pin it first
         *         ("Your Selection"), the auto-featured "Best Available" boat
         *         second ("Recommended"), then the rest in sort order — no
         *         section heading. Matched-but-sold-out still pins (case B); its
         *         card carries the next-available chips, so intent is preserved.
         *   C  — a boat was requested but isn't in today's payload at all
         *         (in maintenance / fully delisted): keep the normal featured-
         *         pinned list and prepend a notice naming the boat from the
         *         trigger's data-tour-name.
         *   —  — no preselect: unchanged featured-pin behavior.
         */
        let cards;
        if (intentActive) {
            // Intent path — reorder by the portal's `order` (precedence intent →
            // best_available → sortOrder is already baked in). Tours absent from
            // `order` (shouldn't happen — it covers the date's payload) sink to
            // the end while keeping their sortOrder via a stable sort.
            const rank = new Map(serverOrder.map((o, i) => [String(o.tourId), i]));
            const order = cardIds.map((id, i) => i).sort((a, b) => {
                const ra = rank.has(String(cardIds[a])) ? rank.get(String(cardIds[a])) : Number.MAX_SAFE_INTEGER;
                const rb = rank.has(String(cardIds[b])) ? rank.get(String(cardIds[b])) : Number.MAX_SAFE_INTEGER;
                return ra !== rb ? ra - rb : a - b;
            });
            cards = order.length ? order.map(i => cardsArr[i]).join('') : this._soldOutHtml();
        } else if (selectedTourId !== null) {
            // A/B — pin selected first, then the featured/recommended boat
            // second (unless it IS the selected boat, already on top).
            const sIdx = cardIds.indexOf(selectedTourId);
            if (sIdx > 0) {
                cardsArr.unshift(cardsArr.splice(sIdx, 1)[0]);
                cardIds.unshift(cardIds.splice(sIdx, 1)[0]);
            }
            if (featuredTourId !== null && featuredTourId !== selectedTourId) {
                const fIdx = cardIds.indexOf(featuredTourId);
                if (fIdx > 1) {
                    cardsArr.splice(1, 0, cardsArr.splice(fIdx, 1)[0]);
                    cardIds.splice(1, 0, cardIds.splice(fIdx, 1)[0]);
                }
            }
            cards = cardsArr.join('');
        } else {
            /* Pull the highlight tour to the very first card slot — ahead of
             * every other Nalu card. */
            if (featuredTourId !== null) {
                const fIdx = cardIds.indexOf(featuredTourId);
                if (fIdx > 0) {
                    cardsArr.unshift(cardsArr.splice(fIdx, 1)[0]);
                    cardIds.unshift(cardIds.splice(fIdx, 1)[0]);
                }
            }

            // Truly empty payload (no tours at all) — fall back to a generic notice.
            const list = cardsArr.length ? cardsArr.join('') : this._soldOutHtml();

            // C — a boat was requested but has no listing today. The name only
            // exists on the trigger button (a delisted boat has no card), so
            // skip the notice if it wasn't supplied rather than naming nothing.
            cards = (preselectId !== null && this.preselectTourName)
                ? this._preselectUnavailableHtml(this.preselectTourName) + list
                : list;
        }

        /* Keep a reference so Step-3 can look up by id */
        this.tours = tours;

        /* Dump HTML + init slider */
        html(results, `<div class="nalu-wrapper nalu-tours__list">${cards}</div>`);
        initSliders(results);

        // Track Step-2 View (once per modal session)
        this._tracked ||= {};
        if (!this._tracked.tours_view) {
            this._tracked.tours_view = true;
            naluTrack('nalu_tours_view', { step: 2, step_name: 'tours', ...this._trackCtx() });
        }

        /* Wire the tour-card "Next available" date chips. Each chip carries its
         * target ISO in data-jump-iso; tap hands off to goToDate, which slides
         * the carousel and re-renders this step. */
        results.querySelectorAll('.nalu-tours__card-next-dates [data-jump-iso]').forEach(el => {
            el.addEventListener('click', async e => {
                e.preventDefault();
                const iso = el.dataset.jumpIso;
                if (iso) await this.goToDate(iso);
            });
        });

        /* Hook BOOK buttons */
        results.querySelectorAll(".btn-nalu").forEach(btn => {
            btn.addEventListener('click', async e => {
                e.preventDefault();

                // Show Loader
                showLoader();

                this._cameFromStep2 = true;

                let clickedEvent;
                try {
                    clickedEvent = JSON.parse(decodeURIComponent(btn.dataset.ev));
                } catch (err) {
                    console.error("Nalu bad data-ev", err, btn.dataset.ev);
                    alert("Sorry, something went wrong with that departure slot.");
                    hideLoader();
                    return;
                }

                const clickedType = btn.dataset.booktype; // "Group" | "Private"
                this.selectedEvent = clickedEvent;

                // Age-band heads-up: if the party has children and this boat
                // enforces a higher minimum child age than another available
                // option, surface the gentler boats (same day first, else
                // nearby days) before taking any hold. Presentational only —
                // "Continue" proceeds as-is and "Switch" re-enters the same
                // proceed path for the chosen boat. No child age is known at
                // this step, so this never blocks — it only informs.
                const guidance = this._ageBandGuidance(clickedEvent, clickedType);
                if (guidance.warn) {
                    hideLoader();
                    this._showAgeBandDialog({
                        selectedEvent : clickedEvent,
                        selMinAge     : guidance.selMin,
                        selMaxAge     : guidance.selMax,
                        alternatives  : guidance.alternatives,
                        otherDays     : guidance.otherDays,
                        onContinue    : () => {
                            showLoader();
                            this.selectedEvent = clickedEvent;
                            return this._beginHoldFromStep2(clickedType);
                        },
                        onSwitch      : (ev, type) => {
                            showLoader();
                            this.selectedEvent = ev;
                            return this._beginHoldFromStep2(type);
                        },
                    });
                    return;
                }

                await this._beginHoldFromStep2(clickedType);
            });
        });

        // Per-slot "X booking now" badge. Gated on the org-wide master
        // switch — when show_viewer_count is false, no reads, no refresh
        // interval, no stragglers.
        if (this.holdConfig?.showViewerCount === true) {
            this._refreshCalendarViewerBadges().catch(() => {});
            this._startCalendarViewerRefresh();
        }

        // Keep the active month fresh (and the portal-side cache warm for
        // other visitors) while Step-2 is on screen. Idempotent — no-op if
        // already running, so this doesn't reset the cadence on re-render.
        this._startMonthRefresh();
    }

    /* ------------------------------------------------------------------ */
    /*  RENDER STEP-3 TOUR INFO & BOOKING FORM                            */
    /* ------------------------------------------------------------------ */
    async populateStep3() {
        // Prevent double-render / double-PI
        if (this._step3Mounted) {
            return;             // already built Step-3 for this entry
        }
        this._step3Mounted = true;
        this._step3SessionId = (this._step3SessionId || 0) + 1;
        naluDebug('[nalu:step3] mount', { session: this._step3SessionId });
        
        // force a fresh payment session every time Step-3 is entered
        this.intent = null;
        this._serverTotals = null;
        this.currentBookingId = null;

        this._paymentSucceeded = false;
        this._pressedPayNow = false;
        this._chargeMayHaveLanded = false;
        this._lastStripeError = null;
        this._lastPaymentIntentId = null;
        this._deletePendingOnLeave = false;

        // wallet + stripe elements should not leak across sessions
        this._wallet = null;

        try { this._unmountPaymentElementTabs?.(); } catch {}
        this._elements = null;

        const PLUGIN_URL = window.naluBookingVars.plugin_url;
        
        const ev        = this.selectedEvent; // Chosen event slot
        const tour      = (this.tours || {})[ev.tourId] || {}; // Get Tour by tourId
        const type      = this.selectedType; // Set Tour Type = "Group" | "Private"

        /* ── Tour-level fields with fallbacks */
        const boatName      = tour.boatName      || ev.boatName   || "—";
        const tourName      = tour.name          || ev.tourName   || boatName;
        const adultPrice    = +tour.adultPrice   || +ev.adultPrice   || 0;
        const childPrice    = +tour.childPrice   || +ev.childPrice   || 0;
        const privatePrice  = +tour.privatePrice || +ev.privatePrice || 0;
        const meals         = tour.meals         || ev.meals        || [];
        const maxAge        = tour.maxAge        || 0;

        /* Expose adultPrice and childPrice so other methods can read them */
        this.ticketPrices = { adultPrice, childPrice, privatePrice };
        
        /* Pax */
        const A = this.people.adults;
        const C = this.people.children;

        /* Image Gallery – media items */
        const media = (tour.media || []).slice(0, 15);

        const imageSlides = media.length <= 1
            ? (media[0]
                ? `<div class="nalu-tours__card-slider__item">
                    <img src="${media[0].url}" alt="${tour.name}" decoding="async">
                    </div>`
                : ''
                )
            : media.map((m, idx) => `
                <div class="nalu-slide">
                    <div class="nalu-tours__card-slider__item">
                    <img src="${m.url}" alt="${tour.name}" loading="${idx === 0 ? 'eager' : 'lazy'}" decoding="async">
                    </div>
                </div>
                `).join('');

        /* ---------- Currency formatter (cents → USD) ---------- */
        const $   = sel => this.root.querySelector(sel);
        const cur = cents => ((cents || 0) / 100).toLocaleString('en-US', {
            style: 'currency', currency: 'USD'
        });

        const iso = d => new Intl.DateTimeFormat(
            'en-US',
            {
                weekday : 'short',
                month   : 'short',
                day     : 'numeric',
                year    : 'numeric',
                hour    : 'numeric',
                minute  : '2-digit',
                timeZone: 'Pacific/Honolulu',
                hour12  : true
            }
        ).format(d instanceof Date ? d : new Date(d));

        /* ---------- Local Tour Info helpers ---------- */
        function tourInfoList(label,val,icon){
            const itemClass = `${label === 'Departure Location' ? 'nalu-form-tour__item flex-col' : 'nalu-form-tour__item'}`;
            return `<div class="${itemClass}">
                        <div class="nalu-form-tour__title">
                            <img src="${PLUGIN_URL}img/${icon}" width="16" height="16" alt="${label}">
                            <span>${label}</span>
                        </div>
                        <div class="nalu-form-tour__text">${val}</div>
                    </div>`;
        }

        /* ==========================================================
        * 1) EARLY: get server totals + create PaymentIntent
        *    (so we can show Fees + Total in leftHTML correctly)
        * ========================================================== */
        let grandCents = 0;
        let feesCents  = 0;

        try {
            // Create Payment Intent Call
            const createPIBody = new URLSearchParams({
                action: 'nalu_create_payment_intent',
                security: naluBookingVars.security,
                currency: 'usd',
                hold_id: this.bookingHoldId || '',

                adults: String(A || 0),
                children: String(C || 0),
                adultPriceCents: String(adultPrice || 0),
                childPriceCents: String(childPrice || 0),
                privatePriceCents: String((type === 'Group') ? 0 : (privatePrice || 0)),
            });

            const data = await postNaluAjax(createPIBody);

            if (!data?.success) {
                const msg = data?.data?.message || 'Unable to start payment.';
                naluSentryCapture(new Error('create_payment_intent returned !success: ' + msg), {
                    stage    : 'step3_create_payment_intent',
                    code     : data?.data?.code,
                    holdId   : this.bookingHoldId,
                    tourId   : this.selectedEvent?.tourId,
                    day      : this.bookingDay,
                });
                this._showUnavailableDialog({
                    tourId      : this.selectedEvent?.tourId,
                    bookingType : this.selectedType,
                    day         : this.bookingDay,
                    time        : this.selectedEvent?.isoLocalStartDate?.slice(11, 16),
                });
                return;
            }

            // Server truth for UI + Stripe amount
            grandCents = +data.data.grossCents || 0;
            feesCents  = +data.data.taxesFeesCents || 0;

            this._serverTotals = { grossCents: grandCents, taxesFeesCents: feesCents };

            // Payment Intent details
            this.intent = {
                clientSecret: data.data.clientSecret,
                id: data.data.paymentIntent,
                currency: data.data.currency || 'usd'
            };
            naluDebug('[nalu:step3] intent minted', {
                pi: this.intent.id,
                session: this._step3SessionId,
            });

            // Mock-mode signal from the portal. When true, the clientSecret
            // is a synthetic pi_MOCK… that real Stripe.js will reject — skip
            // Stripe Elements mounting and confirmPayment at submit time.
            this._stripeDisabledFromServer = !!data.data?.stripeDisabled;

        } catch (e) {
            console.error('PI init failed', e);
            naluSentryCapture(e, {
                stage  : 'step3_create_payment_intent_throw',
                holdId : this.bookingHoldId,
                tourId : this.selectedEvent?.tourId,
                day    : this.bookingDay,
            });
            this._showUnavailableDialog({
                tourId      : this.selectedEvent?.tourId,
                bookingType : this.selectedType,
                day         : this.bookingDay,
                time        : this.selectedEvent?.isoLocalStartDate?.slice(11, 16),
            });
            return;
        }

        /* ==========================================================
        * 2) Now that we have totals, render LEFT column with them
        * ========================================================== */

        const subtotalAdults = (A | 0) * (adultPrice | 0);
        const subtotalChild  = (C | 0) * (childPrice | 0);

        /* Operator-tunable highlight color for the per-passenger unit rate.
         * Surfaced from the wp-option nalu_unit_price_color; fall back to the
         * light-red default if the value is missing/empty so the pill always
         * renders. Fed to CSS as the --nalu-unit-price-bg custom property on
         * the price container (a dynamic color can't be a Tailwind class). */
        const unitPriceColor = (window.naluBookingVars && naluBookingVars.unitPriceColor) || '#fde8e8';

        /* ---------------------------------------------------------------- */
        /*  LEFT COLUMN (tour summary + totals)                              */
        /* ---------------------------------------------------------------- */
        const leftHTML = /*html*/`
            <div class="nalu-form-body__left-top">

                <!-- Image Slider -->
                <div class="nalu-tours__card-slider">${imageSlides}</div>

                <!-- Tour facts -->
                <div class="nalu-form-tour" data-maxage="${maxAge || ''}">
                    ${tourInfoList('Tour Name', tourName, 'icon-tour.svg')}
                    ${tourInfoList('Boat', boatName, 'icon-boat.svg')}
                    ${tourInfoList('When', iso(new Date(ev.isoLocalStartDate)), 'icon-calendar-alt.svg')}
                    ${tourInfoList('Check In Time', ev.localCheckInTime, 'icon-clock.svg')}
                    ${tourInfoList('Departure Location', tour.location, 'icon-location.svg')}
                </div>

                <!-- Price Breakdown -->
                <div class="nalu-form-tour__price" style="--nalu-unit-price-bg:${unitPriceColor}">
                    <!-- ── GROUP TOUR ───────────────────────────── -->
                    ${ type === 'Group' ? `
                        <div class="nalu-form-tour__price-list">
                            <span>${A} Adult${A>1?'s':''} @ <span class="nalu-unit-price">${cur(adultPrice)}</span></span>
                            <span>${cur(subtotalAdults)}</span>
                        </div>
                        ${ C ? `
                        <div class="nalu-form-tour__price-list">
                            <span>${C} Child${C>1?'ren':''} @ <span class="nalu-unit-price">${cur(childPrice)}</span></span>
                            <span>${cur(subtotalChild)}</span>
                        </div>` : '' }
                        <div class="nalu-form-tour__price-list">
                            <span>Taxes &amp; Fees</span>
                            <span>${cur(feesCents)}</span>
                        </div>
                    ` : `
                    <!-- ── PRIVATE CHARTER ─────────────────────── -->
                        <div class="nalu-form-tour__price-list">
                            <span>Private Charter</span>
                            <span class="nalu-unit-price">${cur(privatePrice)}</span>
                        </div>
                        <div class="nalu-form-tour__price-list">
                            <span>Taxes &amp; Fees</span>
                            <span>${cur(feesCents)}</span>
                        </div>
                    ` }
                    <div class="nalu-form-tour__total">
                        <span>Total</span><span data-target="total-price">${cur(grandCents)}</span>
                    </div>
                </div>

                <div class="nalu-form-terms"><p>By completing this transaction, you agree to Nalu Bookings\' <a href="https://www.nalubookings.com/customer-terms-of-service/" target="_blank" rel="noopener noreferrer">Customer Terms of Service</a> and <a href="https://www.nalubookings.com/customer-privacy-policy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.</p></div>
                <!-- Self-contained card-on-file mandate. Replaces Stripe's
                     suppressed auto-mandate (Payment Element terms.card='never').
                     Names the merchant of record and bounds what the saved card
                     can be used for, so it stands on its own until a formal
                     operator TOS exists. -->
                <div class="nalu-form-terms nalu-form-card-mandate"><p>Makana Charters &amp; Tours charges your card for this booking and keeps it on file only for add-ons you request or a late-change/no-show fee — never for anything else.</p></div>
            </div><!--.nalu-form-body__left-top-->

            <div class="nalu-form-body__left-footer">
                <button type="button" class="btn-nalu btn-nalu--large" title="Confirm Booking &amp; Pay" id="nalu-pay-btn">
                    <span class="btn-text">Confirm Booking &amp; Pay</span>
                </button>
            </div><!--.nalu-form-body__left-footer-->`;

        //$('.nalu-form-body__left').innerHTML = leftHTML;
        const leftCol = this.root.querySelector('.nalu-form-body__left');
        if (leftCol) {
            leftCol.innerHTML = leftHTML;
        }

        /* Fire up slick on the gallery that was just added */
        initSliders( // the same helper you import at the top
            this.root.querySelector('.nalu-form-body__left-top')
        );


        /* ---------------------------------------------------------------- */
        /*  3) PAY Button handler -> POST reservation                          */
        /* ---------------------------------------------------------------- */
        const payBtnEl = $('#nalu-pay-btn');
        if (payBtnEl && !payBtnEl._wired) {
            payBtnEl._wired = true;
            payBtnEl.addEventListener('click', () =>
                this.submitReservation(
                    ev,                 // event
                    type,               // "Group" / "Private"
                    this._serverTotals?.grossCents || 0,
                    //grand,              // grandTotalUSD
                    this.ticketPrices   // { adultPrice, childPrice, ticketPrices }
                )
            );
        }

        /* Back to the step 2 */
        /* Update the back arrow once (no duplicate listeners) */
        const backBtn = this.root.querySelector(
            '.nalu-back-btn[data-step="nalu-step-2"]'
        );

        if (backBtn && !backBtn._wired) {
            backBtn._wired = true;
            backBtn.addEventListener('click', async (ev) => {
                ev.preventDefault();
                showLoader();

                /* 1) kill the timer & delete the hold (ignore network errors) */
                this.stopHoldCountdown?.();

                try {
                    // If a reservation exists and last attempt declined → delete pending booking (Case 2)
                    //if (this.currentBookingId && this._deletePendingOnLeave) {
                    // If a reservation exists and payment did NOT succeed → delete the pending booking
                    if (this.currentBookingId && !this._paymentSucceeded) {
                        try { await this.deletePendingReservation(this.currentBookingId); } catch {}
                        this.currentBookingId = null;
                        this.bookingHoldId    = null; // reservation had already claimed the hold
                    }
                    // Else, if no reservation yet but a hold exists → delete hold (user never pressed Pay Now)
                    else if (!this.currentBookingId && this.bookingHoldId) {
                        try { await this.deleteReservationHold(this.bookingHoldId); } catch {}
                        this.bookingHoldId = null;
                    }

                } finally {
                    // Swap screens back to Step-2 and keep router in sync
                    this.els.step1.classList.remove("active");
                    this.els.step2.classList.add("active");
                    this.els.step3.classList.remove("active");

                    // Ensure syncWithState doesn't skip fetch thinking we "came from step 2"
                    this._cameFromStep2 = false;

                    this.router.push({
                        step       : 'tours',
                        // Back to Step 2: the carousel's own position, never the picker.
                        start_time : this.centerDate,
                        adult      : this.people.adults,
                        child      : this.people.children
                    });

                    // Freshen Step-2 window (uses loader already shown)
                    await this._cleanupToStep2KeepingSearch();

                    // Clear any Step-3 errors
                    this.clearStep3ErrorSummary?.();

                    // Hide Loader
                    hideLoader();
                    
                }
            });
        }

        /* ---------------------------------------------------------------- */
        /*  AGREEMENTS (built from event.agreements)                        */
        /* ---------------------------------------------------------------- */
        const AGR_META = {
            1 : {
                id:'PassengerWarning',
                label:'Passenger Warning*',
                text:'Please check this box recognizing that no pregnant women or anyone with physical conditions/injuries are allowed on this trip for safety reasons. This includes back injuries/back surgeries.',
            },
            2 : { 
                id:'portAcknowledgement',
                label:'Port Allen Acknowledgement*',
                text:'Please check this box to confirm you understand this tour departs from Port Allen Harbor, a different harbor than all other boats.',
            }
        };

        /* Prefer the portal's authoritative text (this.agreementsMeta, keyed by
         * organization_agreements id) so the operator can edit the wording
         * without a plugin release and the consent the portal records matches
         * exactly what's shown here. Fall back to the built-in AGR_META only
         * when an older portal sent no text. An id with neither source is
         * skipped — better to omit a warning than to render a blank, unlabelled
         * required checkbox the customer can't understand but must tick. */
        const agreementHtml = (ev.agreements || [])
            .map(key => {
                const portalMeta = (this.agreementsMeta || {})[key];
                /* Honor the portal's is_required flag: an agreement explicitly
                 * marked optional renders as an un-gated acknowledgement (no
                 * data-required, so validateStep3 ignores it and the portal
                 * won't record it as accepted). Required is the safe default —
                 * the AGR_META fallback (older portals, no flag) stays
                 * mandatory. The "*" required-marker is appended for portal
                 * titles (which don't carry one); AGR_META labels already do. */
                const required = !portalMeta || portalMeta.isRequired !== false;
                const meta = portalMeta
                    ? { id:`agreement_${key}`, label:portalMeta.title + (required ? '*' : ''), text:portalMeta.message }
                    : (AGR_META[key] || {});
                if (!meta.id || !meta.label || !meta.text) return '';
                const reqAttr = required ? ' data-required="1"' : '';
                return /*html*/`
                    <div class="nalu-form-block nalu-form-block--body">
                        <h3 class="nalu-form-block__sub-heading">${meta.label}</h3>
                        <div class="nalu-form-group nalu-form-group--full form-check nalu-form-check">
                            <input type="checkbox" class="nalu-form-check__input" id="${meta.id}"${reqAttr}>
                            <label class="nalu-form-check__label" for="${meta.id}">${meta.text}</label>
                        </div>
                    </div>`;
            }).join('');

        /* Child Safety Check — shown only when the party includes children.
         * Unlike the agreements above (tour-driven via ev.agreements), this is
         * gated on party composition: any child present requires the booker to
         * affirm the children fall within the tour's age range. data-required
         * wires it into validateStep3 automatically. The portal records the
         * affirmation (version + affirmed bounds) onto the reservation. */
        const childSafetyHtml = (C > 0) ? (() => {
            const minA = Number(tour?.minAge || 4);
            const maxA = Number(tour?.maxAge || 12);
            return /*html*/`
                <div class="nalu-form-block nalu-form-block--body">
                    <h3 class="nalu-form-block__sub-heading">Child Safety Check*</h3>
                    <div class="nalu-form-group nalu-form-group--full form-check nalu-form-check">
                        <input type="checkbox" class="nalu-form-check__input" id="childSafetyCheck" data-required="1">
                        <label class="nalu-form-check__label" for="childSafetyCheck">I confirm each child in my party is at least ${minA} years old, as required for this tour.</label>
                    </div>
                    <p class="nalu-form-check__note">Children over 12 are booked at the adult fare; if a child fare is used for someone 13 or older, we may charge the difference at check-in.</p>
                </div>`;
        })() : '';

        /* ---------------------------------------------------------------- */
        /*  RIGHT COLUMN (passenger inputs)                                 */
        /* ---------------------------------------------------------------- */
        $('.nalu-form-block--passengers').innerHTML = buildPassengerBlocks(A, C);
        $('.nalu-form-agreements').innerHTML = agreementHtml + childSafetyHtml;

        /* -------------------------------------------------- *
        * Phone field: intl-tel-input with US default
        * -------------------------------------------------- */
        const phoneInput = this.root.querySelector('[data-field="phone"]');

        /* Legacy hard allow-list, kept ONLY as the fallback when the portal
         * doesn't send a region policy (older portals). With a region policy,
         * booking is open to every country and we instead gate the SMS opt-in
         * by the selected country (see _syncSmsOptIn below). */
        const legacyAllowedCountries = [
            'us','ca','mx','be','de','it','sm','es','lu','is','pt','se','gb','at','fi','ax',
            'ie','nl','ch','dk','fr','no','kr','jp','tw','sg','hk','vi','pr','au','nz'
        ];

        /* Countries we're willing to text, from the portal (lower-case iso2).
         * null → no policy available → fall back to the legacy allow-list and
         * leave the SMS opt-in ungated, preserving historical behaviour. */
        const smsAllowed = Array.isArray(this.regionPolicy?.smsAllowedCountries)
            ? this.regionPolicy.smsAllowedCountries.map(c => String(c).toLowerCase())
            : null;
        this._smsAllowedCountries = smsAllowed;

        const itiOpts = {
            initialCountry      : 'us',
            nationalMode        : false,          // always outputs +1…
            separateDialCode    : false,          // shows “+1” outside the box
            //preferredCountries: ['us','ca','gb'],
            utilsScript         : 'https://cdn.jsdelivr.net/npm/intl-tel-input@18.1.1/build/js/utils.js',
        };
        if (!smsAllowed) {
            // No policy: don't silently open booking (and SMS) to new regions.
            itiOpts.onlyCountries = legacyAllowedCountries;
        }
        this.phoneIti = window.intlTelInput(phoneInput, itiOpts);

        /* put “+1” in the field the very first time the form is shown */
        if (!phoneInput.value.trim()) {
            phoneInput.value = '+1';
        }

        /* will flip to true when utils.js finishes */
        this.phoneUtilsReady = false;

        if (this.phoneIti.promise) {
            this.phoneIti.promise.then(() => {
                this.phoneUtilsReady = true;
            });
        }

        /* Flag changed → reset the field to the new dial code, but ONLY when the
         * visitor actually picked a country from the dropdown.
         *
         * `countrychange` is not the flag-picker event it looks like: intl-tel-
         * input also binds it to keyup (plus cut/paste) and re-detects the
         * country on every keystroke, so typing and deleting fire it too. Acting
         * on those broke editing in two ways:
         *
         *   1. Mid-dial-code, iti matches NO country and reports {} — "+8" on
         *      the way to "+81". `'+' + undefined` wrote the literal "+undefined"
         *      into the field, and every later keystroke appended to that.
         *   2. Worse on delete: iti's "default country" (`_defaultCountry`) is
         *      reassigned to whichever country was last selected, and deleting
         *      down to a bare "+" makes iti fall back to it. So clearing a
         *      +61 number re-selected AU at the last keystroke and this handler
         *      slammed "+61" back into the field — the country code could not
         *      be deleted at all.
         *
         * Neither is ours to react to: when the visitor types, the dial code they
         * type IS the answer, and iti updates the flag by itself. So arm on
         * `open:countrydropdown` and only rewrite while armed. Disarm is deferred
         * a tick because iti closes the dropdown BEFORE firing `countrychange`
         * on a pick — an immediate disarm would swallow the one event we want,
         * while the deferred one still clears an open-then-dismiss with no pick.
         *
         * Note iti has already swapped the dial code in-place by this point
         * (`_updateDialCode`), preserving the old subscriber digits; overwriting
         * with the bare dial code is deliberate — digits from the previous
         * country don't carry over to the new one. */
        let flagPickArmed = false;
        phoneInput.addEventListener('open:countrydropdown',  () => { flagPickArmed = true; });
        phoneInput.addEventListener('close:countrydropdown', () => {
            setTimeout(() => { flagPickArmed = false; }, 0);
        });

        phoneInput.addEventListener('countrychange', () => {
            if (flagPickArmed) {
                flagPickArmed = false;
                const dialCode = this.phoneIti.getSelectedCountryData()?.dialCode;
                if (dialCode) phoneInput.value = '+' + dialCode;
            }
            this._syncSmsOptIn();
        });

        /* The SMS opt-in is region-gated, and the gate re-evaluates on every
         * keystroke — including the mid-dial-code moments where iti reports no
         * country at all. Unticking the box there would silently drop an opt-in
         * the user had already given, so track their intent separately from the
         * box's current (gate-driven) state and restore it whenever the region
         * allows texting again. Only real user input updates the intent: our
         * own writes to `.checked` don't fire a `change` event. Reset per paint
         * because the passenger block — checkbox included — is re-rendered. */
        this._smsOptInWanted = false;
        const smsBox = this.root.querySelector('[data-field="text_alert"]');
        smsBox?.addEventListener('change', () => {
            this._smsOptInWanted = smsBox.checked;
        });

        /* Reflect the initial flag's SMS eligibility on first paint. */
        this._syncSmsOptIn();

        /* Live validation: blur marks a field "touched" + validates; subsequent
         * edits revalidate inline so the error clears the moment it's fixed.
         * Stripe Payment Element handles its own card-field validation. */
        this._setupStep3LiveValidation();

        /* TRACK: first real interaction with the passenger form (one-shot per
         * Step-3 session). This is what separates "landed on the form" from
         * "started filling it in" — the no-PI engagement signal. PII guard: we
         * push the FIELD NAME the user touched, never its value. */
        if (this._infoStartedSession !== this._step3SessionId) {
            const step3El = this.els.step3;
            const onFirstInput = (e) => {
                if (this._infoStartedSession === this._step3SessionId) return;
                this._infoStartedSession = this._step3SessionId;
                naluTrack('nalu_info_started', {
                    field: e.target?.dataset?.field || e.target?.name || undefined,
                    ...this._trackCtx(),
                });
            };
            step3El?.addEventListener('input', onFirstInput, { once: true });
        }

        /* ==========================================================
        * 4) Mount Stripe UI now (we already have clientSecret).
        *    In mock mode (stripeDisabled), skip the mount entirely —
        *    Stripe.js can't load Elements against a pi_MOCK…
        *    clientSecret, and validateStep3's `if (this._elements)`
        *    branch will skip Stripe field validation when null.
        * ========================================================== */
        if (this._stripeDisabledFromServer) {
            naluDebug('Stripe disabled by server (mock mode); skipping Elements mount');
        } else {
            try {
                await this._mountPaymentElementTabs({
                    clientSecret: this.intent.clientSecret,
                    amount: grandCents,
                    currency: this.intent.currency,
                    returnUrl: window.naluStripe?.returnUrl
                });
            } catch (e) {
                console.error('Stripe mount failed', e);
                naluSentryCapture(e, {
                    stage  : 'step3_stripe_mount',
                    holdId : this.bookingHoldId,
                    tourId : this.selectedEvent?.tourId,
                    day    : this.bookingDay,
                });
                this._showUnavailableDialog({
                    tourId      : this.selectedEvent?.tourId,
                    bookingType : this.selectedType,
                    day         : this.bookingDay,
                    time        : this.selectedEvent?.isoLocalStartDate?.slice(11, 16),
                });
                return;
            }
        }

        // Notify UX
        NaluBookingUx.notifyEnteredStep3();
        
        // Build Passengers - Form Block
        function buildPassengerBlocks(adults,children){
            const blk = [];
            blk.push(`<div class="nalu-form-block">`);
                // Passengers Header
                blk.push(`<div class="nalu-form-block__header">`);
                    blk.push(`<h2 class="nalu-form-block__heading">Passenger Information</h2>`);
                    blk.push(`<div class="nalu-form-block__header-arrow">`);
                        blk.push(`<img src="${PLUGIN_URL}img/icon-chevron-down.svg" class="img-fluid nolazy" width="17" height="9" alt="Reservation Countdown">`);
                    blk.push(`</div>`);
                blk.push(`</div>`);
                
                // Passengers Body
                blk.push(`<div class="nalu-form-block__body">`);

                    // Primary Adult
                    blk.push(personForm('Adult',1,true));
                    // Remaining Adults
                    for(let i=2;i<=adults;i++) blk.push(personForm('Adult',i,false));
                    // Children
                    for(let i=1;i<=children;i++) blk.push(personForm('Child',i,false,true,tour));

                    // Booking Comment
                    blk.push(`<h3 class="nalu-form-block__sub-heading">Booking Comment</h3>`);
                    blk.push(`<div class="nalu-form-group nalu-form-group--full">`);
                        blk.push(`<label class="nalu-sr" for="booking_comment">Booking Comment</label>`);
                        blk.push(`<input type="text" class="nalu-form-input" name="booking_comment" id="booking_comment" data-field="booking_comment">`);
                    blk.push(`</div>`);

                blk.push(`</div>`);  // .nalu-form-block__body block

            blk.push(`</div>`);  // nalu-form-block block
            return blk.join('');
        }

        // Person Form
        function personForm(role,idx,isPrimary=false,isChild=false,tour){
            const key = role.toLowerCase() + '_' + idx;  // "child_1", "adult_2", etc.
            const mealId = `meal_${role.toLowerCase()}_${idx}`;

            const fullNameId = `${key}_full_name`;
            const emailId    = isPrimary ? 'primary_email'         : `${key}_email`;
            const email2Id   = isPrimary ? 'primary_email_confirm' : `${key}_email_confirm`;
            const phoneId    = 'phone';                              // keep existing id for Stripe/ITI wiring
            const textAlertId= 'text_alert';
            
            const monthOpts = [
                ['','Month'],['01','Jan'],['02','Feb'],['03','Mar'],['04','Apr'],
                ['05','May'],['06','Jun'],['07','Jul'],['08','Aug'],['09','Sep'],
                ['10','Oct'],['11','Nov'],['12','Dec']
            ].map(([v,l]) => `<option value="${v}">${l}</option>`).join('');

            // Child-specific note about age limits (if applicable)
            let childNote = '';
            if( isChild ) {
                const getTour   = tour || {};
                const tourMinAge = Number(getTour.minAge || 4);
                const tourMaxAge = Number(getTour.maxAge || 12);
                childNote = `Child must be between ${tourMinAge} and ${tourMaxAge} years old.`;
            }
            
            return /*html*/`
            <h3 class="nalu-form-block__sub-heading">${role} ${idx}${isPrimary?' &amp; Primary Contact Information':''}</h3>
            ${ isChild ? `
                <p class="nalu-form-block__child-note">${childNote}</p>
                ` : '' }
            <div class="nalu-form-block__row">
                <div class="nalu-form-group">
                    <label for="${fullNameId}">Full Name*</label>
                    <input
                        class="nalu-form-input"
                        id="${fullNameId}"
                        data-field="${role.toLowerCase()}_${idx}_name"
                        name="${isPrimary ? 'name' : `${key}_name`}"
                        autocomplete="name"
                        required
                    >
                </div>
                ${ isPrimary ? `
                    <div class="nalu-form-group">
                        <label for="${emailId}">Email*</label>
                        <input
                            class="nalu-form-input"
                            data-field="email"
                            id="${emailId}"
                            type="email"
                            name="email"
                            autocomplete="email"
                            required
                        >
                    </div>
                    <!-- Confirm Email -->
                    <div class="nalu-form-group">
                        <label for="${email2Id}">Confirm Email*</label>
                        <input
                            class="nalu-form-input"
                            id="${email2Id}"
                            type="email"
                            data-field="confirm_email"
                            name="email_confirm"
                            autocomplete="email"
                            required
                        >
                    </div>
                    <!-- Phone -->
                    <div class="nalu-form-group">
                        <label for="${phoneId}">Phone*</label>
                        <input
                            class="nalu-form-input"
                            type="tel"
                            id="${phoneId}"
                            data-field="phone"
                            name="phone"
                            autocomplete="tel"
                            required
                        >
                        <div class="nalu-form-error" data-err-for="phone"></div>
                    </div>
                    <div class="nalu-form-group nalu-form-group--full form-check nalu-form-check nalu-form-check--sm">
                        <input type="checkbox" class="nalu-form-check__input" id="text_alert" data-field="text_alert">
                        <label class="nalu-form-check__label" for="text_alert">Get text messages about your booking from Makana Charters / Liko Kauai Cruises. This is our main way to reach you with important updates about your trip, such as schedule or weather changes. Messaging rates may apply. You can reply STOP any time to opt out.</label>
                    </div>` : '' }
                <div class="nalu-form-group secondlast-child">
                    <label for="${mealId}">Choose Lunch*</label>
                    <select
                    class="nalu-form-select"
                    id="${mealId}"
                    data-field="${role.toLowerCase()}_${idx}_meal"
                    data-required="meal"
                    required
                    >
                        <option value="" selected>Please select...</option>
                        ${ (meals || []).map(m => `<option value="${m}">${m}</option>`).join('') }
                    </select>
                </div>
                <div class="nalu-form-group ${ isPrimary ? '' : 'nalu-form-group--full' }">
                    <label for="${role.toLowerCase()}_${idx}_note">Custom Meal Note</label>
                    <input 
                        class="nalu-form-input"
                        id="${role.toLowerCase()}_${idx}_note"
                        data-field="${role.toLowerCase()}_${idx}_note"
                        name="${role.toLowerCase()}_${idx}_note"
                    >
                </div>

            </div>`;
        }

    }

    /**
     * Step-3 submit:
     *   A) Create reservation on server (also creates a PaymentIntent + returns clientSecret)
     *   B) Confirm card with Stripe (this is where the bank approves/declines)
     *   C) Confirm payment with server (mark PAID, send emails, etc.)
     *   D) Show confirmation
     *
     * Error policy:
     *   - If Stripe declines → keep the reservation PENDING so user can try another card.
     *     If they exit, call deletePendingReservation(bookingId).
     *   - If user never pressed Pay Now (no reservation) and exits → delete the HOLD only.
     *   - If Stripe succeeded but server confirm failed → DO NOT charge again; allow retry of confirm.
    */
    async submitReservation(ev, type, grandTotalUSD, prices) {

        // Lock this submit to the current Step-3 session
        this._submitSessionId = this._step3SessionId;

        /* ----- helpers & DOM refs --------------------------------------- */
        const { adultPrice, childPrice, privatePrice } = prices;
        const $val     = sel => this.getFieldValue(sel);
        const payBtn   = qs('#nalu-pay-btn');
        const payLabel = qs('#nalu-pay-btn .btn-text');

        /* ----- validate & guard ----------------------------------------- */
        const isValid = await this.validateStep3();
        // Do NOT disable button or show a dialog; field errors are already rendered
        if (!isValid) {
            return;
        }

        // hard guard: Step-3 must have created the PI already. Treated as
        // an expired session (the user must go pick again) — the dedicated
        // expired dialog already wires the right "View Available Tours"
        // teardown.
        if (!this.intent?.clientSecret || !this.intent?.id) {
            naluSentryCapture(new Error('Submit reached without a Stripe PaymentIntent'), {
                stage  : 'submit_no_payment_intent',
                holdId : this.bookingHoldId,
                tourId : this.selectedEvent?.tourId,
                day    : this.bookingDay,
            });
            this._showExpiredDialog();
            return;
        }

        /* Submit Button Guard */
        if (payBtn.disabled) return;
        payBtn.disabled = true;
        this._setModalBusy(true); // disable close 'X' while processing

        /* ----- flags consulted by your hardModalClose() logic ------------ */
        this._pressedPayNow         = true;          // user clicked Pay in this session
        this._paymentSucceeded      = false;         // flips true after Stripe success
        this._chargeMayHaveLanded   = false;         // true once the card is submitted to Stripe
        this._lastStripeError       = null;          // for diagnostics / UI
        this._lastPaymentIntentId   = null;          // filled after Stripe returns
        this._deletePendingOnLeave  = false;        // default before reuse check
        
        /* ------ reuse existing reservation + PI on retries --------------- */
        const sameSession       = (this._submitSessionId === this._step3SessionId);
        const alreadyHavePI     = !!this.intent?.clientSecret;
        const reuseExistingPI   = !!(sameSession && this.currentBookingId && alreadyHavePI && !this._paymentSucceeded);
        //const reuseExistingPI   = !!(this.currentBookingId && alreadyHavePI && !this._paymentSucceeded);
        
        try {
            /* =================================================================
            * 1. CREATE RESERVATION  (server also creates the PaymentIntent)
            * Skip this entire block on retries if we already have a PI.
            * ================================================================= */
            if (!reuseExistingPI) {
                // Invariant: creating a reservation requires a live hold. If Step-3
                // state desynced under us — a router teardown that fired while this
                // submit was awaiting (validation / Stripe), a restored/duplicated
                // tab, a stale session — we can reach here with no hold AND no
                // pending reservation to reuse. There is nothing legitimate to
                // confirm; POSTing on regardless is exactly how `bookingHoldId`
                // used to go out as the literal string "null" (URLSearchParams
                // stringifies a JS null). Surface the expired dialog and route the
                // visitor back to Step-2 instead. See BUG_bookingHoldId_null.md.
                if (!this.bookingHoldId) {
                    naluDebug('Nalu submitReservation: create branch reached with no bookingHoldId — aborting', {
                        currentBookingId: this.currentBookingId,
                        hasIntent: !!this.intent?.clientSecret,
                    });
                    try { this._showExpiredDialog?.(); } catch {}
                    return;
                }

                payLabel.textContent = 'Processing...';

                this._deletePendingOnLeave = false; // default: do not delete on leave

                // Find the matching tour in this.tours (array or object)
                let tourName = '';
                let boatName = '';

                const tours = this.tours || [];

                // If this.tours is an array
                if (Array.isArray(tours)) {
                    const match = tours.find(t => {
                        // adjust keys here to match your tour shape
                        return String(t.id) === String(ev.tourId) || String(t.tourId) === String(ev.tourId);
                    });

                    if (match) {
                        tourName = match.name || match.tourName || '';
                        boatName = match.assetName || match.boatName || tourName;
                    }
                }

                // If this.tours is an object keyed by tourId
                else if (tours && typeof tours === 'object') {
                    const match = tours[ev.tourId];
                    if (match) {
                        tourName = match.name || match.tourName || '';
                        boatName = match.assetName || match.boatName || tourName;
                    }
                }

                // Fallbacks from the event object if still empty
                if (!tourName) {
                    tourName = ev.tourName || ev.name || '';
                }
                if (!boatName) {
                    boatName = ev.boatName || ev.assetName || tourName;
                }
                
                /* Build the create reservation POST body */
                const createReservationBody = new URLSearchParams({
                    action              : 'nalu_create_reservation',
                    security            : naluBookingVars.security,

                    // Reservation facts
                    event_id            : ev.eventId,
                    tour_name           : tourName,
                    boat_name           : boatName,
                    manual_booking      : (this.tours?.[ev.tourId]?.manualBooking === true) ? '1' : '0',
                    day                 : this.bookingDay,
                    date_time           : this.dateFmt(ev.isoLocalStartDate),
                    book_type           : type,
                    // `|| ''` — never let URLSearchParams stringify a null/undefined
                    // hold into the literal "null"/"undefined". The guard above means
                    // this is always a real UUID by now; this is the structural backstop.
                    bookingHoldId       : this.bookingHoldId || '',

                    // Pax + pricing (server will compute authoritative totals)
                    adult               : this.people.adults,
                    child               : this.people.children,
                    total_pax           : this.people.adults + this.people.children,

                    // Send the prices so PHP can calculate the real subtotal
                    adult_price         : adultPrice,
                    child_price         : childPrice,
                    private_price       : privatePrice,
                    price_total         : String(this._serverTotals?.grossCents || grandTotalUSD || 0),
                    taxes_fees          : String(this._serverTotals?.taxesFeesCents || 0), 
                    //price_total         : grandTotalUSD,

                    /* REQUIRED: Primary contact fields */
                    primary_name        : $val('[data-field="adult_1_name"]'),
                    primary_email       : $val('[data-field="email"]'),
                    primary_phone       : this.phoneIti.getNumber(),

                    /* Optional: meals / notes for each passenger */
                    adult_1_meal        : $val('[data-field="adult_1_meal"]'),
                    adult_1_note        : $val('[data-field="adult_1_note"]'),
                    //text_alert      : $val('[data-field="text_alert"]')?.checked ?? false,
                    text_alert          : (this.root.querySelector('[data-field="text_alert"]')?.checked ? '1' : '0'),
                    booking_comment     : $val('[data-field="booking_comment"]') || 'N/A',
                    accommodations      : $val('[data-field="accommodations"]') || '',
                    referrer            : $val('[data-field="how_did_you_hear"]')|| 'None',
                    payment_intent_id   : this.intent?.id || ''   // Intent created in populateStep3
                });

                /* Add every <input/ select> that has data-field="…" */
                this.root.querySelectorAll('[data-field]').forEach(el => {
                    const key = el.dataset.field;

                    /* Phone Number: Already sent a canonical phone number above – skip the raw field */
                    if (key === 'phone' || key === 'text_alert') return;
                    
                    if (el.type === 'checkbox') {
                        createReservationBody.set(key, el.checked ? '1' : '0');
                    } else {
                        createReservationBody.set(key, (el.value || '').trim());
                    }
                    
                });

                /* -------------- POST to WP -------------- */
                const wpRes = await postNaluAjax(createReservationBody);
                
                //if (!wpRes.success) throw new Error(wpRes.data?.message || 'Seat hold failed');
                if (!wpRes.success) {
                    // extract code from structured field or raw json blob

                    let code = Number(wpRes?.data?.code ?? wpRes?.code);
                    const msg  = wpRes?.data?.message || 'Seat hold failed';
                    naluDebug('Booking Error Response:', wpRes);

                    // Non-recoverable: force exit flow
                    if ([105, 106, 107, 999].includes(code)) {
                        naluDebug('Non-recoverable booking error code:', code);

                        // Or override message dynamically:
                        this._showNonRecoverableDialog({
                            // title: 'Booking can’t be completed', // optional
                            //text: msg || 'Please contact the tour operator to complete your booking.'
                        });
                        
                        throw Object.assign(new Error('Non-recoverable booking error'), { _naluShown: true });
                    }

                    if (this._isUnavailableResponse(wpRes)) {
                        this._showUnavailableDialog({
                            message     : wpRes?.data?.message || 'These seats were just booked by someone else.',
                            tourId      : this.selectedEvent?.tourId,
                            bookingType : this.selectedType,
                            day         : this.bookingDay,
                            time        : this.selectedEvent?.isoLocalStartDate?.slice(11, 16),
                        });
                        throw Object.assign(new Error('Booking Unavailable:'), { _naluShown: true });
                    } else {
                        // Anything that isn't a recognised unavailable signal
                        // and isn't a non-recoverable code (handled above) is
                        // treated as a server-side hiccup the visitor can't
                        // fix on this slot: capture to Sentry, then bounce
                        // them to Step-2 to pick again.
                        naluSentryCapture(new Error('Reservation create failed: ' + (msg || '')), {
                            stage  : 'create_reservation',
                            code,
                            holdId : this.bookingHoldId,
                            tourId : this.selectedEvent?.tourId,
                            day    : this.bookingDay,
                        });
                        this._showUnavailableDialog({
                            tourId      : this.selectedEvent?.tourId,
                            bookingType : this.selectedType,
                            day         : this.bookingDay,
                            time        : this.selectedEvent?.isoLocalStartDate?.slice(11, 16),
                        });
                        throw Object.assign(new Error('Create reservation failed default: '), { _naluShown: true });

                    }
                }
                
                // Expecting these from PHP: reservation_id, clientSecret, customerId, (optional) payment_intent_id (Intent created successfully)
                this.currentBookingId = wpRes.data.reservation_id;
                naluDebug('[nalu:step3] reservation created', {
                    bookingId: this.currentBookingId,
                    pi       : this.intent?.id,
                    session  : this._step3SessionId,
                });

                // URGENT: HOLD is now claimed by the reservation on the server.
                // Keeping bookingHoldId can cause accidental "delete hold" later.
                this.bookingHoldId = null;

                // DO NOT overwrite this.intent.clientSecret/id here — only keep customerId if provided
                this.intent = {
                    ...this.intent,
                    customerId: wpRes.data?.customerId || this.intent?.customerId
                };

                // Server signals "Stripe not configured / mock mode" — skip
                // the Stripe.confirmPayment leg and proceed straight to the
                // server-side payment confirm. Stash on `this` so retries
                // (which take the reuseExistingPI branch) keep the flag.
                this._stripeDisabledFromServer = !!wpRes.data?.stripeDisabled;

            } else {
                // Reuse the existing clientSecret; don’t create another reservation
                payLabel.textContent = 'Authorizing payment...';
            }

            /* =================================================================
            * 2. STRIPE CARD CONFIRMATION (real bank checks happen here)
            * ================================================================= */
            // TRACK: card submitted, charge attempt begins. Pairs with
            // nalu_payment_failed / nalu_booking_confirmed to close the funnel
            // tail: started → declined(reason) | succeeded.
            naluTrack('nalu_payment_started', {
                wallet: !!this._wallet?.pmId,   // Apple/Google Pay vs card form
                ...this._trackCtx(),
            });
            if (this._stripeDisabledFromServer) {
                naluDebug('Stripe disabled by server (mock mode); skipping confirmPayment');
                this._paymentSucceeded    = true;
                this._lastPaymentIntentId = this.intent?.id;
            } else {
                payLabel.textContent = 'Authorizing payment...';

                let error, paymentIntent;

                if (this._wallet?.pmId) {
                    // Wallet path: confirm using the PaymentMethod that Apple/Google Pay
                    // already produced. confirmCardPayment is the right API for a
                    // pre-collected pmId (confirmPayment is for the Elements flow) and
                    // it handles 3DS challenges inline (Stripe overlays its own modal).
                    // Consume the pmId immediately — Stripe rejects re-using a wallet
                    // payment method on retry, so the next attempt has to re-tap
                    // Apple/Google Pay or fall through to the card form.
                    const walletPmId = this._wallet.pmId;
                    this._wallet = null;
                    ({ error, paymentIntent } = await this.stripe.confirmCardPayment(
                        this.intent.clientSecret,
                        { payment_method: walletPmId }
                    ));
                } else {
                    // Payment Element path (Card inside tabs)
                    ({ error, paymentIntent } = await this.stripe.confirmPayment({
                        elements: this._elements,
                        confirmParams: { return_url: window.naluStripe?.returnUrl },
                        redirect: 'if_required',
                    }));
                }

                // TEMPORARY fault injector (WP_DEBUG-gated via naluBookingVars.debug,
                // so it cannot fire in production). Reproduces the incident's exact
                // variant: the charge LANDED at Stripe but the client never learned
                // it — dropped response, backgrounded tab, network handoff. The real
                // charge is kept; we only discard the result and synthesize the
                // transport error Stripe.js would have surfaced. Because
                // `_paymentSucceeded` stays false, the Step-2 teardown DELETES the
                // reservation holding the captured payment, leaving an orphan
                // capture. One-shot: clears itself so the retry runs clean.
                // Activate from the console: `window.NALU_FORCE_STRIPE_LOST_RESPONSE = true`.
                if (window.naluBookingVars?.debug && window.NALU_FORCE_STRIPE_LOST_RESPONSE) {
                    window.NALU_FORCE_STRIPE_LOST_RESPONSE = false;
                    naluDebug('[nalu:inject] discarding successful confirmPayment result', {
                        pi     : paymentIntent?.id,
                        status : paymentIntent?.status,
                    });
                    paymentIntent = null;
                    error = {
                        type   : 'api_connection_error',
                        code   : 'injected_lost_response',
                        message: 'Simulated lost confirm response (NALU_FORCE_STRIPE_LOST_RESPONSE)',
                    };
                }

                // From here on the card MAY have been charged. Every failure path
                // below must assume that until the portal says otherwise — no
                // navigation, no deleting the reservation, no card form.
                this._chargeMayHaveLanded = true;

                // Only ONE shape of failure is safe to act on locally: a card
                // decline whose intent is back at requires_payment_method. Nothing
                // was captured and the same intent can take another card.
                //
                // Everything else is AMBIGUOUS and must be resolved by asking the
                // portal — transport errors, api_error/rate_limit, a terminal
                // intent, or a non-terminal status with no error at all. Stripe
                // returning an error object does NOT mean the charge failed; the
                // confirm may have reached Stripe and been processed.
                //
                // Guessing "declined" here is exactly how a visitor gets charged
                // twice: on a captured intent the retry is rejected with
                // payment_intent_unexpected_state, they are bounced out of
                // checkout, and a fresh intent takes a second charge.
                let ambiguous = false;

                if (error) {
                    this._lastStripeError = error;
                    console.error('Stripe confirmPayment error:', error);
                    // TRACK: the decline. Codes only — Stripe never hands us a PAN,
                    // and we deliberately omit error.message (can echo user input).
                    naluTrack('nalu_payment_failed', {
                        decline_code : error.decline_code,   // e.g. insufficient_funds
                        error_code   : error.code,           // e.g. card_declined
                        error_type   : error.type,
                        ...this._trackCtx(),
                    });

                    const definitiveDecline = error.type === 'card_error'
                        && error.payment_intent?.status === 'requires_payment_method';

                    if (definitiveDecline) {
                        // Nothing was captured — safe to say so and let them retry
                        // on this same intent (which is what stops the retry from
                        // minting a second one).
                        this._chargeMayHaveLanded  = false;
                        this._deletePendingOnLeave = true;
                        this._showFailedDialog(error);
                        throw Object.assign(error, { _naluShown: true });
                    }

                    ambiguous = true;
                } else if (!paymentIntent || (paymentIntent.status !== 'succeeded' && paymentIntent.status !== 'processing')) {
                    // No error, but no terminal success either — e.g. requires_action
                    // after a 3DS handoff that actually completed. Capture so we can
                    // see how often this fires.
                    naluSentryCapture(new Error('Stripe confirmPayment returned non-terminal status: ' + (paymentIntent?.status || 'null')), {
                        stage          : 'confirm_payment_indeterminate',
                        paymentIntentId: paymentIntent?.id,
                        bookingId      : this.currentBookingId,
                    });
                    naluTrack('nalu_payment_failed', {
                        error_code : 'indeterminate',
                        pi_status  : paymentIntent?.status || null,
                        ...this._trackCtx(),
                    });
                    ambiguous = true;
                }

                if (ambiguous) {
                    // Claim nothing. The portal will retrieve the intent and tell
                    // us whether money moved. Deliberately NOT arming
                    // _deletePendingOnLeave: this reservation may be the only
                    // record pointing at a captured charge.
                    this._deletePendingOnLeave = false;
                    this._lastPaymentIntentId  = paymentIntent?.id || this.intent?.id || null;
                    naluDebug('[nalu:pay] ambiguous Stripe outcome — asking portal for disposition', {
                        errorCode: error?.code || null,
                        piStatus : paymentIntent?.status || null,
                    });
                } else {
                    this._paymentSucceeded    = true;
                    this._lastPaymentIntentId = paymentIntent.id;
                }
            }
            // Either the charge succeeded or its outcome is unknown. Both mean the
            // reservation must survive a teardown: it may be the only record
            // pointing at a captured payment. Only an authoritative "nothing was
            // captured" re-arms the delete.
            this._deletePendingOnLeave = false;

            /* =================================================================
            * 3. CONFIRM WITH SERVER (mark PAID, send emails, etc.)
            * ================================================================= */
            payLabel.textContent = 'Booking seats...';

            /* ------------------------------------------------------------------ */
            /* Notify the portal ( logs → db, sends e-mail, etc. )             */
            /* ------------------------------------------------------------------ */
            const confirmBody = new URLSearchParams({
                action      : 'nalu_confirm_payment',
                security    : naluBookingVars.security,
                bookingId   : this.currentBookingId,
                clientSecret: this.intent.clientSecret,
                customerId  : this.intent.customerId,
                payment_intent_id: this._lastPaymentIntentId || ''
            });
            // TEMPORARY: forwards a test flag so AjaxController returns a forced
            // failure on this confirm AJAX (WP_DEBUG gated PHP-side). Used to
            // exercise _showPostPaymentSupportDialog without breaking the portal.
            // Activate from the console: `window.NALU_FORCE_CONFIRM_FAIL = true`.
            // Remove this block once the UX is accepted.
            // if (typeof window !== 'undefined' && window.NALU_FORCE_CONFIRM_FAIL) {
            //     confirmBody.set('nalu_force_confirm_fail', '1');
            // }
            // Confirm payment payload deliberately not logged — carries Stripe clientSecret.

            // The portal retrieves the PaymentIntent and answers with a
            // disposition — it is the authority on whether money moved, not us.
            // Safe to retry because the route is idempotent: it re-derives the
            // outcome from Stripe and no-ops if the booking is already confirmed.
            const settled = await this._confirmWithPortal(confirmBody);

            if (settled.disposition === 'unpaid') {
                // The portal asked Stripe: nothing was captured. This is the only
                // answer that may put a card form back in front of the visitor —
                // and the existing intent is still live, so the retry reuses it
                // rather than minting a second one. Stay on Step-3; never navigate.
                this._chargeMayHaveLanded  = false;
                this._paymentSucceeded     = false;
                this._deletePendingOnLeave = true;
                naluTrack('nalu_payment_failed', {
                    error_code : 'portal_unpaid',
                    pi_status  : settled.data?.paymentIntentStatus || null,
                    ...this._trackCtx(),
                });
                this._showFailedDialog({
                    message: 'That payment did not go through. Please try another card.',
                });
                throw Object.assign(new Error('Portal disposition: unpaid'), { _naluShown: true });
            }

            if (settled.disposition !== 'paid') {
                // Undetermined — either the portal could not establish the outcome
                // or we never got a determinate answer out of it. The charge may
                // well have landed, so show neither a confirmation nor a card
                // form. The reconciler settles the booking and the confirmation
                // email follows.
                naluSentryCapture(new Error('Payment disposition undetermined after confirm'), {
                    stage          : 'confirm_payment_undetermined',
                    bookingId      : this.currentBookingId,
                    paymentIntentId: this._lastPaymentIntentId,
                    tourId         : ev?.tourId,
                    day            : this.bookingDay,
                });
                this._deletePendingOnLeave = false;
                this._showPostPaymentSupportDialog();
                return;
            }

            // disposition === 'paid' — the portal confirmed against Stripe.
            this._paymentSucceeded = true;

            // NOTE: the old "confirm returned !success after Stripe charged" branch
            // lived here. It is now unreachable — a failed or indeterminate confirm
            // resolves to `undetermined` above and takes the same support-dialog
            // path, after retries, instead of being inferred from a bare !success.

            /* =================================================================
            * 4. SHOW CONFIRMATION PAGE
            * ================================================================= */
            this._paymentSucceeded = true;
            this._pressedPayNow    = false;
        
            const tour              = this.tours?.[ev.tourId] || {};     // Get Tour by tourId
            const tourLocation      = tour.location || 'Port Allen Small Boat Harbor';      // Get Tour location
            const tourLocationLink  = tour.locationLink || 'https://makanacharters.com/directions/';      // Get Tour location link
            const bookingId         = this.currentBookingId; // Reuse the one we already have

            this.showConfirmation(
                bookingId,
                $val('[data-field="adult_1_name"]'),
                tour.name || ev.tourName || ev.boatName || '—',
                grandTotalUSD,
                this.dateFmt(ev.isoLocalStartDate),
                ev.localCheckInTime,
                tourLocation,
                tourLocationLink,
            );

        } catch (err) {
            
            console.error('Nalu Submit Reservation', err);

            // CLEANUP RULES:
            //  - If a reservation was never created AND a hold exists → delete the hold.
            //  - If a reservation exists → do NOT delete the hold (it’s already claimed);
            //    keep the reservation pending so the user can retry payment or exit (then we delete pending).
            if (!this.currentBookingId && this.bookingHoldId) {
                try { await this.deleteReservationHold(this.bookingHoldId); } catch {}
                this.bookingHoldId = null;
            }

            // Last-resort fallback. The branches inside the try already show
            // the right dialog (and tag it with `_naluShown:true`); this only
            // fires when something threw before reaching any of them.
            if (!err?._naluShown) {
                naluSentryCapture(err, {
                    stage     : 'submit_reservation_catchall',
                    bookingId : this.currentBookingId,
                    holdId    : this.bookingHoldId,
                    tourId    : this.selectedEvent?.tourId,
                    day       : this.bookingDay,
                    chargeMayHaveLanded: !!this._chargeMayHaveLanded,
                });

                if (this._chargeMayHaveLanded) {
                    // Once the card has been submitted to Stripe, an unexpected
                    // failure must NOT route through the unavailable dialog: that
                    // dialog exists to send the visitor back to Step-2, and
                    // re-entering Step-3 mints a fresh PaymentIntent — which is
                    // how a visitor whose charge already landed ends up paying
                    // twice. Keep them here and hand off to support.
                    this._deletePendingOnLeave = false;
                    this._showPostPaymentSupportDialog();
                } else {
                    this._showUnavailableDialog({
                        tourId      : this.selectedEvent?.tourId,
                        bookingType : this.selectedType,
                        day         : this.bookingDay,
                        time        : this.selectedEvent?.isoLocalStartDate?.slice(11, 16),
                    });
                }
            }

        } finally {
            payLabel.textContent = 'Confirm Booking & Pay';
            payBtn.disabled = false;
            this._setModalBusy(false); // re-enable close 'X'
        }
    }


    /**
     * Ask the portal to settle this booking and report what actually happened.
     *
     * The portal retrieves the PaymentIntent from Stripe and answers with a
     * disposition — `paid`, `unpaid`, or `undetermined`. It is the authority on
     * whether money moved; the browser is not, because the browser can lose the
     * confirm response while the charge lands.
     *
     * Retries are safe and deliberate: the route re-derives the outcome from
     * Stripe on every call and no-ops when the booking is already confirmed, so a
     * replay cannot double-charge, double-record, or re-send the confirmation
     * email. We retry ONLY when we failed to get a determinate answer (transport
     * failure / non-2xx) — never the Stripe charge itself.
     *
     * Falls back to `paid` when a 2xx carries no disposition field, so a plugin
     * running ahead of the portal deploy keeps its old meaning: a 200 from the
     * confirm route was the confirmation.
     *
     * @returns {Promise<{ok:boolean, disposition:'paid'|'unpaid'|'undetermined', data:object|null}>}
     */
    async _confirmWithPortal(confirmBody, { attempts = 3, backoffMs = 400 } = {}) {
        let lastData = null;

        for (let attempt = 1; attempt <= attempts; attempt++) {
            try {
                // TEMPORARY fault injector (WP_DEBUG-gated via naluBookingVars.debug,
                // so it cannot fire in production). Simulates TRANSPORT-level
                // failures on the confirm POST — the charge has already landed by
                // this point, so it reproduces "money taken, portal never told"
                // without involving Stripe.
                //   `window.NALU_FORCE_CONFIRM_THROW = true` → fail every attempt
                //        (retries exhaust → `undetermined` → support dialog)
                //   `window.NALU_FORCE_CONFIRM_THROW = 2`    → fail twice, then
                //        succeed (proves the retry recovers and reaches Step-4)
                if (window.naluBookingVars?.debug && window.NALU_FORCE_CONFIRM_THROW) {
                    if (typeof window.NALU_FORCE_CONFIRM_THROW === 'number') {
                        window.NALU_FORCE_CONFIRM_THROW -= 1;
                    }
                    naluDebug('[nalu:inject] throwing on confirm POST', { attempt });
                    throw new Error('Simulated transport failure on confirm POST (NALU_FORCE_CONFIRM_THROW)');
                }

                const res = await postNaluAjax(confirmBody);
                if (res?.success) {
                    const disposition = res?.data?.disposition || 'paid';
                    naluDebug('[nalu:pay] portal disposition', { disposition, attempt });
                    return { ok: true, disposition, data: res.data ?? null };
                }
                lastData = res?.data ?? null;
                naluDebug('[nalu:pay] confirm returned !success', { attempt });
            } catch (e) {
                // Transport-level failure — we learned nothing about the payment.
                naluDebug('[nalu:pay] confirm threw', { attempt, message: e?.message });
            }

            if (attempt < attempts) {
                await new Promise((resolve) => setTimeout(resolve, backoffMs * attempt));
            }
        }

        return { ok: false, disposition: 'undetermined', data: lastData };
    }

    /**
     * Mount Stripe Payment Element (tabs) into an existing placeholder.
     * - host: where to mount the Payment Element (default "#card-element")
     * - prbHost: optional container for the Payment Request Button (Apple/Google Pay)
     */
    async _mountPaymentElementTabs({
        clientSecret,
        amount,
        currency,
        returnUrl,
        host = '#card-element',
        prbHost = '#wallet-button'
    }) {
        // 1) Ensure Stripe is loaded (uses publishable key + connected account)
        if (!this.stripe) {
            this.stripe = await loadStripe(naluStripe.pk, { stripeAccount: naluStripe.acct });
        }

        // 2) Create a fresh Elements instance with the client secret
        this._elements?.destroy?.();
        this._elements = this.stripe.elements({
            clientSecret,
            locale: 'en-GB',
            appearance: {
                theme: 'stripe',
                variables: { borderRadius: '5px', colorPrimary: '#18181B', spacingUnit: '4px' }
            }
        });

        // 3) Create the Payment Element (tabs layout)
        this._paymentElement = this._elements.create('payment', {
            layout: {
                type: 'tabs',
                defaultCollapsed: false
            },
            // Suppress Stripe's auto-generated card-on-file mandate ("…charge
            // your card for future payments…"). Because the intent uses
            // setup_future_usage, we are responsible for collecting the mandate
            // ourselves — we do so with our own clearer, bounded line rendered
            // next to the pay button (.nalu-form-card-mandate) and record the
            // accepted terms versions server-side on the reservation.
            terms: { card: 'never' },
            wallets: {
                applePay : 'auto',
                googlePay: 'auto',
                link     : 'auto'
            },
            defaultValues: {
                billingDetails: {
                    name : this.getFieldValue('[data-field="adult_1_name"]') || '',
                    email: this.getFieldValue('[data-field="email"]') || '',
                }
            }
        });

        // 4) Handle real-time validation errors from the Payment Element.
        this._paymentComplete = false;
        this._paymentElement.on('change', ({ complete, error }) => {
            this._paymentComplete = !!complete;
            this._setPaymentError(error?.message || '');
        });

        // 5) Mount the Payment Element into the host container
        const hostEl = this.root.querySelector(host);
        if (!hostEl) {
            console.warn(`Nalu Payment host "${host}" not found.`);
            return;
        }
        this._paymentElement.mount(hostEl);

        // 6) Setup the Payment Request Button (Apple/Google Pay) if available
        const prbEl = prbHost ? this.root.querySelector(prbHost) : null;
        if (prbEl) {
            const pr = this.stripe.paymentRequest({
                country: 'US',         // adjust if needed
                currency,
                total: { label: 'Makana Charters', amount },
                requestPayerName: true,
                requestPayerEmail: true,
                requestPayerPhone: true,
            });

            const can = await pr.canMakePayment();
            if (can) {
                const prb = this._elements.create('paymentRequestButton', {
                    paymentRequest: pr,
                    style: { paymentRequestButton: { type: 'default', height: '48px' } }
                });
                prb.mount(prbEl);

                // Keep the PR reference so totals can be patched later via pr.update()
                this._paymentRequest = pr;

                pr.on('paymentmethod', async (ev) => {
                    // The wallet supplies the card; skip Payment Element validation
                    // for this check. submitReservation will auto-skip it too because
                    // this._wallet?.pmId is set below.
                    const valid = await this.validateStep3({ skipPaymentElementCheck: true });
                    if (!valid) {
                        ev.complete('fail');
                        return;
                    }

                    this._wallet = { pmId: ev.paymentMethod.id };

                    // Dismiss the wallet sheet immediately. Any later failure
                    // (decline, server confirm error) is surfaced through the
                    // same dialogs as the card flow.
                    ev.complete('success');

                    const payBtn = this.root.querySelector('#nalu-pay-btn');
                    if (payBtn && !payBtn.disabled) {
                        payBtn.click();
                    }
                });
            } else {
                prbEl.style.display = 'none';
            }
        }
    }

    // Show or clear error message below the Payment Element
    _setPaymentError(msg) {
        const host = this.root.querySelector('#card-element'); // same as `host` default above
        if (!host) return;
        // remove previous error
        host.parentElement?.querySelectorAll('.nalu-form-error').forEach(n => n.remove());
        if (msg) {
            const div = document.createElement('div');
            div.className = 'nalu-form-error';
            div.textContent = msg;
            host.parentElement.appendChild(div);
        }
    }

    // Show an inline error message in a specific selector
    _showInlineError(sel, msg){
        const el = this.root.querySelector(sel);
        if (!el) return;
        el.textContent = msg || 'Something went wrong.';
        el.classList.remove('hidden');
    }

    /** Delete a reservation-hold on the portal */
    async deleteReservationHold(holdId){
        if(!holdId) return;
        const paymentIntent = this.intent?.id || this._lastPaymentIntentId || '';

        const res = await postNaluAjax(new URLSearchParams({
            action          : 'nalu_delete_hold',
            security        : naluBookingVars.security,
            hold_id         : holdId,
            payment_intent  : paymentIntent
        }));

        if(!res.success){
            console.warn('Nalu hold deletion failed', res);
        } else {
            naluDebug('Nalu hold deleted', holdId);
        }

        // Always clear local state after attempting deletion
        this.bookingHoldId = null;
    }

    /**
    * Delete a PENDING reservation after Pay Now was pressed but payment failed/abandoned.
    * - Requires your WP AJAX route: nalu_delete_pending_reservation (see PHP below)
    * - Sends bookingId and customerId (Stripe Customer on your side) to the React server via WP.
    * - Always clears local state so a fresh attempt won’t “stick”.
    */
    async deletePendingReservation(bookingId, customerIdOverride) {
        if (!bookingId) return { success:false, data:{ message:'No bookingId' } };

        // Prefer override (from localStorage), else instance state
        const customerId = customerIdOverride || this.intent?.customerId || this.customerId || '';
        if (!customerId) {
            console.warn('Nalu delete pending reservation: missing customerId; proceeding anyway');
        }

        // Prefer the last known PI if available
        const paymentIntent = this.intent?.id || this._lastPaymentIntentId || '';

        let json;
        try {
            const body = new URLSearchParams({
                action    : 'nalu_delete_reservation',
                security  : naluBookingVars.security,
                bookingId : String(bookingId),
                customerId,
                payment_intent : paymentIntent
            });

            json = await postNaluAjax(body);
        } catch (e) {
            console.warn('Nalu delete pending reservation network error', e);
            json = { success: false, data: { message: 'Network error' } };
        }

        if (!json?.success) {
            console.warn('Nalu delete pending reservation failed', json);
        } else {
            naluDebug('Nalu pending reservation deleted:', bookingId);
        }

        this.currentBookingId = null;
        this.bookingHoldId    = null;
        return json;
    }

    /* ------------------------------------------------------------------ */
    /*  HELPER FUNCTIONS TO VALIDATE THEN POST                            */
    /* ------------------------------------------------------------------ */
    /** Read and trim a field value */
    getFieldValue(sel) {
        return (this.root.querySelector(sel)?.value || '').trim();
    }

    /* ------------------------------------------------------------------ */
    /*  INLINE ERROR HELPERS                                              */
    /* ------------------------------------------------------------------ */
    clearErrors () {
        // Clear the top Step-3 banner
        this.clearStep3ErrorSummary?.();
        
        this.root.querySelectorAll('.nalu-form-error')
            .forEach(el => el.remove());
        this.root.querySelectorAll('.nalu-form-invalid')
            .forEach(el => el.classList.remove('nalu-form-invalid'));
    }

    /* ─────────── SHOW / CLEAR one field error ─────────── */
    showError (target, msg = '') {

        // Accept either a selector string or an actual element
        const el = (typeof target === 'string')
            ? this.root.querySelector(target)
            : target;

        if (!el) return; // nothing to decorate

        /* <div class="nalu-form-error"> … </div> immediately after the field */
        let box = el.nextElementSibling?.classList.contains('nalu-form-error')
                ? el.nextElementSibling
                : null;

        if (msg) { // ► ADD / UPDATE error
            if (!box) {
                box            = document.createElement('div');
                box.className  = 'nalu-form-error';
                el.after(box);
            }
            box.textContent = msg;
            el.classList.add('nalu-form-invalid');

        } else { // ► CLEAR error
            box?.remove();
            el.classList.remove('nalu-form-invalid');
        }
    }

    // Validate Step-3 form fields and return an array of error messages.
    /** * Validate the fields in Step-3 of the booking form.
     * @returns {Array} - An array of error messages for invalid fields.
     */
    /* ─────────── VALIDATE all Step-3 inputs ─────────── */
    async validateStep3 (options = {}) {
        const { skipPaymentElementCheck = false } = options;

        // Submitting implies the user has "seen" every field. Marking them
        // touched here means any post-submit edit triggers live re-validation
        // via the input/change listeners installed in _setupStep3LiveValidation.
        this._markAllStep3FieldsTouched();

        const errs      = []; // {el,msg} objects
        const q         = s => this.root.querySelector(s);
        const emailRx   = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const usPhoneRx = /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;

        /* — Primary contact — */
        const name1 = q('[data-field="adult_1_name"]');
        if (!name1.value.trim())          errs.push({ el:name1, msg:'Full name required' });

        const email = q('[data-field="email"]');
        if (!emailRx.test(email.value))   errs.push({ el:email, msg:'Valid email address required' });

        const email2 = q('[data-field="confirm_email"]');
        if (email.value !== email2.value) errs.push({ el:email2, msg:'Email addresses do not match' });

        /* PHONE – rely on intl-tel-input for validation ---------------------- */
        const phoneEl    = q('[data-field="phone"]');

        /* strip all spaces so “+1 213 373 4253” → “+12133734253”               */
        const rawPhone   = phoneEl.value.replace(/\s+/g,'').trim();
        const phoneEmpty = (rawPhone === '');
        let   phoneBad   = false;

        if (!phoneEmpty) {
            if (this.phoneUtilsReady) {           // utils.js has finished loading
                /* intl-tel-input already knows the current field value,               */
                /* just ask it if that number is valid for the selected country flag   */
                phoneBad = !this.phoneIti.isValidNumber();
            } else {
                /* utils.js is still downloading – fall back to a simple “+########”   */
                phoneBad = !/^\+\d{10,15}$/.test(rawPhone);
            }
        } else {
        }

        if (phoneEmpty)      errs.push({ el: phoneEl, msg: 'Phone number is required' });
        else if (phoneBad)   errs.push({ el: phoneEl, msg: 'Enter a valid phone number' });

        /* — Every other passenger name (adults & children) — */
        for (let i = 2; i <= this.people.adults; i++) {
            const fld = q(`[data-field="adult_${i}_name"]`);
            if (fld && !fld.value.trim())
                errs.push({ el:fld, msg:'Full name required' });
        }
        for (let i = 1; i <= this.people.children; i++) {
            const fld = q(`[data-field="child_${i}_name"]`);
            if (fld && !fld.value.trim())
                errs.push({ el:fld, msg:'Full name required' });
        }

        /* --- Child DOB and minAge/maxAge check --- */
        const tour   = this.tours?.[this.selectedEvent?.tourId] || {};
        const tourMinAge = Number(tour.minAge || 0);
        const tourMaxAge = Number(tour.maxAge || 0);

        // — Meal selection required for each passenger (when choices exist) —
        this.root.querySelectorAll('select[data-field$="_meal"]').forEach(sel => {
            // If there are no real choices (only the placeholder), don’t force it
            const hasChoices = sel.querySelectorAll('option[value]:not([value=""])').length > 0;
            if (hasChoices && !sel.value) {
                errs.push({ el: sel, msg: 'Please choose a meal option' });
            }
        });

        /* — Agreements that apply to THIS event — */
        /* — Agreements & other required check-boxes — ------------------ */
        this.root
            .querySelectorAll('input[type="checkbox"][data-required="1"]')
            .forEach(cb => {
                if (!cb.checked)
                errs.push({ el: cb, msg: `Please accept by checking this box to proceed.` });
            });

        // --- Stripe Payment Element validation (authoritative) ---
        // Mock mode: server told us Stripe is disabled, so we never mounted
        // Elements. Skip card validation entirely — there's no card to check.
        // Wallet path: an Apple/Google Pay payment method is already in hand
        // (this._wallet.pmId), so the Payment Element is irrelevant.
        if (this._stripeDisabledFromServer || skipPaymentElementCheck || this._wallet?.pmId) {
            this._setPaymentError('');
        } else if (this._elements) {
            // Ask Stripe to run its own field validation (no network; client-side only)
            const { error } = await this._elements.submit();
            if (error) {
                // show inline + collect into errs
                this._setPaymentError(error.message || 'Please complete your card details.');
                const mountEl = this.root.querySelector('#nalu-payment-element') || this.root;
                errs.push({ el: mountEl, msg: error.message || 'Please complete your card details.' });
            } else {
                // clear any prior payment error
                this._setPaymentError('');
            }
        } else {
            // Legacy Card Element fallback (keep your existing legacy branch if you still support it)
            if (!this._paymentElement && !this.cardComplete) {
                const mountEl = this.root.querySelector('#card-element');
                errs.push({ el: mountEl, msg: 'Please enter valid card details' });
            }
        }

        /* — Clear previous round, then show new errors — --------------- */
        this.root.querySelectorAll('.nalu-form-error').forEach(n => n.remove());
        this.root.querySelectorAll('.nalu-form-invalid')
        .forEach(n => n.classList.remove('nalu-form-invalid'));

        errs.forEach(o => this.showError(o.el, o.msg));

        // Show top banner + scroll
        this.renderStep3ErrorSummary(errs);

        return errs.length === 0;     // true ⇒ form is OK

    }

    /* ─────────── LIVE VALIDATION (Step 3) ─────────────────────────────
     * Single-field rule lookup. validateStep3 runs the same checks in a
     * full pass at submit time; this helper is what the per-field blur /
     * input listeners call so the user sees errors clear in real time as
     * they fix them.
     *
     * Returns { el, msg } on failure, null on pass. Called by both the
     * live listeners and (eventually) validateStep3 so rules don't drift.
     * ────────────────────────────────────────────────────────────────── */
    _validateStep3Field(el) {
        if (!el) return null;
        const field = el.dataset?.field || '';
        const tag   = el.tagName;

        // Required checkbox (e.g. agreement checkboxes injected by Step-3 build)
        if (tag === 'INPUT' && el.type === 'checkbox' && el.dataset?.required === '1') {
            return el.checked ? null : { el, msg: 'Please accept by checking this box to proceed.' };
        }

        // Meal select — required only when the tour ships real choices
        if (tag === 'SELECT' && /_meal$/.test(field)) {
            const hasChoices = el.querySelectorAll('option[value]:not([value=""])').length > 0;
            if (hasChoices && !el.value) return { el, msg: 'Please choose a meal option' };
            return null;
        }

        // Email format
        if (field === 'email') {
            const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRx.test((el.value || '').trim())) return { el, msg: 'Valid email address required' };
            return null;
        }

        // Confirm-email match (cross-field). Empty confirm field is also an error
        // — user shouldn't be able to leave it blank when email is filled.
        if (field === 'confirm_email') {
            const email = this.root.querySelector('[data-field="email"]')?.value || '';
            if ((el.value || '').trim() !== email.trim()) return { el, msg: 'Email addresses do not match' };
            return null;
        }

        // Phone (intl-tel-input authoritative once utils.js is ready,
        // permissive +N{10..15} fallback while the script is loading)
        if (field === 'phone') {
            const raw = (el.value || '').replace(/\s+/g,'').trim();
            if (raw === '' || raw === '+1') return { el, msg: 'Phone number is required' };
            const bad = this.phoneUtilsReady
                ? !this.phoneIti?.isValidNumber()
                : !/^\+\d{10,15}$/.test(raw);
            if (bad) return { el, msg: 'Enter a valid phone number' };
            return null;
        }

        // Adult / child name fields (adult_1_name, adult_2_name, child_1_name, …)
        if (/^(adult|child)_\d+_name$/.test(field)) {
            if (!(el.value || '').trim()) return { el, msg: 'Full name required' };
            return null;
        }

        return null;
    }

    /* Validate a single Step-3 field and surface the error inline. Reused
     * by the blur / input / change listeners installed on Step 3 entry. */
    validateField(el) {
        if (!el) return;
        const result = this._validateStep3Field(el);
        this.showError(el, result?.msg || '');

        // If post-submit banner is up and everything's now clean, drop it.
        if (!this.root.querySelector('.nalu-form-error')) {
            this.clearStep3ErrorSummary?.();
        }
    }

    /* Element qualifies for live validation if we have a rule for it.
     * Currently: anything with a known data-field, or a required checkbox. */
    _isLiveValidatableField(el) {
        if (!el || !el.dataset) return false;
        const field = el.dataset.field || '';
        const isReqCheckbox = el.tagName === 'INPUT' && el.type === 'checkbox' && el.dataset.required === '1';
        if (isReqCheckbox) return true;
        if (!field) return false;
        return field === 'email'
            || field === 'confirm_email'
            || field === 'phone'
            || /^(adult|child)_\d+_name$/.test(field)
            || /_meal$/.test(field);
    }

    /* Stable key for the touched set. Prefer data-field; fall back to id. */
    _step3FieldKey(el) {
        return el.dataset?.field ? `field:${el.dataset.field}` : (el.id ? `id:${el.id}` : null);
    }

    /* Wire blur-then-live validation onto the Step-3 right column. Idempotent
     * within a Step-3 visit — the touched set resets each entry so the user
     * doesn't carry stale "you already left this field empty" state across
     * Back/Forward navigation. */
    /* Gate the SMS opt-in by the phone field's selected country. Numbers in
     * regions we won't text (high SMS toll-fraud) can still book — we just
     * capture the number, hide the opt-in, and surface a note that our team
     * will call. The server is authoritative (forces text_opt_in=false and
     * blocks the send regardless), this is the matching UX. No-op when the
     * portal sent no region policy (legacy allow-list path). */
    _syncSmsOptIn() {
        const smsAllowed = this._smsAllowedCountries;
        if (!smsAllowed) return; // legacy: opt-in always available

        const box  = this.root.querySelector('[data-field="text_alert"]');
        if (!box) return;
        const wrap = box.closest('.nalu-form-check') || box.parentElement;

        const iso  = (this.phoneIti?.getSelectedCountryData?.()?.iso2 || '').toLowerCase();
        const ok   = smsAllowed.includes(iso);

        let note = this.root.querySelector('[data-field="sms-region-note"]');
        if (ok) {
            box.disabled = false;
            box.checked  = !!this._smsOptInWanted;  // restore the user's choice
            if (wrap) wrap.style.display = '';
            if (note) note.style.display = 'none';
        } else {
            box.checked  = false;   // never submit an opt-in for these regions
            box.disabled = true;
            if (wrap) wrap.style.display = 'none';
            if (!note && wrap?.parentNode) {
                note = document.createElement('div');
                note.setAttribute('data-field', 'sms-region-note');
                note.className = 'nalu-form-group nalu-form-group--full nalu-form-note';
                note.textContent = 'Text updates aren’t available for your region — our team will call or email you with any booking details.';
                wrap.parentNode.insertBefore(note, wrap.nextSibling);
            }
            if (note) note.style.display = '';
        }
    }

    _setupStep3LiveValidation() {
        const scope = this.root.querySelector('.nalu-form-body__right');
        if (!scope) return;

        this._step3Touched = new Set();

        if (this._step3LiveBound) return;
        this._step3LiveBound = true;

        // blur (delegated via focusout, since blur doesn't bubble): mark
        // touched + validate. Cross-field hop for email → confirm_email.
        scope.addEventListener('focusout', (e) => {
            const el = e.target;
            if (!this._isLiveValidatableField(el)) return;
            const key = this._step3FieldKey(el);
            if (key) this._step3Touched.add(key);
            this.validateField(el);

            if (el.dataset?.field === 'email') {
                const confirm = scope.querySelector('[data-field="confirm_email"]');
                const ckey = confirm ? this._step3FieldKey(confirm) : null;
                if (confirm && ckey && this._step3Touched.has(ckey)) {
                    this.validateField(confirm);
                }
            }
        });

        // Live re-validate while typing — only for fields the user has
        // already touched at least once. Avoids yelling at empty inputs.
        scope.addEventListener('input', (e) => {
            const el = e.target;
            if (!this._isLiveValidatableField(el)) return;
            const key = this._step3FieldKey(el);
            if (!key || !this._step3Touched.has(key)) return;
            this.validateField(el);

            if (el.dataset?.field === 'email') {
                const confirm = scope.querySelector('[data-field="confirm_email"]');
                const ckey = confirm ? this._step3FieldKey(confirm) : null;
                if (confirm && ckey && this._step3Touched.has(ckey)) {
                    this.validateField(confirm);
                }
            }
        });

        // Selects + checkboxes are "touched on first change" — there's no
        // separate blur model that makes sense for a click-to-toggle.
        scope.addEventListener('change', (e) => {
            const el = e.target;
            if (!this._isLiveValidatableField(el)) return;
            const key = this._step3FieldKey(el);
            if (key) this._step3Touched.add(key);
            this.validateField(el);
        });

        // Agreement checkboxes (PassengerWarning, portAcknowledgement, etc.)
        // are visually replaced by the label::before pseudo, with the actual
        // <input> hidden via `visibility: hidden`. That removes them from
        // the keyboard tab order entirely — focusout on the checkbox itself
        // never fires, so the blur+input listeners above never see them.
        //
        // Treat focus or click on any field that DOM-follows a required
        // checkbox as the touch signal: the user has tabbed or clicked past
        // the agreements, so any still-unchecked one should now be flagged.
        // We bind on the modal root (this.root) rather than just the right
        // column for two reasons: (a) Stripe's payment-element iframe can
        // swallow focusin propagation in some browsers, (b) clicking the
        // Confirm Pay button in the LEFT column should also flag missed
        // agreements before triggering the heavier submit-time validate.
        const flagEarlierRequiredCheckboxes = (target) => {
            if (!target) return;
            const required = scope.querySelectorAll('input[type="checkbox"][data-required="1"]');
            if (!required.length) return;
            required.forEach(cb => {
                if (cb === target) return;
                const pos = target.compareDocumentPosition(cb);
                // cb precedes target in DOM order — i.e. user has moved past it
                if ((pos & Node.DOCUMENT_POSITION_PRECEDING) === 0) return;
                const key = this._step3FieldKey(cb);
                if (!key) return;
                this._step3Touched.add(key);
                this.validateField(cb);
            });
        };

        this.root.addEventListener('focusin', (e) => flagEarlierRequiredCheckboxes(e.target));
        this.root.addEventListener('click',   (e) => flagEarlierRequiredCheckboxes(e.target));
    }

    /* Mark every live-validatable field touched. Called at the top of
     * validateStep3 so that, after a failed submit, any further edit the
     * user makes triggers live re-validation immediately. */
    _markAllStep3FieldsTouched() {
        if (!this._step3Touched) this._step3Touched = new Set();
        this.root.querySelectorAll('[data-field], input[type="checkbox"][data-required="1"]')
            .forEach(el => {
                if (!this._isLiveValidatableField(el)) return;
                const key = this._step3FieldKey(el);
                if (key) this._step3Touched.add(key);
            });
    }

    // Creates/updates the Step-3 top banner (call when validation fails)
    renderStep3ErrorSummary(errs) {
        const rightCol = this.root.querySelector('.nalu-form-body__right');
        if (!rightCol) return;

        rightCol.querySelector('.nalu-form-validation-errors')?.remove();
        if (!errs || errs.length === 0) return;

        const box = document.createElement('div');
        box.className = 'nalu-form-validation-errors';
        box.setAttribute('role', 'alert');
        box.setAttribute('aria-live', 'polite');

        box.innerHTML = `
            <p><span class="icon-circle-error"></span> Some required fields are missing or invalid. Please check the fields below.</p>
        `;

        rightCol.prepend(box);

        const isMobile = window.matchMedia('(max-width: 767px)').matches;
        const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        const container = document.getElementById('naluFormBodyStep3');   // the modal step scroller
        const sidebar = document.getElementById('naluStickySidebar');     // stacked above on mobile

        if (container) {
            // Prevent "jump back" when inserting banner above current scroll position
            container.style.overflowAnchor = 'none';
            box.style.overflowAnchor = 'none';

            const behavior = prefersReduced ? 'auto' : 'smooth';

            if (isMobile) {
                // Scroll so the RIGHT column starts at the top of the visible area
                // (more robust than "sidebar height" because it includes gaps/margins)
                const containerTop = container.getBoundingClientRect().top;
                const rightTop = rightCol.getBoundingClientRect().top;

                // current scrollTop + delta between rightCol top and container top
                const targetTop = container.scrollTop + (rightTop - containerTop);

                requestAnimationFrame(() => {
                    container.scrollTo({ top: targetTop, behavior });
                });

                // On mobile do NOT focus or scrollIntoView first error,
                // it causes the "scroll up then back down" effect.
                return;
            }

            // Desktop: scroll to top so banner is visible
            requestAnimationFrame(() => {
                container.scrollTo({ top: 0, behavior });
            });
        }

        // Desktop only extras (optional)
        box.tabIndex = -1;
        box.focus({ preventScroll: true });

    }


    // Clears/removes the Step-3 top banner (call when leaving/closing/resetting)
    clearStep3ErrorSummary() {
        const box = this.root.querySelector('.nalu-form-validation-errors');
        if (box) box.remove(); // fully remove so it never lingers
    }

    /* Format Time 24 to 12 - 08:00:00  ➜  8:00 AM  */
    formatTime24(hms){
        if(!hms) return '';
        const [h, m] = hms.split(':').map(Number);
        const d = new Date();
        d.setHours(h, m, 0, 0);
        return d.toLocaleTimeString('en-US',{hour:'numeric',minute:'2-digit'});
    }

    /**
     * Show the confirmation screen with booking details.
     * @param {string} id - The booking ID.
     * @param {number} total - The total price of the booking.
     * @param {string} name - The name of the primary contact.
     * @param {string} tour - The name of the booked tour.
     * @param {string} whenTourDateTime - The formatted date and time of the tour.
     * @param {string} whenTourCheckInTime - The formatted check-in time of the tour.
     * @param {string} departureLocation - The departure location of the tour.
    */
    showConfirmation(id, name, tour, totalPrice, whenTourDateTime, tourCheckInTime, departureLocation, departureLocationLink) {

        /* ── A. stop any pending 5-minute countdown ───────────────── */
        /* ── Kill the countdown & hold timer ─────── */
        clearTimeout(holdTimerId);      // stop the 5-min “still there?” timer
        holdTimerId = null;             // (optional) mark it as cleared
        clearInterval(this._countdownId);   // stop the on-screen countdown

        /* ?ark the flow as finished and clear any residual hold id */
        this.confirmationShown  = true;   // Tells UX helper we’re on the “thank-you” page
        this._allowStep3        = false;
        this.bookingHoldId      = null;   // There is no hold any more
        this._currentStep       = 'confirm'; // Set current step
        /* ----------------------------------------------- */

        // Ensure any leftover stash is cleared once we’re confirmed
        try { localStorage.removeItem(LS_CLEANUP_KEY); } catch {}
        // if (this.holdTimerId) {clearTimeout(this.holdTimerId);}
        
        const { step3 } = this.els;
        const step4 = this.els.step4 ||= qs('#nalu-step-4', this.root);

        step3.classList.remove("active");
        step4.classList.add("active");
        

        qs('#naluBookingID',       step4).textContent = id;
        qs('#naluBookingFirstName',step4).textContent = name.split(' ')[0] || name;
        qs('#naluBookingTour',     step4).textContent = tour;
        qs('#naluBookingDateTime', step4).textContent = whenTourDateTime;
        qs('#naluBookingCheckInTime', step4).textContent = tourCheckInTime;
        //qs('#naluBookingCheckInTime', step4).textContent = this.formatTime24(tourCheckInTime);
        qs('#naluDepartureLocation', step4).textContent = departureLocation;

        /* Make the “Directions” link say the right place */
        const dirLink = step4.querySelector('#naluDirectionsLink');
        if (dirLink){
            dirLink.textContent = `Directions To ${departureLocation}`;
            dirLink.title       = departureLocation;
            dirLink.href        = departureLocationLink;
        }
        
        //this.router.push({ step:'confirm', id });
        this.router.replace({ step:'confirm', id });

        // Nalu Booking confirmation tracking
        naluTrack('nalu_booking_confirmed', {
            booking_id:         id,
            transaction_id:     this._lastPaymentIntentId || undefined,
            value:              ( totalPrice ) ? totalPrice / 100 : 0,
            currency:           (this.intent?.currency || 'usd').toUpperCase(),
            // _trackCtx reads live state, so tour_id/booking_type resolve here
            // even though `ev` isn't in scope inside showConfirmation.
            ...this._trackCtx(),
        });

        // Wire the mobile “Done” button to hard-close the modal
        const doneBtn = this.root.querySelector('#bookingDoneBtn');
        if (doneBtn && !doneBtn._wired) {
            doneBtn._wired = true;
            doneBtn.addEventListener('click', async (e) => {
                e.preventDefault();
                try { showLoader?.(); } catch {}
                try {
                    await window.NaluBookingUx?.hardModalClose?.();
                } finally {
                    try { hideLoader?.(); } catch {}
                }
            });
        }
    }

    /* ============================================================
    Hold countdown logic -- class methods
    ============================================================ */
    startHoldCountdown () {
        // Stop any previous run
        this.stopHoldCountdown?.();

        // Step-2 calendar polling no longer matters once we're in checkout —
        // the visitor isn't looking at it anymore. Pause both loops; reset()
        // and step-2 re-render will start them again.
        this._stopCalendarViewerRefresh?.();
        this._stopMonthRefresh?.();

        // `show_viewer_count` is the org-wide master switch. When false, no
        // heartbeats fire (so no one else sees this visitor either), no
        // calendar badges render, and the step-3 viewer display stays hidden.
        const showViewers = this.holdConfig?.showViewerCount === true;
        if (showViewers) {
            this.startViewerHeartbeat();
        }

        // "none" strategy: there is no hold (no portal-side TTL) so the
        // countdown is meaningless. Either surface a live viewer count in
        // the same header slot, or hide the slot entirely if the operator
        // opted out — there's nothing else to put there.
        if (this.holdConfig?.strategy === 'none') {
            if (!showViewers) {
                const span = this.root.querySelector('#naluFormCountdown');
                const countdownBox = span?.parentElement?.parentElement;
                if (countdownBox) countdownBox.style.display = 'none';
                return;
            }
            this._renderStep3ViewerCount({ count: 0, totalSeats: 0 });
            return;
        }

        // Determine hold duration based on total passengers
        const totalPax = (this.people?.adults || 0) + (this.people?.children || 0);
        const holdMinutes = this._computeHoldMinutes(totalPax);
        const holdMillis  = holdMinutes * 60 * 1000;

        // 1) create absolute deadline
        const now = Date.now();
        this._holdEndsAt = now + holdMillis;
        this._holdExpiredFired = false;

        // 2) draw once immediately
        const out = this.root.querySelector('#naluFormCountdown');
        const draw = (msLeft) => {
            const secs = Math.max(0, Math.ceil(msLeft / 1000));
            const m = Math.floor(secs / 60);
            const s = secs % 60;
            if (out) out.textContent = `${m} Minute${m !== 1 ? 's' : ''} ${String(s).padStart(2,'0')} Second${s !== 1 ? 's' : ''}`;
        };
        draw(this._holdEndsAt - now);

        // 3) rAF tick (pauses in background, but we recompute on resume)
        const tick = () => {
            // only tick while we’re actually on Step-3
            if (this._currentStep !== 'info' || this.confirmationShown) {
                this.stopHoldCountdown?.();
                return;
            }

            const msLeft = this._holdEndsAt - Date.now();
            if (msLeft <= 0) {
                this._expireHoldIfOnStep3();
                return;
            }
            draw(msLeft);
            this._countdownRaf = window.requestAnimationFrame(tick);
        };
        this._countdownRaf = window.requestAnimationFrame(tick);

        // 4) On tab visibility changes, recompute immediately (iOS Safari friendly)
        this._visHandlerBound = () => {
            if (document.visibilityState === 'visible') {
            const msLeft = (this._holdEndsAt ?? 0) - Date.now();
            if (msLeft <= 0) {
                this._expireHoldIfOnStep3();
            } else {
                draw(msLeft);
                if (!this._countdownRaf) {
                    this._countdownRaf = window.requestAnimationFrame(() => tick());
                }
            }
            }
        };
        document.addEventListener('visibilitychange', this._visHandlerBound, { passive: true });
    }

    stopHoldCountdown () {
        if (this._countdownRaf) {
            cancelAnimationFrame(this._countdownRaf);
            this._countdownRaf = null;
        }
        if (this._visHandlerBound) {
            document.removeEventListener('visibilitychange', this._visHandlerBound);
            this._visHandlerBound = null;
        }
        this._holdEndsAt = null;
        this._holdExpiredFired = false;
        // Heartbeat is paired with Step-3 lifetime: when the timer stops
        // (back, close, success, expiry), presence stops too.
        this.stopViewerHeartbeat?.();
    }

    /* Compute dynamic hold minutes MAX(10m, 7m + TotalPassengers*1m) */
    _computeHoldMinutes(totalPax) {
        const pax = Number(totalPax || 0);
        return Math.max(10, 7 + pax);
    }

    /* ============================================================
    Viewer presence (heartbeat) — Step-3 lifecycle
    ============================================================ */

    /* Lazy session id, generated once per modal session. Cleared by reset(). */
    _getViewerSessionId() {
        if (!this._viewerSessionId) {
            this._viewerSessionId = generateSessionId();
        }
        return this._viewerSessionId;
    }

    _viewerHeartbeatPayload() {
        const ev = this.selectedEvent;
        if (!ev?.eventId) return null;
        const day = (ev.isoLocalStartDate || '').slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return null;
        return {
            eventId  : ev.eventId,
            day      : day,
            sessionId: this._getViewerSessionId(),
            adults   : Number(this.people?.adults || 0),
            children : Number(this.people?.children || 0),
        };
    }

    /* Start heartbeating presence to the portal. Called on Step-3 entry in
       BOTH strategies — the portal needs the heartbeat in either mode so the
       calendar can render an accurate "X people booking" badge. The display
       branch (timer vs viewer count) is independent; see _renderStep3Header. */
    startViewerHeartbeat() {
        this.stopViewerHeartbeat();
        const payload = this._viewerHeartbeatPayload();
        if (!payload) return;

        // Fire one heartbeat immediately, then on a 15s interval (half the
        // 30s server-side TTL — gives one missed-poll grace before the
        // entry expires from the zset).
        const tick = async () => {
            const next = this._viewerHeartbeatPayload();
            if (!next) return;
            const summary = await heartbeatViewer(next);
            // While in "none" mode, the response doubles as the step-3 count.
            if (this.holdConfig?.strategy === 'none') {
                this._renderStep3ViewerCount(summary);
            }
        };
        tick();
        this._viewerHeartbeatInterval = window.setInterval(tick, 15000);
    }

    stopViewerHeartbeat() {
        if (this._viewerHeartbeatInterval) {
            clearInterval(this._viewerHeartbeatInterval);
            this._viewerHeartbeatInterval = null;
        }
    }

    /* Replace the Step-3 "Reservation Expires In: X minutes" header with a
       live count of OTHER visitors when the org's hold strategy is "none".
       The portal-returned count and totalSeats include this visitor's own
       heartbeat, so subtract our own contribution before rendering.
       The whole row is hidden when no one else is present — saying "you're
       the only one" is just noise. */
    _renderStep3ViewerCount({ count = 0, totalSeats = 0 } = {}) {
        const span = this.root.querySelector('#naluFormCountdown');
        if (!span) return;
        const textWrapper = span.parentElement;
        const countdownBox = textWrapper?.parentElement; // .nalu-form-header__countdown
        const iconBox = countdownBox?.querySelector('.nalu-form-header__countdown-icon');

        // Swap palette to the soft-amber info variant once, when we know the
        // strategy is "none". Keeps the timer's red palette untouched in the
        // legacy mode.
        countdownBox?.classList.add('nalu-form-header__countdown--info');
        if (iconBox) iconBox.style.display = 'none';

        // Subtract our own session so the displayed count is OTHERS only.
        const myParty = Number(this.people?.adults || 0) + Number(this.people?.children || 0);
        const others = Math.max(0, Number(count || 0) - 1);
        const otherSeats = Math.max(0, Number(totalSeats || 0) - myParty);

        if (others <= 0) {
            // Hide the row entirely — nothing useful to say.
            if (countdownBox) countdownBox.style.display = 'none';
            return;
        }
        if (countdownBox) countdownBox.style.display = '';

        // Render the operator-supplied template into the prefix text node and
        // clear the legacy timer span — the template owns the whole line now.
        const text = formatViewerTemplate(
            (typeof window !== 'undefined' && window.naluBookingVars?.viewerStep3Template) || '',
            { count: others, seats: otherSeats },
            DEFAULT_VIEWER_STEP3_TEMPLATE
        );
        if (textWrapper && textWrapper.firstChild && textWrapper.firstChild.nodeType === Node.TEXT_NODE) {
            textWrapper.firstChild.textContent = text;
        }
        span.textContent = '';
    }

    /* ============================================================
    Per-slot viewer count badges on the calendar (Step-2)
    ============================================================ */

    /* Sweep visible bookable slots and decorate each with a small
       "X booking" badge when the portal reports >0 active sessions. Called
       at the end of renderEvents and on a 30s refresh while Step-2 is open. */
    async _refreshCalendarViewerBadges() {
        const slots = this.root.querySelectorAll('.btn-nalu[data-event-id][data-day]');
        if (!slots.length) return;

        // Dedupe (eventId, day) before fetching to avoid duplicate calls when
        // a slot somehow appears twice in the DOM.
        const pairs = new Map();
        slots.forEach((el) => {
            const key = `${el.dataset.eventId}|${el.dataset.day}`;
            if (!pairs.has(key)) {
                pairs.set(key, { eventId: Number(el.dataset.eventId), day: el.dataset.day });
            }
        });

        // Fire all reads in parallel; any individual failure returns 0 and
        // simply doesn't render a badge.
        await Promise.all(Array.from(pairs.values()).map(async ({ eventId, day }) => {
            const { count } = await getViewers({ eventId, day });
            if (!Number.isFinite(count)) return;
            this.root
                .querySelectorAll(`.btn-nalu[data-event-id="${eventId}"][data-day="${day}"]`)
                .forEach((btn) => this._applyViewerBadge(btn, count));
        }));
    }

    _applyViewerBadge(btn, count) {
        const lowSeats = btn.querySelector('.nalu-low-seats');
        let badge = btn.querySelector('.nalu-viewers-badge');
        if (count <= 0) {
            badge?.remove();
            if (lowSeats) lowSeats.style.display = '';   // restore "Only N seats left"
            return;
        }
        // Live "N booking now" takes priority over the static "Only N seats left".
        if (lowSeats) lowSeats.style.display = 'none';
        if (!badge) {
            badge = document.createElement('span');
            badge.className = 'nalu-viewers-badge btn-text-sm';
            // Append after the existing .btn-text content so the badge sits
            // beneath the time on a new line, mirroring the "Only N seats
            // left" affordance.
            btn.querySelector('.btn-text')?.appendChild(badge);
        }
        // Calendar slot only has viewer count — seat-per-slot isn't known
        // client-side here. Operators who want pluralization use
        // {count:singular|plural} (e.g. "{count:person|people} booking now").
        badge.textContent = formatViewerTemplate(
            (typeof window !== 'undefined' && window.naluBookingVars?.viewerCalendarTemplate) || '',
            { count },
            DEFAULT_VIEWER_CALENDAR_TEMPLATE
        );
    }

    _startCalendarViewerRefresh() {
        this._stopCalendarViewerRefresh();
        this._calendarViewerInterval = window.setInterval(() => {
            this._refreshCalendarViewerBadges().catch(() => {});
        }, 30000);
    }

    _stopCalendarViewerRefresh() {
        if (this._calendarViewerInterval) {
            clearInterval(this._calendarViewerInterval);
            this._calendarViewerInterval = null;
        }
    }

    /* wipe everything so a fresh modal shows next time */
    reset({ silent = true } = {}) {
        // Stop any countdowns
        this.stopHoldCountdown?.();
        this.stopViewerHeartbeat?.();
        this._stopCalendarViewerRefresh?.();
        this._stopMonthRefresh?.();
        this._viewerSessionId = null;

        // Clear server/client session ids & flags
        this.currentBookingId       = null;
        this.bookingHoldId          = null;
        this._allowStep3            = false; // Allow Step-3 only after a successful hold
        this._step3Invalid          = false;
        this.intent                 = null;
        this._pressedPayNow         = false;
        this._paymentSucceeded      = false;
        this._chargeMayHaveLanded   = false;
        this._deletePendingOnLeave  = false;
        this._lastStripeError       = null;
        this._lastPaymentIntentId   = null;
        this._wallet                = null;  // clear any wallet/Link quick-pay state
        this._step3Mounted          = false; // allow populateStep3() to rebuild
        naluTrace('[nalu:step3] UNMOUNT via reset');

        // ── Stripe teardown (Payment Element & legacy Card) ───────────────────
        try {
            // Unmount card element (if you ever mounted it)
            this._cardEl?.unmount?.();
            this._cardEl = null;
            
            // Destroy Payment Element & its Elements instance (this clears collected values)
            // NOTE: Stripe v3 supports elements.destroy() to nuke all mounted elements.
            this._elements?.destroy?.();
            this._elements = null;
            
            document.getElementById('card-element')?.replaceChildren();
            document.getElementById('wallet-button')?.replaceChildren();
            document.getElementById('nalu-payment-tabs')?.replaceChildren?.();
            document.getElementById('nalu-payment-element')?.replaceChildren?.();

            this.cardComplete = false;
        } catch {}

        // ── Reset Passengers to Adults 1, Children 0 ────────────────────────
        this.people?.set?.(1, 0, { emit: true });

        // Clear all Step-3 form inputs (data-field)
        this.root.querySelectorAll('[data-field]').forEach(el => {
            if (el.type === 'checkbox') {
                el.checked = false;
            } else {
                el.value = '';
            }
        });

        // Phone default
        const phoneInput = this.root.querySelector('[data-field="phone"]');
        if (phoneInput) phoneInput.value = '+1';

        // ── Reset Step-1 to defaults (HST "today", Adults 1, Children 0) ─────
        try {
            // If you already have hstTodayISO() helper, use it (preferred)
            const todayHST = (typeof hstTodayISO === 'function') ? hstTodayISO() : dayjs().format('YYYY-MM-DD'); // fallback
            this.date?.setISO?.(todayHST, { silent }); // keep silent default true
        } catch {}
        
        // ── Let the next open re-run initial auto-pick and blocked-days fetch ──
        this._didInitialLoad     = false;   // so refreshBlockedDays() runs on next open
        this._didInitialAutoPick = false;   // so initialAutoSelectDate() runs again
        this._lastBlockedKey     = null;    // force a fresh simple-calendar call
        try { this.date?.resetAutoBump?.(); } catch {} // reset DatePicker’s one-time bump

        // Reset screens to neutral (we’ll explicitly show Step-1 on open)
        this.els.step4 ||= qs('#nalu-step-4', this.root);
        this.els.step1.classList.remove("active");
        this.els.step2.classList.remove("active");
        this.els.step3.classList.remove("active");
        this.els.step4?.classList.remove("active"); // clear stale confirmation panel

        // Clear any visible errors
        try { this.clearErrors?.(); } catch {}
    }

    /* Wire the "Check Availability" button to always force-refresh */
    _bindCheckBtn() {
        const btn = this.root.querySelector('#nalu-booking-check');
        if (!btn || btn._wired) return;
        btn._wired = true;

        btn.addEventListener('click', async (e) => {
            e.preventDefault();
            // Keep local state in sync with the pickers just before fetching
            this._syncSearchFormFromStep1();
            await this.onSubmit({ forceFetch: true, silent: false });
        });
    }


    /* Read date + pax from Step-1 controls into this.date / this.people */
    _syncSearchFormFromStep1() {
        // date: if you store ISO on the date button, keep it current
        const iso = this.root.querySelector('#nalu-date-picker')?.dataset?.iso;
        if (iso) this.date.setISO?.(iso);

        // pax: read current counters (fallbacks keep previous values if not found)
        const getInt = (sel, fb) => {
            const t = this.root.querySelector(sel)?.textContent?.trim();
            const n = parseInt(t, 10);
            return Number.isFinite(n) ? n : fb;
        };
        const A = getInt('[data-counter="adults"]', this.people?.adults ?? 1);
        const C = getInt('[data-counter="children"]', this.people?.children ?? 0);

        // no assignment here — keep the PassengerPicker instance alive
        // (its getters read from DOM; reset()/showStep1() will call .set())
        //this.people = { adults: A, children: C };
    }

    // Sold out HTML for Step-2
    _soldOutHtml () {
        const PLUGIN_URL = window.naluBookingVars.plugin_url;
        return `
            <div class="nalu-empty">
                <div class="nalu-empty__icon">
                    <img src="${PLUGIN_URL}img/icon-no-ticket.svg" width="80" height="54" alt="Sold out">
                </div>
                <h3 class="nalu-empty__title">Sorry, all the tours for this date are sold out</h3>
            </div>
        `;
    }

    /* Notice shown above the boat list when the visitor preselected a boat
     * (via the trigger button's data-tour-id) that has no listing in today's
     * payload — e.g. a boat pulled for maintenance or fully delisted. The name
     * comes from the trigger's data-tour-name because a boat with no tour
     * record has no other name source. */
    _preselectUnavailableHtml (boatName) {
        const name = (boatName || 'That boat').trim();
        return `
            <div class="nalu-tours__notice" role="status">
                <p class="nalu-tours__notice-title">Sorry — the ${name} is currently unavailable.</p>
                <p class="nalu-tours__notice-text">It may be out for maintenance. Please take a look at the other boats available below.</p>
            </div>
        `;
    }


    /* ──────────────────────────────────────────────────────────
    * Recovery on load + stash on unload (uses your methods)
    * If the user refreshed Step-3 after a decline (or with a hold only),
    * we’ll have stashed ids in localStorage. On next load, delete them silently.
    * ────────────────────────────────────────────────────────── */

    // Helper: consider certain server failures as "already cleaned"
    _treatAsAlreadyCleared(res) {
        if (!res) return false;
        if (res.success) return true;

        const text = (
            (res?.data?.message || res?.message || res?.error || '') + ' ' +
            (res?.data?.code    || res?.code    || '')
        ).toLowerCase();

        // common back-end wordings when the object no longer exists
        return /not\s*found|no\s*such|does\s*not\s*exist|expired|already\s*(deleted|canceled|cancelled|released)/i.test(text);
    }
    
    async _recoverAbandonedFromStorage() {
        let raw;
        try { raw = localStorage.getItem(LS_CLEANUP_KEY); } catch {}
        if (!raw) return;

        let payload;
        try { payload = JSON.parse(raw); } catch {
            localStorage.removeItem(LS_CLEANUP_KEY);
            return;
        }

        const now = Date.now();
        const ttl = payload.kind === 'hold' ? HOLD_TTL_MS : PENDING_TTL_MS;

        // If too old, just drop it (server cron likely handled it)
        if (!payload.ts || (now - payload.ts) > ttl) {
            localStorage.removeItem(LS_CLEANUP_KEY);
            return;
        }


        try {
            let res;
            
            if (payload.kind === 'pending' && payload.bookingId) {
                res = await this.deletePendingReservation(payload.bookingId, payload.customerId);
                if (res?.success) localStorage.removeItem(LS_CLEANUP_KEY);
                return;
            }

            if (payload.kind === 'hold' && payload.holdId) {
                res = await this.deleteReservationHold(payload.holdId);
                if (res?.success) localStorage.removeItem(LS_CLEANUP_KEY);
                return;
            }

            if (this._treatAsAlreadyCleared(res)) {
                // success OR server says "doesn't exist" → stop retrying
                localStorage.removeItem(LS_CLEANUP_KEY);
            } else {
                // optional: cap retries to avoid noise
                payload.attempts = (payload.attempts|0) + 1;
                if (payload.attempts >= 3) {
                    localStorage.removeItem(LS_CLEANUP_KEY);
                } else {
                    localStorage.setItem(LS_CLEANUP_KEY, JSON.stringify(payload));
                }
            }
        } catch (e) {
            console.warn('Nalu cleanup exception', e);
            // keep key so we can retry next load
        }
    }

    /**
     * On page refresh/close, stash either:
     *  - pending reservation (only if payment declined → _deletePendingOnLeave = true)
     *  - or, if no reservation yet, an outstanding hold id.
     */
    _installUnloadStash() {
        if (this._unloadWired) return;
        this._unloadWired = true;

        window.addEventListener('beforeunload', () => {
            try {
                // If a reservation exists and payment failed/declined → stash pending booking for cleanup
                if (this.currentBookingId && this._deletePendingOnLeave) {
                    const customerId = this.intent?.customerId || this.customerId || '';
                    localStorage.setItem(LS_CLEANUP_KEY, JSON.stringify({
                        kind: 'pending',
                        bookingId: this.currentBookingId,
                        customerId,
                        ts: Date.now()
                    }));
                    return;
                }
                // Else if no reservation, but a hold exists → stash hold id
                if (!this.currentBookingId && this.bookingHoldId) {
                    localStorage.setItem(LS_CLEANUP_KEY, JSON.stringify({
                        kind: 'hold',
                        holdId: this.bookingHoldId,
                        ts: Date.now()
                    }));
                }
            } catch {}
        });
    }

    /* ------------------------------------------------------------
    *  Dialog helpers (title + text setters and open/close)
    * ------------------------------------------------------------ */
    _findDialog(id){ return document.getElementById(id); }

    _setDialogCopy(dlg, { title, text } = {}){
        if (!dlg) return;
        const t = dlg.querySelector('[data-dialog-title]');
        const p = dlg.querySelector('[data-dialog-text]');
        if (t && title) t.textContent = title;
        if (p && text)  p.textContent = text;
    }

    _openDialog(id, { title, text } = {}){
        const dlg = this._findDialog(id);
        if (!dlg) return;
        this._setDialogCopy(dlg, { title, text });
        try { dlg.showModal(); } catch {}
        return dlg;
    }

    _expireHoldIfOnStep3 () {
        if (this._holdExpiredFired) return;
        this._holdExpiredFired = true;

        // Only show expired UI while actually on Step-3 (info)
        const onStep3 = (this._currentStep === 'info') && !this.confirmationShown;
        if (onStep3) {
            try { this._showExpiredDialog(); } catch {}
        }

        // Always stop the timer afterwards
        this.stopHoldCountdown?.();
    }

    /* Stripe error → friendly copy */
    _friendlyStripeMessage(err){
        // Stripe error objects vary; normalize
        const code   = err?.code || err?.payment_intent?.last_payment_error?.code || '';
        const reason = (err?.message || err?.payment_intent?.last_payment_error?.message || '').trim();

        const map = {
            'card_declined'          : 'Your bank declined this card. Try a different card or contact your bank.',
            'insufficient_funds'     : 'This card has insufficient funds. Please use another card.',
            'expired_card'           : 'This card is expired. Use a different card.',
            'incorrect_cvc'          : 'The security code (CVC) is incorrect.',
            'incorrect_number'       : 'The card number is incorrect.',
            'invalid_expiry_month'   : 'The expiry date month looks invalid.',
            'invalid_expiry_year'    : 'The expiry date year looks invalid.',
            'authentication_required': 'This card requires additional authentication. Please try again and complete the verification.',
            'processing_error'       : 'We couldn’t process the card. Please try again or use another card.',
        };

        return {
            title: 'Payment Method Declined',
            text : reason || map[code] || 'Your payment was declined. Please try another card.'
        };
    }

    /* Unavailable test for WP AJAX responses */
    _isUnavailableResponse(json){
        // code can arrive in several places depending on caller
        const msg  = (json?.data?.message || json?.message || '').toLowerCase();
        const code = Number(json?.data?.code ?? json?.code);
        //const code = json?.data?.code || json?.code || '';
        
        // Treat the portal’s capacity error as "unavailable"
        if (code === 107) return true;
        
        return /unavailable|sold\s*out|no\s*capacity|not\s*enough.*capacity|insufficient.*capacity|over.*capacity|capacity.*exceed/.test(msg);
    }

    /* Show payment failed dialogs */
    _showFailedDialog(err){
        const { title, text } = this._friendlyStripeMessage(err || {});
        const dlg = this._openDialog('nalu-failed-dialog', { title, text });

        // Buttons close only; user stays on Step-3 to fix card
        if (dlg){
            const btns = dlg.querySelectorAll('[data-failed-cancel]');
            btns.forEach(b => b.onclick = () => { try { dlg.close(); } catch {} });
        }
    }

    /**
     * Tour-unavailable dialog. Shown whenever a booking attempt fails because
     * the slot is gone — a sold-out hold (any strategy, including "none"), the
     * on-checkout hold request, a capacity error at reservation time, or a
     * stale booking-type mismatch. Also doubles as the user-facing surface for
     * recoverable server errors elsewhere in the booking flow (calendar load
     * fails, payment-intent create fails, etc.) — anywhere the right next
     * action is "bounce back to Step-2 and pick something else". The caller
     * is responsible for capturing the technical cause to Sentry; the user
     * sees the same calm "pick another tour" surface either way.
     *
     * Besides the headline it can carry a short reason line and a row of
     * "try one of these dates" chips drawn from the cached calendar window.
     * Without a tourId the chips are suppressed automatically.
     *
     * Opening the dialog also kicks a silent, forced re-fetch of Step-2 so the
     * calendar behind the modal already reflects the new sold-out state by the
     * time the visitor clicks through. The "View Available Tours" button (and
     * any chip) waits on that refresh before its own teardown — see
     * `_cleanupToStep2KeepingSearch`.
     *
     * @param {object|string} [opt]         - a string is treated as `{ message }`
     * @param {string} [opt.title]          - override headline copy
     * @param {string} [opt.message]        - reason line under the headline
     * @param {number|string} [opt.tourId]  - tour the visitor was booking
     * @param {string} [opt.bookingType]    - 'Group' | 'Private'
     * @param {string} [opt.day]            - YYYY-MM-DD the attempt was for
     * @param {string} [opt.time]           - 'HH:MM' the visitor had picked
     */
    _showUnavailableDialog(opt = {}) {
        // Hard guard, independent of any individual caller. This dialog's entire
        // purpose is to send the visitor back to Step-2 to pick again — and
        // re-entering Step-3 mints a fresh PaymentIntent and Stripe customer. If
        // the card has already been submitted and we have not established that
        // nothing was captured, that path double-charges. Refuse and hand off to
        // support instead, so a future caller cannot reintroduce the bug.
        if (this._chargeMayHaveLanded) {
            naluSentryCapture(new Error('Unavailable dialog suppressed: a charge may already be captured'), {
                stage    : 'unavailable_dialog_post_charge',
                bookingId: this.currentBookingId,
                paymentIntentId: this._lastPaymentIntentId,
            });
            this._deletePendingOnLeave = false;
            this._showPostPaymentSupportDialog();
            return;
        }

        if (typeof opt === 'string') opt = { message: opt };
        const { title = '', message = '', tourId = null, bookingType = '', day = '', time = '' } = opt || {};

        // Refresh Step-2 behind the modal so stale availability isn't waiting
        // for the visitor when they dismiss — and so the dismiss itself is
        // instant (no second fetch / "Loading…" flash; see _cleanupToStep2…).
        // Tracked (plus the date it was for) so the button/chip handlers can
        // await it instead of racing onSubmit's in-flight lock; the swallow
        // keeps a slow/failed refresh from surfacing as an unhandled rejection.
        this._unavailRefreshDate = this.date?.iso ?? null;
        try { this._unavailRefresh = Promise.resolve(this.onSubmit({ forceFetch: true, silent: true })).catch(() => {}); } catch {}

        const dlg = this._openDialog?.('nalu-unavailable-dialog')
                || document.getElementById('nalu-unavailable-dialog');
        if (!dlg) return;
        try { dlg.showModal?.(); } catch {}

        // Optional headline override (template default is the tour-specific
        // copy with a <br>; callers without tour context can pass a more
        // general line). The original HTML is cached on first open so a later
        // call without `title` restores it instead of inheriting the previous
        // override.
        const t = dlg.querySelector('[data-dialog-title]');
        if (t) {
            if (dlg._naluDefaultTitleHTML == null) dlg._naluDefaultTitleHTML = t.innerHTML;
            if (typeof title === 'string' && title.trim()) {
                t.textContent = title.trim();
            } else {
                t.innerHTML = dlg._naluDefaultTitleHTML;
            }
        }

        // Optional reason line (the template ships an empty, hidden <p>).
        const p = dlg.querySelector('[data-dialog-text]');
        if (p) {
            const txt = (message || '').trim();
            p.textContent = txt;
            p.hidden = !txt;
        }

        // "Try one of these dates" chips — same tour & booking type, nearest
        // first, drawn from whatever the cached window already holds. Tapping a
        // chip closes the dialog, lands back on Step-2 (using the already-fresh
        // data from the silent refresh), then jumps the carousel to that date.
        const altBox = dlg.querySelector('[data-alt-dates]');
        if (altBox) {
            const near = (tourId != null)
                ? this._nearbyBookableDates({ tourId, bookingType, aroundISO: day, aroundHHMM: time, limit: 5, radiusDays: 14 })
                : [];
            const chips = this._dateChipsHTML(near, 'nalu-date-chip');
            if (chips) {
                altBox.innerHTML = `<div class="nalu-dialog__alt-label">Try one of these nearby dates:</div>`
                                 + `<div class="nalu-date-chips">${chips}</div>`;
                altBox.hidden = false;
                altBox.querySelectorAll('[data-jump-iso]').forEach(btn => {
                    btn.addEventListener('click', async () => {
                        const iso = btn.dataset.jumpIso;
                        try { dlg.close?.(); } catch {}
                        await this._cleanupToStep2KeepingSearch();
                        if (iso) await this.goToDate(iso);
                    });
                });
            } else {
                altBox.innerHTML = '';
                altBox.hidden = true;
            }
        }

        // "View Available Tours" / exit-confirm → existing teardown, keep search.
        this._bindDialogButtons(
            dlg,
            '[data-expired-choose],[data-exit-confirm]',
            async () => {
                try { dlg.close?.(); } catch {}
                await this._cleanupToStep2KeepingSearch();
            }
        );
    }

    /**
     * Age-band heads-up dialog. Shown at Step-2 slot selection when the party
     * includes children and the chosen boat enforces a higher minimum child
     * age than a gentler available option (see `_ageBandGuidance`). Never
     * blocks — it names the age floor and offers less-restrictive boats:
     * same-day boats as one-tap "switch" buttons (→ `onSwitch`, which re-enters
     * the normal proceed path for that boat), or, when none run that day,
     * nearby-day jump chips. "Continue" proceeds with the originally chosen
     * boat (→ `onContinue`); dismissing simply returns to Step-2 unchanged.
     *
     * Copy is built here (data-driven) rather than in the PHP template.
     *
     * @param {object}   opt
     * @param {object}   opt.selectedEvent          - the boat the visitor picked
     * @param {number}   opt.selMinAge              - that boat's minimum child age
     * @param {Array}    [opt.alternatives=[]]      - same-day gentler boats (from _lessRestrictiveSameDay)
     * @param {Array}    [opt.otherDays=[]]         - nearby days (from _lessRestrictiveOtherDays)
     * @param {Function} opt.onContinue             - proceed with the picked boat
     * @param {Function} opt.onSwitch               - (event, type) proceed with a swapped boat
     */
    _showAgeBandDialog({ selectedEvent, selMinAge = 0, selMaxAge = 0, alternatives = [], otherDays = [], onContinue, onSwitch } = {}) {
        const dlg = this._openDialog?.('nalu-agewarn-dialog')
                || document.getElementById('nalu-agewarn-dialog');
        // Fail open: if the template is missing, never trap the booking — just
        // proceed with the picked boat as a normal click would.
        if (!dlg) { try { showLoader(); } catch {} return onContinue?.(); }
        try { dlg.showModal?.(); } catch {}

        // "ages 4-12" when both bounds are known; degrade gracefully to a lone
        // floor when a boat carries no upper bound rather than printing "N-0".
        const ageRange = (min, max) => (max > 0 ? `ages ${min}-${max}` : `ages ${min}`);

        const selTour = this.tours?.[selectedEvent?.tourId];
        const selName = selTour?.name || 'This boat';

        // Reason line.
        const p = dlg.querySelector('[data-dialog-text]');
        if (p) {
            p.textContent = `${selName} is for children ${ageRange(selMinAge, selMaxAge)}. `
                          + `For safety reasons, younger children can't join this boat.`;
            p.hidden = false;
        }

        // Same-day gentler boats → one-tap switch buttons. Rebuilt each open
        // (fresh nodes), so their listeners never go stale across reopens.
        const altBox = dlg.querySelector('[data-agewarn-alts]');
        if (altBox) {
            if (alternatives.length) {
                // One block per gentler boat: name + age band, then that boat's
                // departure times as pills. Tapping a time switches to that exact
                // slot and proceeds to checkout.
                const groups = alternatives.map((a, ti) => {
                    const pills = a.slots.map((e, si) =>
                        `<button type="button" class="nalu-agewarn__time" data-agewarn-switch data-tour-index="${ti}" data-slot-index="${si}">`
                        + `${this.formatTime24(e.isoLocalStartDate?.slice(11, 16))}`
                        + `</button>`
                    ).join('');
                    return `<div class="nalu-agewarn__group">`
                         + `<div class="nalu-agewarn__group-name">${a.name} <span class="nalu-agewarn__group-ages">${ageRange(a.minAge, a.maxAge)}</span></div>`
                         + `<div class="nalu-agewarn__times">${pills}</div>`
                         + `</div>`;
                }).join('');
                altBox.innerHTML = `<div class="nalu-dialog__alt-label">These boats today welcome younger children:</div>`
                                 + `<div class="nalu-agewarn__groups">${groups}</div>`;
                altBox.hidden = false;
                altBox.querySelectorAll('[data-agewarn-switch]').forEach(btn => {
                    btn.addEventListener('click', async () => {
                        const a = alternatives[Number(btn.dataset.tourIndex)];
                        const e = a?.slots?.[Number(btn.dataset.slotIndex)];
                        try { dlg.close?.(); } catch {}
                        if (e) await onSwitch?.(e, a.type);
                    });
                });
            } else {
                altBox.innerHTML = '';
                altBox.hidden = true;
            }
        }

        // Nearby-day chips — only when nothing gentler runs that day.
        const dateBox = dlg.querySelector('[data-agewarn-dates]');
        if (dateBox) {
            const chips = (!alternatives.length && otherDays.length)
                ? this._dateChipsHTML(otherDays, 'nalu-date-chip')
                : '';
            if (chips) {
                dateBox.innerHTML = `<div class="nalu-dialog__alt-label">Or pick a day with a boat for younger children:</div>`
                                  + `<div class="nalu-date-chips">${chips}</div>`;
                dateBox.hidden = false;
                dateBox.querySelectorAll('[data-jump-iso]').forEach(btn => {
                    btn.addEventListener('click', async () => {
                        const iso = btn.dataset.jumpIso;
                        try { dlg.close?.(); } catch {}
                        await this._cleanupToStep2KeepingSearch();
                        if (iso) await this.goToDate(iso);
                    });
                });
            } else {
                dateBox.innerHTML = '';
                dateBox.hidden = true;
            }
        }

        // "Continue with this boat" — proceed as originally picked. Rebound via
        // onclick (not _bindDialogButtons) so a reopen swaps in the fresh
        // per-open closure instead of keeping the first open's stale one.
        dlg.querySelectorAll('[data-agewarn-continue]').forEach(b => {
            b.onclick = async (e) => {
                e.preventDefault();
                try { dlg.close?.(); } catch {}
                await onContinue?.();
            };
        });

        // Close/dismiss → cancel: stay on Step-2 unchanged, take no hold.
        dlg.querySelectorAll('[data-agewarn-close]').forEach(b => {
            b.onclick = (e) => { e.preventDefault(); try { dlg.close?.(); } catch {} };
        });
    }

    /* --- Age helpers --- */

    /**
     * Return the number of days in a given month/year.
     * Example: _daysInMonth("02", "2024") → 29 (leap year)
     *
     * @param {string|number} m - Month "01".."12"
     * @param {string|number} y - Year "1990"
     * @returns {number} Days in that month
     */
    _daysInMonth (m, y) {
        const mi = Number(m), yi = Number(y);
        // JS quirk: new Date(year, monthIndex=0..11, day=0) → last day of previous month
        return new Date(yi || 2000, mi, 0).getDate();
    }

    /**
     * Validate if Y-M-D parts form a real calendar date.
     * Ensures year = 4 digits, month = 01–12, day = within month range.
     *
     * @param {string} y - Year as "YYYY"
     * @param {string} m - Month as "MM"
     * @param {string} d - Day as "DD"
     * @returns {boolean} True if valid, false otherwise
     */
    _isValidYMD (y, m, d) {
        if (!(y && m && d)) return false;
        if (!/^\d{4}$/.test(y) || !/^\d{2}$/.test(m) || !/^\d{2}$/.test(d)) return false;

        const mi = Number(m), di = Number(d);
        if (mi < 1 || mi > 12) return false;

        const max = this._daysInMonth(m, y);
        return di >= 1 && di <= max;
    }

    /**
     * Calculate the age in full years at a given reference date.
     * Example: DOB 2013-02-16, ref=2025-09-17 → 12
     * (ignores months after birthday, only returns completed years).
     *
     * @param {string|number} y - Year of birth
     * @param {string|number} m - Month of birth (1-12)
     * @param {string|number} d - Day of birth
     * @param {Date} [ref=new Date()] - Reference date (default today)
     * @returns {number} Age in years
     */
    _calcAge (y, m, d, ref = new Date()) {
        const yi = Number(y), mi = Number(m) - 1, di = Number(d);
        const dob = new Date(yi, mi, di);

        let age = ref.getFullYear() - yi;
        const hadBirthdayThisYear =
            ref.getMonth() > mi || (ref.getMonth() === mi && ref.getDate() >= di);

        if (!hadBirthdayThisYear) age -= 1;
        return age;
    }

    /**
     * Calculate precise age (years + months) at a given reference date.
     * Unlike _calcAge, this catches children who are "over maxAge" by months.
     *
     * Example: DOB 2013-02-16, ref=2025-09-17 → { years: 12, months: 7 }
     *
     * @param {string|number} y - Year of birth
     * @param {string|number} m - Month of birth (1-12)
     * @param {string|number} d - Day of birth
     * @param {Date} [ref=new Date()] - Reference date (default today)
     * @returns {{years:number, months:number}} Object with years + months
     */
    _calcAgeDetail (y, m, d, ref = new Date()) {
        const yi = Number(y), mi = Number(m) - 1, di = Number(d);
        const dob = new Date(yi, mi, di);

        let years = ref.getFullYear() - yi;
        let months = ref.getMonth() - mi;
        let days   = ref.getDate() - di;

        if (days < 0) {
            months -= 1; // birthday day not reached in this month
        }
        if (months < 0) {
            years -= 1;
            months += 12;
        }

        return { years, months };
    }

    /**
     * Post-payment support dialog. Used in the narrow window between Stripe
     * confirming the charge and our portal-side confirm route succeeding —
     * money has moved but we couldn't finalize the booking on our side. The
     * portal's reconciler self-heals these transactions, so the user-facing
     * action is "wait 24h, contact charter if no email" rather than retry.
     *
     * Caller is responsible for capturing the failure to Sentry (with the
     * PaymentIntent id) before opening this dialog.
     */
    _showPostPaymentSupportDialog() {
        const dlg = document.getElementById('nalu-postpay-support-dialog');
        if (!dlg) return;
        try { dlg.showModal?.(); } catch {}

        if (dlg._wired) return;
        dlg._wired = true;

        const hardClose = async () => {
            try { dlg.classList.remove('open'); dlg.close?.(); } catch {}
            if (typeof showLoader === 'function') showLoader();
            try {
                await window.NaluBookingUx?.hardModalClose?.();
            } finally {
                if (typeof hideLoader === 'function') hideLoader();
            }
        };

        dlg.querySelectorAll('[data-postpay-done]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                hardClose();
            });
        });

        dlg.addEventListener('cancel', (e) => {
            e.preventDefault();
            hardClose();
        });
    }

    /* Small helper that pops the “session expired” dialog */
    _showExpiredDialog() {
        const dlg = document.getElementById('nalu-expired-dialog');
        if (!dlg) return;

        // Optional: tweak button text
        const chooseBtns = dlg.querySelectorAll('[data-expired-choose]');
        chooseBtns.forEach(b => { b.textContent = 'View Available Tours'; });

        try { dlg.showModal?.(); } catch {}

        // Primary path: go to Step-2 with fresh data
        this._bindDialogButtons(
            dlg,
            '[data-expired-choose],[data-exit-confirm]',
            async () => {
                try { dlg.close?.(); } catch {}
                await this._cleanupToStep2KeepingSearch();
            }
        );

        // Optional: keep a path to Step-1 if you add a button with data-expired-restart later
        this._bindDialogButtons(
            dlg,
            '[data-expired-restart]',
            async () => {
                try { dlg.close?.(); } catch {}
                await this._cleanupToStep1?.();
            }
        );
    }

    /** Bind the same click handler to ALL matches inside a dialog (once). */
    _bindDialogButtons(dlg, selector, handler) {
        dlg.querySelectorAll(selector).forEach(btn => {
            if (btn._wired) return;
            btn._wired = true;
            btn.addEventListener('click', async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await handler(e);
            });
        });
    }

    /**
     * Non-recoverable dialog:
     * Close + Done both do the same as "data-exit-confirm" (cleanup + close modal)
     * Title/text are defaulted from PHP (data-dialog-title/text), but can be overridden.
     */
    /* Calendar/availability data couldn't be loaded — this is a plugin/portal
     * failure, NOT a sold-out date. Be honest: route the customer to call,
     * reusing the non-recoverable contact dialog (phone/email CTA). Used for
     * both the detailed window load failure and a simple-calendar (blocked-days)
     * failure, so the customer is never shown a false "sold out". */
    _showAvailabilityErrorDialog() {
        // Only when the plugin is actually open. Guards the race where a calendar
        // fetch was already in flight when the user closed the modal (the await
        // rejects after close), plus any stray background refresh — neither should
        // pop a "call us" dialog over a closed plugin.
        if (!this._allowNetwork) return;
        this._showNonRecoverableDialog({
            title: "We're Having Trouble Loading Availability",
            text : "Sorry, we can't load tour availability right now. Please call or email us to book — this is a temporary issue on our end, not a sold-out date.",
        });
    }

    _showNonRecoverableDialog({ title, text } = {}) {
        const dlg = document.getElementById('nalu-nonrecoverable-dialog');
        if (!dlg) return;

        // Optional override (keep PHP defaults if not provided)
        if (title) {
            const t = dlg.querySelector('[data-dialog-title]');
            if (t) t.textContent = title;
        }
        if (text) {
            const p = dlg.querySelector('[data-dialog-text]');
            if (p) p.textContent = text;
        }

        try { dlg.showModal?.(); } catch {}

        if (dlg._wired) return;
        dlg._wired = true;

        const hardClose = async () => {
            try { dlg.classList.remove('open'); dlg.close?.(); } catch {}

            // show loader optional (same style as your exit dialog)
            if (typeof showLoader === 'function') showLoader();
            try {
                // IMPORTANT: call the UX hard close (the one that does cleanup + closes modal)
                await window.NaluBookingUx?.hardModalClose?.();
            } finally {
                if (typeof hideLoader === 'function') hideLoader();
            }
        };

        // Close icon + Done button use the same attribute
        dlg.querySelectorAll('[data-nr-done]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                hardClose();
            });
        });

        // ESC should behave like Done
        dlg.addEventListener('cancel', (e) => {
            e.preventDefault();
            hardClose();
        });
    }


    /** Unified cleanup that lands the user back on Step-1 (date & passengers). */
    async _cleanupToStep1() {
        try { showLoader?.(); } catch {}

        // stop any timers
        try { this.stopHoldCountdown?.(); } catch {}

        this._allowStep3 = false;

        // delete pending reservation or hold if applicable
        try {
            if (this.currentBookingId && !this.confirmationShown) {
                try { await this.deletePendingReservation(this.currentBookingId); } catch (e) {
                    console.warn('Nalu cleanupToStep1 → deletePendingReservation failed', e);
                }
                this.currentBookingId = null;
                this.bookingHoldId    = null; // reservation had already claimed the hold
            } else if (this.bookingHoldId) {
                try { await this.deleteReservationHold(this.bookingHoldId); } catch (e) {
                    console.warn('Nalu cleanupToStep1 → deleteReservationHold failed', e);
                }
                this.bookingHoldId = null;
            }
        } finally {
            // fully reset the UI and state
            this.reset?.();
            this.showStep1?.();

            // normalize the URL (Step-1) and strip any hash params
            try { this.router?.replace?.({ step: 'search' }); } catch {}
            try { history.replaceState(null, '', location.pathname + location.search); } catch {}

            try { hideLoader?.(); } catch {}
        }
    }

    // ==== at class level ====
    _availCache = new Map(); // key => { events, tours, eventsByDate, ... }
    _currentAvailKey = null;

    _keyFor(dateIso, A, C) {
        return `${dateIso}|A${A}|C${C}`;
    }

    _applyAvailability(data, dateIso) {
        // whatever you already do to push data into the UI:
        this.eventsByDate = data.eventsByDate || this.eventsByDate;
        this.renderEvents(data.events, data.tours);
        this.rebuildCarousel(dateIso);
    }

    // Always render Step-1 and clear any sticky state.
    // Call this from NaluBookingUx.openModal() so reopen starts clean.
    showStep1 () {
        try { this.clearErrors?.(); } catch {}
        this.confirmationShown = false;
        this.selectedEvent     = null;
        this.selectedType      = null;
        this.stopHoldCountdown?.();

        // Ensure Step-1 defaults are visible (in case reset() wasn’t called)
        try {
            const todayISO = hstTodayISO();
            this.date?.setISO?.(todayISO, { silent: true });
        } catch {}

        this.people?.set?.(1, 0, { emit: true });

        const { step1, step2, step3 } = this.els || {};
        const step4 = (this.els ||= {}).step4 ||= qs('#nalu-step-4', this.root);
        if (step1 && step2 && step3) {
            step1.classList.add('active');
            step2.classList.remove('active');
            step3.classList.remove('active');
            step4?.classList.remove('active'); // clear stale confirmation panel
        }

        // Personalize the heading with the preselected boat (set by openModal
        // from the trigger button). Cache the original copy on first run so
        // reopening from a plain "Book Now" restores it.
        try {
            const titleEl = qs('.nalu-picker-title', this.root);
            if (titleEl) {
                titleEl.dataset.baseTitle ||= titleEl.textContent.trim();
                titleEl.textContent = this.preselectTourName
                    ? `${titleEl.dataset.baseTitle} on the ${this.preselectTourName}`
                    : titleEl.dataset.baseTitle;
            }
        } catch {}

        // Normalize router state to Step-1 (search) and do NOT trigger any fetch
        try { this.router?.replace?.({ step: 'search' }); } catch {}

    }

    // ─────────────────────────────────────────────
    // Purge all in-memory results (used on reopen)
    // ─────────────────────────────────────────────
    invalidateCache () {
        try {
            this._stopMonthRefresh?.();
            this.events       = {};
            this.eventsByDate = {};
            this.tours        = {};
            this.eventsRange  = null;
            this._fetching    = null;
            this._queuedISO   = null;
            if (typeof fetchedWindows !== 'undefined') fetchedWindows.length = 0;
        } catch {}
    }
}
